import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from '../../hooks/useApi';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import { readZip, parseCsv } from '../../lib/readZip';
import { downloadXlsx, colName } from '../../utils/xlsxWriter';
import { useSort, SortTh } from '../shared/SortableTable';

// ── Capital on Tap company card spend ────────────────────────────────────────
//
// One screen, two audiences, and the difference is not cosmetic:
//
//   FINANCE   import the export, code the spend, decide the VAT, link merchants to Sage
//             contacts, post to the ledger.
//   EVERYBODY their own transactions and one thing to do with them: upload the receipt.
//
// The mapping between a merchant and a Sage contact is finance's alone, and so is the
// VAT: those are accounting decisions and a wrong one is in the ledger.
//
// The number this screen exists to move is the VAT that cannot be reclaimed because
// nobody uploaded a receipt. It is stated first and stated in pounds, because "you have
// 14 transactions missing receipts" is an admin request and "£371 of VAT we cannot
// reclaim" is a reason.

const T = {
  label: 'text-[9px] font-semibold uppercase tracking-wide',
  meta: 'text-[10px]',
  body: 'text-[11px]',
  strong: 'text-[12px] font-semibold',
  title: 'text-[13px] font-bold',
  page: 'text-base font-extrabold',
};
const sel = 'border border-gray-200 rounded px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-navy';
const btn = 'px-3 py-1.5 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-50';
const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── DATES, THE WAY THE REST OF THE APP WRITES THEM ───────────────────────────
//
// "15 Aug 2026", matching formatDate in utils/format. This screen was printing the raw
// 2026-08-12 out of the database, which is the only place in the app that does, and reads
// as a machine field rather than a date.
//
// Built from the parts rather than through Date, because these are day values with no time
// in them: new Date('2026-08-12') is midnight UTC, and in any timezone behind UTC that
// formats as the 11th. A card statement that moves a purchase to the previous day is a
// reconciliation somebody cannot make add up.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(v);
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}`;
};
// The same, plus the time, for things that happened at a moment rather than on a day: an
// import, a chase, a posting. The ordering within a day is the point of showing it.
const fmtWhen = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return fmtDay(v);
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}, ${m[4]}:${m[5]}`;
};
// Pence, decided once. Summing a split of a charge in floating point and comparing it to
// the charge is exactly where 0.1 + 0.2 shows up.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Bytes → base64, chunked. The one-liner — String.fromCharCode(...all) — throws on a few
// hundred KB, and a receipt photograph from a phone is several megabytes.
function toBase64(buf) {
  const u8 = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < u8.length; i += 8192) bin += String.fromCharCode(...u8.subarray(i, i + 8192));
  return btoa(bin);
}

// The zip gives no content type, so it comes from the extension. A receipt served as
// application/octet-stream downloads instead of displaying, which is the same bug the
// accounts inbox had with its PDFs.
const contentTypeFor = (name) => /\.pdf$/i.test(name) ? 'application/pdf'
  : /\.png$/i.test(name) ? 'image/png'
  : /\.(hei[cf])$/i.test(name) ? 'image/heic'
  : /\.webp$/i.test(name) ? 'image/webp'
  : 'image/jpeg';

// A receipt is a photo or a PDF, which is the rule the server holds it to (the 'receipt'
// kind in src/lib/fileSafety.js, pen test AD25996). The pickers offer only those, since
// image/* would offer SVG, and a drop or a paste is checked here before it is uploaded.
const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.pdf';
const RECEIPT_FILE = /\.(pdf|png|jpe?g|gif|webp|hei[cf])$/i;

// ── Dropping a receipt on the screen ─────────────────────────────────────────
//
// "Could we give a drag and drop option for a receipt in addition to browsing?"
//
// Worth doing properly rather than as a decoration, because the shortest path from a receipt
// to this app is usually a file already open on somebody's desktop or a screenshot on their
// clipboard, and a file picker makes both of those into a three-step detour.
//
// So this handles all three ways a file arrives:
//
//   • DROPPED onto the zone. Dragging over the page anywhere is ignored; only a drop on the
//     zone itself counts, and the browser's default (navigating away to the file) is
//     cancelled on both dragover and drop — miss either and dropping a PDF replaces the app
//     with the PDF.
//   • PASTED, once the zone has focus. Screenshot a receipt, click here, press Ctrl+V. This
//     is the fastest route of the three and no file picker offers it.
//   • BROWSED, as before, for anybody who wants the dialog.
//
// Several files at once are accepted: a receipt is often two photographs, and the transaction
// holds as many pages as it needs.
// `error` is the server's refusal of the last upload, shown here because this is where the
// person dropped the file.
function ReceiptDrop({ onFiles, busy, compact = false, label = 'Add the receipt', error = null }) {
  const [over, setOver] = useState(false);
  const [focused, setFocused] = useState(false);
  const [refused, setRefused] = useState(null);
  const inputRef = useRef(null);

  // All or nothing: uploading the photos out of a mixed drop would leave the person
  // unsure which pages went on.
  const accept = (list) => {
    const files = [...(list || [])].filter(f => f && f.size > 0);
    const wrong = files.filter(f => !RECEIPT_FILE.test(f.name));
    if (wrong.length) {
      setRefused(`${wrong.map(f => f.name).join(', ')} ${wrong.length === 1 ? 'is not a photo or a PDF' : 'are not photos or PDFs'}, so nothing has been uploaded. A receipt can be a JPG, PNG, GIF, WebP or HEIC photo, or a PDF.`);
      return;
    }
    setRefused(null);
    if (files.length) onFiles(files);
  };

  // The hidden input is a child of the zone, so the click it receives bubbles back up to
  // the zone's own handler — which would call click() on it again and re-enter, opening the
  // file dialog twice. Only a click on the zone itself opens it.
  const openPicker = (e) => { if (!e || e.target !== inputRef.current) inputRef.current?.click(); };

  return (
    <div>
      <div
        tabIndex={0}
        onDragEnter={e => { e.preventDefault(); setOver(true); }}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={e => { e.preventDefault(); setOver(false); }}
        onDrop={e => { e.preventDefault(); setOver(false); accept(e.dataTransfer?.files); }}
        onPaste={e => accept([...(e.clipboardData?.files || [])])}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={openPicker}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
        role="button"
        aria-label={label}
        className={`rounded-lg border-2 border-dashed cursor-pointer transition-colors text-center
          ${compact ? 'px-3 py-2' : 'px-3 py-4'}
          ${busy ? 'opacity-60 pointer-events-none' : ''}
          ${over ? 'border-navy bg-navy/5' : focused ? 'border-navy/60 bg-gray-50' : 'border-gray-300 bg-gray-50/60 hover:border-navy/50 hover:bg-gray-50'}`}
      >
        <div className={`${T.body} font-semibold text-gray-700`}>
          {busy ? 'Uploading…' : over ? 'Drop it here' : label}
        </div>
        {!compact && (
          <div className={`${T.meta} text-gray-500 mt-0.5`}>
            Drag a file in, paste a screenshot, or click to browse. A photo, a PDF or several pages all work.
          </div>
        )}
        <input ref={inputRef} type="file" multiple className="hidden" accept={RECEIPT_ACCEPT}
          disabled={busy} onChange={e => { accept(e.target.files); e.target.value = ''; }} />
      </div>
      {(refused || error) && (
        <div role="alert" className={`${T.meta} mt-1 px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700 font-semibold`}>
          {refused || error}
        </div>
      )}
    </div>
  );
}

// ── A list you can type at ───────────────────────────────────────────────────
//
// "Give an ability to search the ledger rather than just a long drop down."
//
// The nominal codes run to a couple of hundred and the Sage contact list to several
// hundred, and a native <select> makes you scroll all of them: you cannot type "fuel",
// and the one-letter jump lands you on the wrong Costa. Both lists are already loaded on
// this screen, so the search is instant and local — no endpoint, no debounce, no waiting.
//
// The results are rendered IN FLOW rather than as a floating layer. Both places this is
// used sit inside a horizontally scrolling table, and `overflow-x: auto` makes the other
// axis scroll too, so an absolutely positioned menu would be clipped by the container
// instead of overlapping it. A list that pushes the row down is a little less elegant and
// always visible, which is the better trade.
function SearchPicker({
  value, options, onChange, placeholder = 'search…', emptyLabel = '— none —',
  disabled = false, allowClear = true, className = '', onCreate = null, createLabel = null,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const selected = options.find(o => o.id === value) || null;
  // Every word has to appear somewhere in the option: "veh fuel" finds "7300 · Vehicle
  // Fuel", and so does "7300". Matching the whole phrase in order would not.
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (words.length
    ? options.filter(o => {
        const hay = `${o.label} ${o.sub || ''}`.toLowerCase();
        return words.every(w => hay.includes(w));
      })
    : options).slice(0, 60);

  const choose = (id) => { onChange(id); setOpen(false); setQ(''); };
  const key = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[cursor]) choose(matches[cursor].id);
      else if (onCreate && q.trim()) { onCreate(q.trim()); setOpen(false); setQ(''); }
    } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setQ(''); inputRef.current?.blur(); }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        value={open ? q : (selected ? selected.label : '')}
        // While searching, the current choice stays visible as the placeholder — otherwise
        // opening the list to look at the options loses sight of what is already set.
        placeholder={open && selected ? `${selected.label} — type to change` : placeholder}
        disabled={disabled}
        onFocus={() => { setOpen(true); setQ(''); setCursor(0); }}
        onChange={e => { setQ(e.target.value); setCursor(0); setOpen(true); }}
        onKeyDown={key}
        className={`${sel} w-full ${selected && !open ? 'text-gray-800' : ''}`}
      />
      {open && (
        <div className="mt-1 border border-gray-200 rounded bg-white shadow-sm">
          <div className="max-h-48 overflow-y-auto">
            {allowClear && (
              // onMouseDown, not onClick: the input's blur fires first and would close the
              // list before a click could land on it.
              <button type="button" onMouseDown={e => { e.preventDefault(); choose(''); }}
                className={`${T.meta} w-full text-left px-2 py-1 text-gray-500 hover:bg-gray-50`}>
                {emptyLabel}
              </button>
            )}
            {matches.map((o, i) => (
              <button key={o.id} type="button" onMouseDown={e => { e.preventDefault(); choose(o.id); }}
                onMouseEnter={() => setCursor(i)}
                className={`${T.body} w-full text-left px-2 py-1 ${i === cursor ? 'bg-navy/10' : 'hover:bg-gray-50'} ${o.id === value ? 'font-semibold' : ''}`}>
                {o.label}
                {o.sub && <span className={`${T.meta} text-gray-400 ml-1`}>{o.sub}</span>}
              </button>
            ))}
            {!matches.length && (
              <div className={`${T.meta} px-2 py-1.5 text-gray-500`}>
                Nothing matches “{q}”.{onCreate ? ' You can create it below.' : ''}
              </div>
            )}
          </div>
          {onCreate && q.trim().length > 1 && (
            <button type="button" onMouseDown={e => { e.preventDefault(); onCreate(q.trim()); setOpen(false); setQ(''); }}
              className={`${T.meta} w-full text-left px-2 py-1.5 border-t border-gray-200 text-navy font-semibold hover:bg-navy/5`}>
              ＋ {createLabel ? createLabel(q.trim()) : `Create “${q.trim()}”`}
            </button>
          )}
          <button type="button" onMouseDown={e => { e.preventDefault(); setOpen(false); setQ(''); }}
            className={`${T.meta} w-full text-left px-2 py-1 border-t border-gray-100 text-gray-400 hover:bg-gray-50`}>
            close
          </button>
        </div>
      )}
    </div>
  );
}

// The two lists this screen picks from, shaped for the picker once rather than at each
// call site — so a nominal code reads the same way everywhere it is chosen.
const ledgerOptions = (ledgers) => (ledgers || []).map(l => ({
  id: l.id, label: `${l.nominal_code} · ${l.name}`,
}));
// The list this screen holds is already filtered to suppliers, so the reference is what
// the second line is for: two suppliers with similar names are told apart by their Sage
// reference, not by reading the name again.
const contactOptions = (contacts) => (contacts || []).map(c => ({
  id: c.id, label: c.name, sub: c.reference || null,
}));

// ── Import ───────────────────────────────────────────────────────────────────
//
// The zip is unpacked in the browser and its parts go up one at a time. Progress is
// shown per file because a month's export is a few hundred of them, and a bar that
// tells you nothing for ninety seconds is indistinguishable from a hang — which is
// exactly the complaint the Sage ledger search earned.
function ImportPanel({ onDone, onFlash }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  // The latest eight, sorted among themselves.
  const recent = useMemo(() => history.slice(0, 8), [history]);
  const historySort = useSort(recent, {
    uploaded: h => h.uploaded_at, file: h => h.filename, rows: h => Number(h.rows_seen) || 0,
    new: h => Number(h.txns_new) || 0, held: h => Number(h.txns_updated) || 0,
    receipts: h => Number(h.receipts_new) || 0, by: h => h.uploaded_by_name,
  }, null, { uploaded: 'desc', rows: 'desc', new: 'desc', held: 'desc', receipts: 'desc' });

  const loadHistory = useCallback(async () => {
    try { setHistory((await api.get('cot/imports'))?.imports || []); } catch { /* quiet */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null); setProgress('Opening the zip…');
    try {
      const zip = await readZip(file);
      const csvName = zip.names.find(n => /\.csv$/i.test(n));
      if (!csvName) throw new Error('There is no transactions CSV in that zip.');
      const rows = parseCsv(new TextDecoder().decode(await zip.read(csvName)));
      if (!rows.length) throw new Error('The transactions CSV has no rows.');

      const { id } = await api.post('cot/imports', { filename: file.name });

      // Transactions first, in batches: they have to exist before a receipt can be
      // matched to one.
      let txnTotals = { created: 0, updated: 0, repeats_flagged: 0, historical: 0, skipped: [] };
      for (let i = 0; i < rows.length; i += 100) {
        setProgress(`Transactions ${Math.min(i + 100, rows.length)} of ${rows.length}…`);
        const r = await api.post(`cot/imports/${id}/transactions`, { rows: rows.slice(i, i + 100) });
        txnTotals = {
          created: txnTotals.created + (r.created || 0),
          updated: txnTotals.updated + (r.updated || 0),
          repeats_flagged: txnTotals.repeats_flagged + (r.repeats_flagged || 0),
          // Imported before the cutover, so waived rather than chased. Counted across the
          // batches because a backfill arrives in forty of them and one batch's number would
          // read as the whole answer.
          historical: txnTotals.historical + (r.historical || 0),
          skipped: [...txnTotals.skipped, ...(r.skipped || [])],
        };
      }

      const receipts = zip.names.filter(n => /\.(pdf|jpe?g|png|heic|webp)$/i.test(n));
      let newR = 0, dupeR = 0, unmatched = [];
      for (let i = 0; i < receipts.length; i++) {
        const name = receipts[i];
        setProgress(`Receipts ${i + 1} of ${receipts.length}…`);
        try {
          const bytes = await zip.read(name);
          // Base64 in a JSON body, through the same api helper as everything else. The
          // first version posted multipart, which cannot work here: the Worker takes the
          // request body as JSON before any route sees it, so a multipart body was already
          // consumed by the time the handler looked — and the failure came back as an HTML
          // 403, which is why it read as "not valid JSON" rather than as an upload problem.
          const r = await api.post(`cot/imports/${id}/receipt`, {
            filename: name.split('/').pop(),
            content_type: contentTypeFor(name),
            data: toBase64(bytes),
          });
          if (r.duplicate) dupeR++;
          else if (r.unmatched) unmatched.push({ name: name.split('/').pop(), reason: r.reason });
          else newR++;
        } catch (e) {
          // One bad file does not lose the rest of the import.
          unmatched.push({ name: name.split('/').pop(), reason: e.message || 'could not be read' });
        }
      }

      const summary = await api.post(`cot/imports/${id}/finish`, {});
      setProgress(null);
      setResult({ ...summary, ...txnTotals, cutover_date: summary?.cutover_date || null,
        settled_in_sage: summary?.settled_in_sage || null,
        receipts_new: newR, receipts_duplicate: dupeR, unmatched });
      onFlash('ok', `${txnTotals.created} new transaction${txnTotals.created === 1 ? '' : 's'}, ${newR} receipt${newR === 1 ? '' : 's'}.`);
      await loadHistory();
      onDone?.();
    } catch (e) {
      setProgress(null);
      onFlash('error', e.message || 'The import failed.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Import an export from Capital on Tap</span>
        <label className={`${btn} border-navy bg-navy text-white hover:bg-navy/90 cursor-pointer`}>
          {busy ? (progress || 'Working…') : 'Choose the zip…'}
          <input ref={fileRef} type="file" accept=".zip" className="hidden" disabled={busy}
            onChange={e => run(e.target.files?.[0])} />
        </label>
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        The zip exactly as Capital on Tap exports it — the transactions CSV and the receipts folder. Safe to upload
        the same period again: transactions are recognised rather than duplicated, and receipts uploaded since last
        time are added. That is how a late receipt reaches us.
      </p>

      {result && (
        <div className="px-3 pb-2 space-y-1">
          <div className={`${T.body} px-2 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-900`}>
            <strong>{result.created}</strong> new transaction{result.created === 1 ? '' : 's'},{' '}
            <strong>{result.updated}</strong> already held,{' '}
            <strong>{result.receipts_new}</strong> new receipt{result.receipts_new === 1 ? '' : 's'}
            {result.receipts_duplicate ? `, ${result.receipts_duplicate} already had` : ''}.
          </div>
          {/* Two things that need a person, said rather than buried in a count. */}
          {result.repeats_flagged > 0 && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
              {result.repeats_flagged} transaction{result.repeats_flagged === 1 ? '' : 's'} look like a repeat of
              another on the same day, same card, same amount. Capital on Tap gives no transaction id, so buying the
              same coffee twice and a duplicated export row are indistinguishable — they are flagged on the payments
              list for you to confirm rather than being merged or accepted.
            </div>
          )}
          {/* ── A BACKFILL, AND WHAT WAS DONE WITH IT ──
              "If I upload all transactions from 1st Jan, will it cope… it can't show as
              outstanding etc." Anything before the cutover is imported for the record and
              for the spend reporting, with a waiver written against it so it never joins the
              Monday chase or the VAT-at-risk figure. Said here, because a silent decision
              about four hundred transactions is not one anybody can check. */}
          {result.historical > 0 && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-gray-300 bg-gray-50 text-gray-700`}>
              {result.historical} transaction{result.historical === 1 ? '' : 's'} dated before the cutover
              {result.cutover_date ? ` of ${fmtDay(result.cutover_date)}` : ''} were imported <strong>as history</strong>:
              nobody is asked for their receipts, they are not in finance's to-do, and they add nothing to the VAT at
              risk. Capital on Tap's own feed posted those to Sage at the time, and the crossover match links them to
              those entries. They still count in the spend reporting, which is the point of having them.
            </div>
          )}
          {result.settled_in_sage?.skipped && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-gray-300 bg-gray-50 text-gray-700`}>
              Nothing was paid or posted in Sage by this import. {result.settled_in_sage.skipped}
            </div>
          )}
          {result.retried_receipts?.matched > 0 && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-900`}>
              {result.retried_receipts.matched} receipt{result.retried_receipts.matched === 1 ? '' : 's'} from an
              earlier zip found their transaction in this one. Capital on Tap names receipts by clearance date, which
              lags the purchase, so a receipt often arrives in the export before its transaction does.
            </div>
          )}
          {result.unmatched?.length > 0 && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
              {result.unmatched.length} receipt{result.unmatched.length === 1 ? '' : 's'} could not be matched to a
              transaction yet. They are stored, not discarded, and every future import tries them again — a receipt
              stamped after the end of this export is simply waiting for the export that contains its transaction:
              <ul className="mt-1 ml-3 list-disc">
                {result.unmatched.slice(0, 6).map(u => <li key={u.name}>{u.name} — {u.reason}</li>)}
              </ul>
            </div>
          )}
          {result.skipped?.length > 0 && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-gray-300 bg-gray-50 text-gray-700`}>
              {result.skipped.length} CSV row{result.skipped.length === 1 ? '' : 's'} skipped: {result.skipped[0].reason}.
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-gray-50">
              <tr>{[['uploaded', 'Uploaded'], ['file', 'File'], ['rows', 'Rows'], ['new', 'New'], ['held', 'Held'],
                    ['receipts', 'Receipts'], ['by', 'By']].map(([k, h]) => (
                <SortTh key={k} col={k} label={h} sort={historySort.sort} onSort={historySort.toggle}
                  className={`${T.label} text-gray-400 px-3 py-1`} />
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {historySort.sorted.map(h => (
                <tr key={h.id}>
                  <td className={`${T.meta} text-gray-500 px-3 py-1 whitespace-nowrap`}>{fmtWhen(h.uploaded_at)}</td>
                  <td className={`${T.meta} text-gray-700 px-3 py-1 truncate max-w-[16rem]`}>{h.filename}</td>
                  <td className={`${T.meta} px-3 py-1`}>{h.rows_seen}</td>
                  <td className={`${T.meta} px-3 py-1 font-semibold`}>{h.txns_new}</td>
                  <td className={`${T.meta} text-gray-500 px-3 py-1`}>{h.txns_updated}</td>
                  <td className={`${T.meta} px-3 py-1`}>{h.receipts_new}{h.receipts_unmatched ? ` (${h.receipts_unmatched} unmatched)` : ''}</td>
                  <td className={`${T.meta} text-gray-500 px-3 py-1`}>{h.uploaded_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

// ── The receipt, in a modal ──────────────────────────────────────────────────
//
// It used to open in a new tab, which loses the transaction: you end up looking at a
// photograph of a till roll with no idea what it is supposed to reconcile to. The point of
// looking at a receipt is comparing it with the charge, so the charge comes with it.
function ReceiptModal({ transaction, receipts, index, onIndex, onClose }) {
  const r = receipts[index];
  useEffect(() => {
    const key = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index < receipts.length - 1) onIndex(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [index, receipts.length, onIndex, onClose]);
  if (!r) return null;

  const t = transaction;
  const isImage = /^image\//.test(r.content_type || '');
  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="px-3 py-2 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <div className={`${T.strong} text-navy`}>
              {t.merchant_name || t.merchant_raw || 'Receipt'} · {money(t.amount)}
            </div>
            <div className={`${T.meta} text-gray-500`}>
              {fmtDay(t.clearance_date)}
              {t.cardholder_name ? ` · ${t.cardholder_name}` : ''} · card {t.card_ending}
              {t.original_currency && t.original_currency !== 'GBP'
                ? ` · charged ${t.original_amount} ${t.original_currency}` : ''}
              {t.note ? ` · “${t.note}”` : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="text-gray-400 hover:text-gray-800 text-lg leading-none flex-shrink-0">✕</button>
        </div>

        {/* What we are reconciling to, next to the paper — the whole reason this is a modal
            and not a new tab. */}
        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex gap-4 flex-wrap">
          {[
            ['Charged', money(t.amount)],
            ['Net', t.net_amount != null ? money(t.net_amount) : '—'],
            ['VAT', t.vat_amount != null ? money(t.vat_amount) : 'not decided'],
            ['Nominal', t.ledger_code ? `${t.ledger_code} ${t.ledger_name || ''}` : 'not coded'],
            ['Sage', t.sage_invoice_id ? (t.sage_invoice_number || 'posted') : 'not yet'],
          ].map(([k, v]) => (
            <span key={k} className={T.meta}>
              <span className="text-gray-400 uppercase font-semibold">{k} </span>
              <span className="text-gray-800 font-semibold">{v}</span>
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 flex items-start justify-center p-2">
          {isImage
            ? <img src={`/api/cot/receipts/${r.id}/download`} alt={r.filename}
                className="max-w-full object-contain" />
            : <iframe src={`/api/cot/receipts/${r.id}/download`} title={r.filename}
                className="w-full h-[70vh] bg-white border-0" />}
        </div>

        <div className="px-3 py-1.5 border-t border-gray-200 flex items-center justify-between gap-2">
          <span className={`${T.meta} text-gray-500`}>
            {r.filename}
            {r.page_count ? ` · page ${r.page_no} of ${r.page_count}` : ''}
            {r.source === 'ops_upload' ? ' · uploaded here' : ' · from Capital on Tap'}
          </span>
          <span className="flex items-center gap-1">
            {receipts.length > 1 && (
              <>
                <button onClick={() => onIndex(Math.max(0, index - 1))} disabled={index === 0}
                  className={`${btn} border-gray-300 text-gray-700 bg-white disabled:opacity-30`}>‹ Previous</button>
                <span className={`${T.meta} text-gray-500`}>{index + 1} of {receipts.length}</span>
                <button onClick={() => onIndex(Math.min(receipts.length - 1, index + 1))}
                  disabled={index === receipts.length - 1}
                  className={`${btn} border-gray-300 text-gray-700 bg-white disabled:opacity-30`}>Next ›</button>
              </>
            )}
            <a href={`/api/cot/receipts/${r.id}/download`} target="_blank" rel="noreferrer"
              className={`${btn} border-gray-300 text-gray-600 bg-white hover:bg-gray-50`}>Open full size</a>
          </span>
        </div>
      </div>
    </div>
  );
}

// The treatments under which nothing is reclaimed, each for its own reason. Mirrors the
// server's list; the labels and explanations come from the server so they cannot drift.
const NO_RECLAIM = new Set(['exempt', 'reverse_charge', 'not_registered', 'blocked', 'outside_scope']);
// What a line is called on the Sage entry when a charge is split automatically.
const cotLineName = (t) => (t.merchant_name || t.merchant_raw || t.description || 'Card purchase').slice(0, 60);

// ── EACH SIDE OF A REFUND NAMES THE OTHER ────────────────────────────────────
//
// Once linked, the charge and its refund are one story told on two rows: the refund says
// which charge it reverses, and the charge says how much of it came back. Without this a
// hotel booked and refunded read as £180 spent on one line and a stray -£180 on another.
// The same sentence on finance's list and on the cardholder's own.
function refundStory(t) {
  if (t.kind === 'refund') {
    if (!t.refund_of_transaction_id) return { text: 'Money back from the merchant · not yet matched to the charge it refunds', open: true };
    return { text: `Refund of the ${money(t.refund_of_amount)} charge on ${fmtDay(t.refund_of_date)}` };
  }
  const back = Number(t.refunded_amount) || 0;
  if (!(back > 0)) return null;
  const whole = Math.abs(back - Math.abs(Number(t.amount) || 0)) < 0.005;
  return whole
    ? { text: `Refunded in full on ${fmtDay(t.refunded_on)}`, whole }
    : { text: `${money(back)} refunded on ${fmtDay(t.refunded_on)}, so ${money(Math.abs(Number(t.amount)) - back)} of it was spent` };
}

function RefundNote({ t }) {
  const story = refundStory(t);
  if (!story) return null;
  return (
    <span className={`${T.meta} block ${story.open ? 'text-amber-800' : 'text-emerald-800'}`}>
      ↩ {story.text}
    </span>
  );
}

// ── MONEY BACK FROM THE MERCHANT ─────────────────────────────────────────────
//
// A refund used to be refused as "not spend", alongside the monthly repayment. True of the
// repayment and wrong of a refund: the money came back, so the original cost is overstated
// by it and the VAT on that cost is over-reclaimed. Nothing was going to correct either,
// and nobody chases a row the screen says is not theirs to deal with.
//
// It posts as a purchase credit note, and it has to carry the nominal code and VAT treatment
// of the charge it reverses — otherwise it corrects the total and leaves the VAT wrong, which
// is the more expensive half. So the first thing offered is which charge that was.
function RefundLink({ t, detail, busy, onLink }) {
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const linked = detail?.refund_of || t.refund_of_transaction_id;

  const find = async () => {
    setLoading(true);
    try { setCandidates((await api.get(`cot/transactions/${t.id}/refund-of`))?.candidates || []); }
    catch { setCandidates([]); }
    finally { setLoading(false); }
  };

  if (linked) {
    return (
      <div className={`${T.meta} mb-1 px-2 py-1.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-900`}>
        Credited against the {t.refund_of_amount != null ? `${money(t.refund_of_amount)} charge on ${fmtDay(t.refund_of_date)}` : 'charge it refunds'},
        so it carries that charge’s nominal code, treatment and VAT
        {t.vat_amount != null ? ` (${money(Math.abs(t.vat_amount))} given back)` : ' once that charge is coded'}.
        {/* The row shows blockers, not the assessment's warnings, so the assumption is said here. */}
        {t.vat_assumed && t.vat_amount != null
          ? ' A part refund, so that is the charge’s VAT in proportion. If the merchant’s credit states a different figure, enter that one.'
          : ''}
        <button onClick={() => onLink(null)} disabled={busy}
          className="ml-2 underline font-semibold hover:text-emerald-950">unlink</button>
      </div>
    );
  }
  return (
    <div className="mb-1 px-2 py-1.5 rounded border border-blue-200 bg-blue-50">
      <div className={`${T.meta} text-blue-900`}>
        This is {money(Math.abs(t.amount))} back from the merchant, so it posts as a <strong>purchase credit
        note</strong>. Link it to the charge it refunds and it will reverse the VAT as well as the cost.
      </div>
      {candidates == null ? (
        <button onClick={find} disabled={busy || loading}
          className={`${btn} mt-1 border-navy bg-navy text-white hover:bg-navy/90`}>
          {loading ? 'Looking…' : 'Find the charge this refunds'}
        </button>
      ) : candidates.length === 0 ? (
        <div className={`${T.meta} mt-1 text-blue-900`}>
          Nothing at this merchant for {money(Math.abs(t.amount))} or more. It can still post as a credit note on
          its own nominal code, but check the original was not on a different card.
        </div>
      ) : (
        <div className="mt-1 space-y-1">
          {candidates.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-2 bg-white rounded border border-blue-100 px-2 py-1 flex-wrap">
              <span className={T.meta}>
                <strong className="text-gray-800">{fmtDay(c.clearance_date)}</strong> · {money(c.amount)}
                {c.exact && <span className="text-emerald-700 font-semibold"> · same amount</span>}
                {c.partial && <span className="text-gray-500"> · part refund of this</span>}
                {c.ledger_code ? ` · ${c.ledger_code} ${c.ledger_name || ''}` : ' · not coded'}
                {c.sage_invoice_id ? ` · in Sage as ${c.sage_invoice_number || 'posted'}` : ' · not yet in Sage'}
              </span>
              <button onClick={() => onLink(c.id)} disabled={busy}
                className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>This one</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SPLITTING ONE CHARGE ─────────────────────────────────────────────────────
//
// For the charge that is more than one thing. The case that forced it: a DHL charge of
// £59.96 which is an import charge. Finance entered the whole amount as VAT — the reclaim
// being the point of it — which left the net at nil, the derived Sage rate at 0%, and Sage
// refusing the entry outright because a zero-rated line cannot carry a VAT amount.
//
// Fixing only the rate would have let it post as if all £59.96 were reclaimable VAT, and an
// import charge from a courier is usually three things with three different answers:
//
//   • IMPORT VAT — reclaimable, and with no net behind it on this invoice. The goods'
//     value sits with the overseas supplier; only the tax arrives here.
//   • DUTY — a real cost, and NOT VAT. Not reclaimable, whatever the courier's paperwork
//     calls it.
//   • THE COURIER'S FEE — a cost, normally with VAT on it at the standard rate.
//
// So the charge gets lines. The running total is the whole design of this panel: the one
// thing that can go wrong here is a split that does not add up to what the card was
// charged, and it should be impossible to save one without having seen that.
const LINE_KINDS = [
  ['cost', 'Cost', 'A cost with its own VAT treatment.'],
  ['import_vat', 'Import VAT', 'Reclaimable. No net against it — the goods’ value is with the overseas supplier.'],
  ['duty', 'Duty', 'A cost, not VAT. Never reclaimable, whatever the courier calls it.'],
  ['tip', 'Gratuity', 'Outside the scope of VAT. Not consideration for a supply, so nothing is reclaimed.'],
  ['outside_scope', 'Outside the scope', 'No UK VAT applies to this part of the charge.'],
];

// ── THE KINDS THAT SETTLE THEIR OWN VAT ──────────────────────────────────────
//
// Duty is not VAT, a gratuity is not consideration for a supply, and import VAT has no net
// behind it. None of those is a treatment somebody chooses, so those lines do not get asked.
const KIND_DECIDES_VAT = new Set(['import_vat', 'duty', 'tip', 'outside_scope']);

// What rate a treatment charges. The only two that charge anything are the two that charge
// anything; everything else reclaims nothing, for its own reason, and the reason is what
// the treatment records. Nil is the right rate for all of them.
const TREATMENT_RATE = { standard: 20, reduced: 5 };
const rateOf = (treatment) => TREATMENT_RATE[treatment] ?? 0;

// ── THE ARITHMETIC NOBODY SHOULD BE DOING BY HAND ────────────────────────────
//
// A £6.50 Tesco receipt is a bottle of water at £1.75 zero rated and a hot sandwich at
// £4.75 standard. Getting that into the old editor meant working out that the VAT inside
// £4.75 is £0.79 and the net is £3.96, in your head, and typing both. What was reported
// instead was £3.80 and £0.95 — a fifth of the gross rather than a sixth — which is 25%,
// a rate nobody charges, and Sage refused it minutes after a save that said it worked.
//
// So the line takes whichever figure the receipt actually shows and works out the other
// two. A till receipt shows gross; a VAT invoice shows net and VAT. Both are one number now.
const fromGross = (grossValue, treatment) => {
  const g = round2(grossValue);
  const p = rateOf(treatment) / 100;
  const vat = round2(g * p / (1 + p));
  return { net: round2(g - vat), vat };
};
const fromNet = (netValue, treatment) => {
  const n = round2(netValue);
  return { net: n, vat: round2(n * rateOf(treatment) / 100) };
};

// Currency in an input, always to the penny. "3.8" and "0" in a money box are how a figure
// stops looking like a figure, and half the reason the split panel read as approximate.
const pence = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? '' : Number(v).toFixed(2));

// ── A VAT FIGURE THAT IS NOT A RATE ANYTHING CHARGES ─────────────────────────
//
// The refusal that came back from Sage on a real split:
//
//   "Other items": No Sage tax rate matches 25% (£0.95 VAT on £3.80 net)
//
// £0.95 is 20% OF £4.75, the gross. VAT at 20% is a fifth of the NET and a sixth of the
// gross, so taking a fifth of the gross overstates it and lands at exactly 25% of the net.
// It is the commonest VAT slip there is, and finding out about it from Sage, minutes later,
// after a save that said it worked, is the worst possible moment.
//
// Only rates ABOVE the highest one anybody charges are flagged. A line at 12% is not
// necessarily wrong — a till receipt with hot and cold food on it blends to something in
// between, and the posting decomposes that into two Sage lines quite happily. Above 20%
// no mixture of real rates can produce it, so it is a mistake with certainty.
const UK_CHARGING_RATES = [20, 5];

function lineVatProblem(net, vat, kind = null) {
  // Import VAT has no net behind it and duty is not VAT at all. Neither is a rate, so
  // neither has a percentage worth checking. Mirrors cotLineRateAboveEverything on the
  // server, which refuses the same lines — the screen only gets there first.
  if (kind === 'import_vat' || kind === 'duty' || kind === 'tip' || kind === 'outside_scope') return null;
  const n = round2(Number(net) || 0);
  const v = round2(Number(vat) || 0);
  if (!(n > 0) || !(v > 0)) return null;
  const percent = Math.round((v / n) * 10000) / 100;
  if (percent <= Math.max(...UK_CHARGING_RATES) + 0.5) return null;
  const gross = round2(n + v);
  // The fifth-of-the-gross mistake, recognised to the penny — guessing at somebody's
  // arithmetic is only worth doing when it is certain, because the fix is one press.
  for (const rate of UK_CHARGING_RATES) {
    const p = rate / 100;
    if (Math.abs(round2(gross * p) - v) >= 0.005) continue;
    const rightVat = round2(gross * p / (1 + p));
    return { percent, gross, rate, fix: { net: round2(gross - rightVat), vat: rightVat } };
  }
  return { percent, gross, rate: null, fix: null };
}

function SplitEditor({ t, ledgers, existing, busy, treatments, onSave, onCancel }) {
  const gross = round2(Number(t.amount) || 0);
  // A line inherits the charge's treatment, because a split usually separates two parts of
  // one purchase and most of it is whatever the charge already was.
  const blank = () => ({
    description: '', sage_ledger_account_id: '', net: '', vat: '', gross: '', kind: 'cost',
    vat_treatment: t.vat_treatment || 'standard',
  });
  const [rows, setRows] = useState(() => existing?.length
    ? existing.map(l => ({
        description: l.description || '',
        sage_ledger_account_id: l.sage_ledger_account_id || '',
        net: pence(l.net_amount),
        vat: pence(l.vat_amount),
        gross: pence(round2((Number(l.net_amount) || 0) + (Number(l.vat_amount) || 0))),
        kind: l.kind || 'cost',
        vat_treatment: l.vat_treatment || t.vat_treatment || 'standard',
      }))
    : [blank(), blank()]);

  const set = (i, patch) => setRows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r));

  // ── ONE FIGURE IN, THREE FIGURES OUT ──
  //
  // Whichever box is typed in, the treatment's rate fills the other two. Typing straight
  // into VAT overrides it and leaves the net alone, because a receipt occasionally states a
  // VAT figure that is not exactly the rate on the net and the receipt wins.
  //
  // The gross is held in state rather than derived from net + VAT on every render. Derived
  // looks tidier and is unusable: typing "4" of "4.75" would immediately rewrite the box to
  // "4.00" and the next keystroke would land after it.
  const both = (net, vat) => ({ net: net.toFixed(2), vat: vat.toFixed(2), gross: round2(net + vat).toFixed(2) });
  const setGross = (i, raw) => {
    const r = rows[i];
    if (raw === '') { set(i, { gross: '', net: '', vat: '' }); return; }
    const n = Number(raw);
    if (!Number.isFinite(n)) { set(i, { gross: raw }); return; }
    const { net, vat } = KIND_DECIDES_VAT.has(r.kind) ? { net: round2(n), vat: 0 } : fromGross(n, r.vat_treatment);
    set(i, { gross: raw, net: net.toFixed(2), vat: vat.toFixed(2) });
  };
  const setNet = (i, raw) => {
    const r = rows[i];
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) {
      set(i, { net: raw, gross: pence(round2((Number(raw) || 0) + (Number(r.vat) || 0))) });
      return;
    }
    if (KIND_DECIDES_VAT.has(r.kind)) {
      set(i, { net: raw, gross: round2(n + (Number(r.vat) || 0)).toFixed(2) });
      return;
    }
    const vat = fromNet(n, r.vat_treatment).vat;
    set(i, { net: raw, vat: vat.toFixed(2), gross: round2(n + vat).toFixed(2) });
  };
  const setVat = (i, raw) => {
    const r = rows[i];
    set(i, { vat: raw, gross: pence(round2((Number(r.net) || 0) + (Number(raw) || 0))) });
  };
  // Changing the treatment re-works the VAT on the money already typed, which is the point
  // of having it on the line: picking "zero rated" should clear the VAT, not leave the old
  // figure sitting under a treatment that contradicts it. The GROSS is what stays put — it
  // is what the receipt says, and no treatment changes what was charged.
  const setTreatment = (i, vat_treatment) => {
    const r = rows[i];
    const g = Number(r.gross);
    if (!Number.isFinite(g) || r.gross === '' || KIND_DECIDES_VAT.has(r.kind)) { set(i, { vat_treatment }); return; }
    const { net, vat } = fromGross(g, vat_treatment);
    set(i, { vat_treatment, ...both(net, vat) });
  };
  const setKind = (i, kind) => {
    const r = rows[i];
    // Duty and a gratuity carry no VAT by definition, so moving a line to one of them puts
    // the whole gross into the net rather than leaving a figure that can never post.
    if (KIND_DECIDES_VAT.has(kind) && kind !== 'import_vat') {
      const g = Number(r.gross);
      set(i, Number.isFinite(g) && r.gross !== ''
        ? { kind, net: g.toFixed(2), vat: '0.00', gross: g.toFixed(2) }
        : { kind, vat: '0.00' });
      return;
    }
    set(i, { kind });
  };

  const sumNet = round2(rows.reduce((n, r) => n + (Number(r.net) || 0), 0));
  const sumVat = round2(rows.reduce((n, r) => n + (Number(r.vat) || 0), 0));
  const total = round2(sumNet + sumVat);
  const left = round2(gross - total);
  const balanced = Math.abs(left) < 0.011;
  const complete = rows.every(r => r.description.trim() && r.sage_ledger_account_id
    && ((Number(r.net) || 0) !== 0 || (Number(r.vat) || 0) !== 0));
  // ── EVERY REASON IT CANNOT POST, IN ONE PLACE ──
  //
  // Reported: "its giving a warning in red and looks accurate, but then a green banner
  // saying its good with a tick." Both were true of what they measured — the money did add
  // up, and a line was still impossible — and the panel had no notion of an overall verdict,
  // so the loudest thing on it was a tick. The tick now waits for everything.
  const lineFaults = rows.reduce((n, r) => n + (lineVatProblem(r.net, r.vat, r.kind) ? 1 : 0), 0);
  const ok = balanced && complete && lineFaults === 0;

  // An import charge, seeded so the totals already balance and only the split of the money
  // is left to type. Everything is a guess until finance put the real figures in, so the
  // guess is the honest one: all of it as import VAT, with a duty line at nil to fill in.
  const seedImport = () => setRows([
    { description: 'Import VAT', sage_ledger_account_id: '', net: '', vat: gross.toFixed(2), gross: gross.toFixed(2), kind: 'import_vat', vat_treatment: 'standard' },
    { description: 'Import duty', sage_ledger_account_id: '', net: '0.00', vat: '0.00', gross: '0.00', kind: 'duty', vat_treatment: 'outside_scope' },
    { description: 'Carrier handling fee', sage_ledger_account_id: '', net: '0.00', vat: '0.00', gross: '0.00', kind: 'cost', vat_treatment: 'standard' },
  ]);

  return (
    <div className="mt-2 rounded-lg border border-navy/30 bg-white p-2.5">
      <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
        <div>
          <div className={`${T.strong} text-navy`}>Split this charge</div>
          <div className={`${T.meta} text-gray-500`}>
            Each line posts as its own Sage line with its own nominal code, so the VAT on one part can be
            reclaimed without reclaiming another. Duty and a carrier’s handling fee are costs, not VAT.
          </div>
        </div>
        <button onClick={seedImport} disabled={busy}
          className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
          Start from an import charge
        </button>
      </div>

      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-end gap-1.5 flex-wrap border-t border-gray-100 pt-1.5">
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>What it is</span>
              <input value={r.description} onChange={e => set(i, { description: e.target.value })}
                placeholder="Import VAT · Duty · Handling fee"
                className={`${T.body} border border-gray-300 rounded-md px-2 py-1 w-[11rem]`} />
            </label>
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>What kind</span>
              <select value={r.kind} onChange={e => setKind(i, e.target.value)} className={sel} disabled={busy}
                title={(LINE_KINDS.find(([v]) => v === r.kind) || [])[2] || ''}>
                {LINE_KINDS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </label>
            {/* ── AND ITS OWN VAT TREATMENT ──
                One charge can carry two, which is most of why a receipt gets split at all:
                the bottle of water is zero rated and the hot sandwich beside it is not. The
                kinds that settle their own VAT are not asked — duty is not VAT whatever
                anybody picks. */}
            {!KIND_DECIDES_VAT.has(r.kind) && (
              <label className="block">
                <span className={`${T.label} text-gray-400 block`}>VAT treatment</span>
                <select value={r.vat_treatment} onChange={e => setTreatment(i, e.target.value)}
                  className={`${sel} w-[11rem]`} disabled={busy}
                  title={(treatments.find(x => x.id === r.vat_treatment) || {}).hint || ''}>
                  {treatments.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
              </label>
            )}
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>Nominal code</span>
              <SearchPicker
                value={r.sage_ledger_account_id} options={ledgerOptions(ledgers)}
                onChange={v => set(i, { sage_ledger_account_id: v })}
                disabled={busy} className="w-[13rem]"
                placeholder="search the ledger…" emptyLabel="— no code —"
              />
            </label>
            {/* ── WHICHEVER FIGURE THE RECEIPT SHOWS ──
                A till receipt shows the gross; a VAT invoice shows net and VAT. Typing any
                one of the three fills the others from the treatment's rate, so the sum
                that was being done in somebody's head — and getting the reported £0.95
                instead of £0.79 — is not done by hand at all. */}
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>Total inc VAT</span>
              <input value={r.gross} onChange={e => setGross(i, e.target.value)}
                onBlur={e => setGross(i, pence(e.target.value))} placeholder="0.00"
                disabled={busy} className={`${sel} w-24 font-semibold`} inputMode="decimal"
                title="Type what the receipt says and the net and VAT follow from the treatment" />
            </label>
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>Net</span>
              <input value={r.net} onChange={e => setNet(i, e.target.value)}
                onBlur={e => setNet(i, pence(e.target.value))} placeholder="0.00"
                disabled={busy} className={`${sel} w-20`} inputMode="decimal" />
            </label>
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>VAT</span>
              <input value={r.vat} onChange={e => setVat(i, e.target.value)}
                onBlur={e => setVat(i, pence(e.target.value))} placeholder="0.00"
                disabled={busy || KIND_DECIDES_VAT.has(r.kind) && r.kind !== 'import_vat'}
                className={`${sel} w-20 ${KIND_DECIDES_VAT.has(r.kind) && r.kind !== 'import_vat' ? 'bg-gray-100 text-gray-500' : ''}`}
                inputMode="decimal"
                title={KIND_DECIDES_VAT.has(r.kind) && r.kind !== 'import_vat'
                  ? 'Nothing is reclaimed on this kind of line'
                  : 'Worked out from the net. Type over it if the receipt says otherwise.'} />
            </label>
            {rows.length > 2 && (
              <button onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} disabled={busy}
                className={`${T.meta} text-red-700 hover:text-red-900 underline pb-1`}>remove</button>
            )}
            {r.kind === 'duty' && (Number(r.vat) || 0) > 0 && (
              <div className={`${T.meta} w-full text-red-800 font-semibold`}>
                Duty is not VAT and cannot be reclaimed. Put this amount in Net.
              </div>
            )}
            {r.kind === 'import_vat' && (Number(r.net) || 0) > 0 && (
              <div className={`${T.meta} w-full text-amber-900`}>
                Import VAT has no net behind it. If this is the carrier’s fee, make it a Cost line.
              </div>
            )}
            {/* Caught here rather than by Sage. The same figures reach the same conclusion
                either way; the difference is whether it arrives while the line is still in
                front of somebody, or minutes later as a refusal on a save that said it
                worked. */}
            {(() => {
              const bad = lineVatProblem(r.net, r.vat, r.kind);
              if (!bad) return null;
              return (
                <div className={`${T.meta} w-full text-red-800`}>
                  <span className="font-semibold">
                    {money(r.vat)} on {money(r.net)} net is {bad.percent}% VAT, and nothing is charged at {bad.percent}%.
                  </span>
                  {bad.fix ? (
                    <>
                      {' '}{money(r.vat)} is {bad.rate}% of {money(bad.gross)}, the gross — at {bad.rate}% the VAT
                      inside {money(bad.gross)} is {money(bad.fix.vat)}, leaving {money(bad.fix.net)} net.
                      <button
                        onClick={() => set(i, { net: bad.fix.net.toFixed(2), vat: bad.fix.vat.toFixed(2) })}
                        disabled={busy}
                        className="ml-1 underline font-semibold text-red-900 hover:text-red-950">
                        Use {money(bad.fix.net)} + {money(bad.fix.vat)}
                      </button>
                    </>
                  ) : ' Check the receipt: this is above every rate there is, so no mixture of items can produce it.'}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      <button onClick={() => setRows(rs => [...rs, blank()])} disabled={busy}
        className={`${T.meta} mt-1.5 underline text-navy hover:text-gold`}>+ another line</button>

      {/* ── ONE VERDICT, COVERING EVERYTHING ──
          This used to report only whether the money added up, in green with a tick, while a
          line above it was in red saying it could never post. Both statements were true and
          together they were useless. The arithmetic is still shown, because it is what
          somebody is watching while they type, but the tick belongs to the whole panel. */}
      <div className={`${T.body} mt-2 px-2 py-1.5 rounded font-semibold ${
        ok ? 'bg-emerald-50 text-emerald-900'
          : lineFaults ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'}`}>
        {money(sumNet)} net + {money(sumVat)} VAT = {money(total)} of {money(gross)} charged
        {balanced ? '' : ` · ${money(Math.abs(left))} ${left > 0 ? 'still to account for' : 'too much'}`}
        {ok ? ' ✓' : null}
        {lineFaults > 0 && (
          <span className="block font-normal">
            {lineFaults === 1 ? 'One line has' : `${lineFaults} lines have`} a VAT figure no rate can produce,
            so this cannot post yet. {balanced ? 'The totals add up, which is not the same thing.' : ''}
          </span>
        )}
        {!lineFaults && balanced && !complete && (
          <span className="block font-normal">
            Every line still needs a description and a nominal code.
          </span>
        )}
      </div>

      <div className="flex gap-2 mt-2 flex-wrap">
        <button onClick={() => onSave(rows)} disabled={busy || !ok}
          className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}
          title={!balanced ? 'The lines must come to the amount charged'
            : lineFaults ? 'A line carries a VAT figure no rate can produce'
            : !complete ? 'Every line needs a description, a nominal code and an amount' : ''}>
          Save the split
        </button>
        <button onClick={onCancel} disabled={busy}
          className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>Cancel</button>
        {existing?.length > 0 && (
          <button onClick={() => onSave([])} disabled={busy}
            className={`${btn} border-red-300 bg-white text-red-800 hover:bg-red-50`}>
            Remove the split
          </button>
        )}
      </div>
    </div>
  );
}

// ── One transaction ──────────────────────────────────────────────────────────
// ── SORTING THE TRANSACTION TABLES ───────────────────────────────────────────
//
// Payments, Marked no receipt and the decide view share one set of columns. Receipt and
// Sage sort by state, in the order the screen thinks of them, what still needs doing
// first. The server applies the same order to Payments (COT_SORTS in the router), which is
// sorted there because its list is capped; the other two arrive whole and sort here.
const TXN_COLUMNS = [['date', 'Date'], ['merchant', 'Merchant'], ['person', 'Who'], ['amount', 'Amount'],
  ['vat', 'VAT'], ['receipt', 'Receipt'], ['sage', 'Sage']];
const TXN_FIRST_DIR = { date: 'desc', amount: 'desc', vat: 'desc' };
const txnReceiptRank = (t) => (t.kind === 'refund' && !t.receipt_count ? 4 : t.receipt_count > 0 ? 3
  : t.receipt_waived_reason ? 2 : t.receipt_none_said_at ? 1 : 0);
const txnSageRank = (t) => {
  if (t.sage_invoice_id) return t.sage_payment_id || t.sage_payment_type ? 4 : 3;
  if (t.sage_excluded_reason) return 5;
  if (t.sage_match_candidates && t.sage_match_candidates !== '[]') return 1;
  return t.sage_ready ? 2 : 0;
};
const TXN_SORT = {
  date: t => t.clearance_date,
  merchant: t => t.merchant_name || t.merchant_raw || t.description,
  person: t => t.person_name || t.cardholder_name,
  amount: t => Number(t.amount),
  vat: t => (t.vat_amount == null ? null : Number(t.vat_amount)),
  receipt: txnReceiptRank,
  sage: txnSageRank,
};
function TxnHead({ sort, onSort }) {
  return (
    <tr>{TXN_COLUMNS.map(([k, h]) => (
      <SortTh key={k} col={k} label={h} sort={sort} onSort={onSort}
        right={k === 'amount' || k === 'vat'}
        title={k === 'receipt' ? 'Sort by receipt: missing first, then said none, accepted, received'
          : k === 'sage' ? 'Sort by Sage: not ready first, then possible match, ready, owed, paid, not going'
          : undefined}
        className={`${T.label} text-gray-400 px-2 py-1`} />
    ))}</tr>
  );
}

function TransactionRow({ t, ledgers, contacts, isFinance, postingOn, onChanged, onFlash, settings, canDecide = isFinance, decideOnly = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [ledger, setLedger] = useState(t.sage_ledger_account_id || '');
  // Money back from the merchant. Its VAT is held negative, like its amount, but the box shows
  // the figure to give back: nobody reads "-£8.67" off a merchant's credit.
  const isRefund = t.kind === 'refund';
  const [vat, setVat] = useState(pence(t.vat_amount == null ? null : isRefund ? Math.abs(t.vat_amount) : t.vat_amount));
  // Why there is no VAT, as opposed to how much. The vocabulary comes from the server so it
  // cannot drift from the rules that act on it.
  const [treatment, setTreatment] = useState(t.vat_treatment || '');
  // What the last press of the button did, kept on the row until the next one.
  const [lastResult, setLastResult] = useState(null);
  // ── HOW LONG IT HAS BEEN WORKING ────────────────────────────────────────────
  //
  // Reported: "when it says Working… it looks stuck." A post is four round trips to Sage
  // and back — the duplicate check, the entry, a call per receipt page, and the payment
  // off the card account — and on a slow day that is a long time in front of a button
  // that says nothing but "Working…". Silence for fifteen seconds reads as broken, and a
  // second press on a posting is the one thing nobody should be tempted into.
  //
  // A counter, not a percentage. Nothing here knows which of the four steps Sage is on,
  // and a bar that moves on a timer would be an invention. Seconds are true.
  const [workingSince, setWorkingSince] = useState(null);
  const [workedFor, setWorkedFor] = useState(0);
  useEffect(() => {
    if (!workingSince) { setWorkedFor(0); return undefined; }
    const tick = setInterval(() => setWorkedFor(Math.round((Date.now() - workingSince) / 1000)), 500);
    return () => clearInterval(tick);
  }, [workingSince]);
  // Wraps anything that talks to Sage, so the clock starts and stops in one place rather
  // than in each of the six handlers that could forget.
  const working = async (fn) => {
    setBusy(true); setWorkingSince(Date.now());
    try { return await fn(); } finally { setBusy(false); setWorkingSince(null); }
  };
  const treatments = settings?.vat_treatments || [];
  const fileRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);

  const load = async () => {
    try { setDetail(await api.get(`cot/transactions/${t.id}`)); } catch { /* quiet */ }
  };
  const expand = async () => { setOpen(o => !o); if (!detail) await load(); };

  const blockers = (() => { try { return JSON.parse(t.sage_blockers || '[]'); } catch { return []; } })();
  // Whether saving will also post. Judged the same way the server judges it — a coded
  // transaction with a receipt, posting on, nothing outstanding — so the label cannot
  // promise something the save then refuses.
  const willPost = postingOn && !t.sage_invoice_id && !t.sage_excluded_reason
    && (ledger || t.sage_ledger_account_id) && (isRefund || t.receipt_count > 0 || t.receipt_waived_reason)
    && blockers.filter(b => !/no nominal code/i.test(b)).length === 0;

  // ── NOTHING RECLAIMED, AND NOTHING SAYING WHY ───────────────────────────────
  //
  // Read off what is in the boxes NOW rather than off the saved row, so it answers while
  // somebody is typing rather than after they have pressed save. The same rule the server
  // gates on: a nil reclaim is fine and needs a reason, and standard or reduced with the
  // VAT left empty is the one combination with no true answer behind it.
  //
  // A waived receipt where the VAT was given up is a reason made elsewhere, so it is not
  // asked for twice.
  // A split charge answers the VAT question on its lines, so the charge-level boxes and
  // the charge-level prompt both step aside for it.
  const isSplit = Number(t.line_count) > 0;
  const vatIsNil = NO_RECLAIM.has(treatment) || !(Number(vat) > 0);
  const nilVatUndecided = vatIsNil && !isSplit && !isRefund
    && (!treatment || treatment === 'standard' || treatment === 'reduced')
    && !(t.receipt_waived_reason && !t.receipt_waived_vat_ok)
    && Math.abs(Number(t.amount) || 0) > 0;
  // At the standard rate: a ceiling on what is being given up, not a prediction.
  const vatAtStake = Math.round(Math.abs(Number(t.amount) || 0) / 6 * 100) / 100;

  // One or several files. A receipt is often two photographs, and a drop or a multi-select
  // hands over a list — uploading only the first would silently lose the second page.
  const upload = async (files, { confirmShared = false } = {}) => {
    const list = Array.isArray(files) ? files : [files].filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    setUploadError(null);
    let added = 0, dupes = 0;
    try {
      for (const file of list) {
        const r = await api.post(`cot/transactions/${t.id}/receipt`, {
          filename: file.name,
          content_type: file.type || contentTypeFor(file.name),
          data: toBase64(await file.arrayBuffer()),
          confirm_shared: confirmShared,
        });
        if (r.error) throw new Error(r.error);
        if (r.duplicate) dupes++; else added++;
      }
      onFlash(added ? 'ok' : 'warn', [
        added ? `${added} receipt page${added === 1 ? '' : 's'} added.` : null,
        dupes ? `${dupes} already held against this transaction.` : null,
      ].filter(Boolean).join(' '));
      setDropOpen(false);
      setSharedAsk(null);
      await load();
      onChanged();
    } catch (e) {
      // ── THE SAME FILE, ALREADY ON SOMETHING ELSE ──
      // Held rather than shown as a bare error, because there is a decision here: either
      // it went on the wrong transaction and needs taking off that one, or it genuinely
      // covers both. Finance can do either from this row.
      if (e.status === 409 && e.data?.already_on) {
        setSharedAsk({ files: list, ...e.data.already_on, message: e.message });
      } else {
        // At the control that was used (a refused file type, most often), not the page flash.
        setUploadError(e.message || 'The receipt could not be uploaded.');
      }
    }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  // Which charge this refund reverses. Sent through the coding save because linking it
  // copies that charge's nominal code and treatment across, which is the whole point.
  const linkRefund = async (originalId) => {
    setBusy(true);
    try {
      const r = await api.put(`cot/transactions/${t.id}`, {
        refund_of_transaction_id: originalId, and_post: false,
      });
      if (r.error) throw new Error(r.error);
      const back = r.transaction?.vat_amount != null ? Math.abs(Number(r.transaction.vat_amount)) : null;
      // On the row, under the panel that was used, as well as at the top of the page.
      const text = originalId
        ? `Linked. The credit note carries that charge’s nominal code and treatment${
          back != null ? `, and gives back ${money(back)} of VAT` : ', and its VAT once that charge is coded'}.`
        : 'Unlinked. The coding it copied stays until you change it.';
      setLastResult({ kind: 'ok', text });
      onFlash('ok', text);
      if (r.transaction?.sage_ledger_account_id) setLedger(r.transaction.sage_ledger_account_id);
      if (r.transaction?.vat_treatment) setTreatment(r.transaction.vat_treatment);
      if (back != null) setVat(pence(back));
      await load();
      onChanged();
    } catch (e) {
      setLastResult({ kind: 'error', text: e.message || 'Could not link that.' });
      onFlash('error', e.message || 'Could not link that.');
    }
    finally { setBusy(false); }
  };

  // ── A TIP ADDED AT THE TERMINAL ─────────────────────────────────────────────
  //
  // Where the receipt totals less than the charge and the difference looks like a gratuity.
  // Recorded as a split, because that is exactly what it is: the meal with its VAT, the tip
  // with none. One press rather than four fields, since the figures are already known.
  const applyTip = async (tip) => {
    const receiptTotal = round2(Math.abs(Number(t.amount)) - Number(tip.amount));
    const receiptVat = round2(Number(detail?.receipts ? extract?.suggestion?.vat ?? 0 : 0));
    setBusy(true);
    try {
      const r = await api.put(`cot/transactions/${t.id}/lines`, {
        lines: [
          {
            description: cotLineName(t),
            sage_ledger_account_id: ledger || t.sage_ledger_account_id || null,
            net_amount: round2(receiptTotal - receiptVat), vat_amount: receiptVat, kind: 'cost',
          },
          {
            description: 'Gratuity',
            sage_ledger_account_id: ledger || t.sage_ledger_account_id || null,
            net_amount: Number(tip.amount), vat_amount: 0, kind: 'tip',
          },
        ],
      });
      if (r.error) throw new Error(r.error);
      onFlash('ok', `Split: ${money(receiptTotal)} for the bill and ${money(tip.amount)} as a gratuity, which carries no VAT.`);
      await load();
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not record the tip.'); }
    finally { setBusy(false); }
  };

  // The split. Saved through its own endpoint rather than folded into the coding save,
  // because it replaces the net and the VAT wholesale and the totals have to be checked
  // server-side against what the card was charged.
  const [splitOpen, setSplitOpen] = useState(false);
  const saveSplit = async (rows) => {
    setBusy(true);
    try {
      const r = await api.put(`cot/transactions/${t.id}/lines`, {
        lines: rows.map(x => ({
          description: x.description,
          sage_ledger_account_id: x.sage_ledger_account_id || null,
          net_amount: Number(x.net) || 0,
          vat_amount: Number(x.vat) || 0,
          kind: x.kind,
          vat_treatment: x.vat_treatment || null,
        })),
      });
      if (r.error) throw new Error(r.error);
      onFlash('ok', rows.length
        ? `Split into ${rows.length} lines. It will post to Sage as ${rows.length} lines.`
        : 'Split removed. It posts as one line again.');
      setSplitOpen(false);
      if (rows.length) { setVat(String(r.vat_amount ?? '')); }
      await load();
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not save the split.'); }
    finally { setBusy(false); }
  };

  // Taking a receipt off a transaction. There was no way to do this at all, which is why a
  // file on the wrong charge could not be put right by the person looking at it.
  const removeReceipt = async (r) => {
    if (!window.confirm(`Remove “${r.filename}” from this transaction?\n\nThe file is deleted. `
      + `Use this when a receipt has been attached to the wrong charge.`)) return;
    setBusy(true);
    try {
      const res = await api.delete(`cot/receipts/${r.id}`);
      if (res.error) throw new Error(res.error);
      onFlash('ok', 'Removed. This transaction is waiting on a receipt again.');
      await load();
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not remove that receipt.'); }
    finally { setBusy(false); }
  };

  // Read the VAT off the receipt. Cached hard: the same receipt is never sent to a model
  // twice, and neither is the same FILE, whichever transaction it arrived on. The result
  // is a suggestion — finance still decide, they just decide against a figure instead of
  // against a photograph of a till roll.
  const [extract, setExtract] = useState(null);
  const [viewing, setViewing] = useState(null);
  // Reading a photograph of a till roll takes ten seconds to a minute. A button that just
  // greys out for that long is indistinguishable from one that has hung — which is the
  // complaint the Sage ledger search earned, and it is not worth earning twice. So it says
  // what it is doing and counts.
  const [reading, setReading] = useState(null);
  const readReceipt = async (force = false) => {
    if (force && !window.confirm('Read this receipt again?\n\nThe first reading is kept, so this is only worth doing if it came back wrong. Reading again costs again.')) return;
    setBusy(true);
    const started = Date.now();
    setReading('Sending the receipt…');
    const tick = setInterval(() => {
      const s = Math.round((Date.now() - started) / 1000);
      setReading(s < 3 ? 'Sending the receipt…'
        : s < 12 ? `Reading it… ${s}s`
        : s < 30 ? `Still reading — a photograph takes longer than a PDF… ${s}s`
        : `Nearly there, this one is slow… ${s}s`);
    }, 1000);
    try {
      const r = await api.post(`cot/transactions/${t.id}/extract`, { force });
      if (r.error) throw new Error(r.error);
      setExtract(r);
      // Pre-filled, not applied: nothing is saved until the button below is pressed.
      // Only where the receipt actually gave a figure. Filling the box with a zero
      // the receipt never stated is how a reclaim silently becomes nil.
      if (r.suggestion?.vat != null && r.suggestion.vat_source !== 'absent') {
        setVat(String(r.suggestion.vat));
      }
      onFlash('ok', r.cached
        ? `From ${r.source} — nothing was charged for this reading.`
        : `Receipt read in ${Math.round((Date.now() - started) / 1000)}s.`);
    } catch (e) { onFlash('error', e.message || 'The receipt could not be read.'); }
    finally { clearInterval(tick); setReading(null); setBusy(false); }
  };

  // ── ONE ACTION ──────────────────────────────────────────────────────────────
  //
  // "Clicking save is confusing — does this just save or push to Sage? Always push to Sage
  // if the switch is on."
  //
  // So it does. Saving the coding is the last thing a transaction needs, so if that leaves
  // it ready and posting is on, it goes to Sage in the same click. The button says which of
  // the two will happen BEFORE it is pressed, which is the part that was missing: the
  // confusion was never about having two steps, it was about not being able to tell which
  // one you were getting.
  const save = async () => working(async () => {
    setLastResult(null);
    try {
      const r = await api.put(`cot/transactions/${t.id}`, {
        sage_ledger_account_id: ledger || null,
        // Under a treatment where nothing is reclaimable the VAT figure has no effect, so it
        // is sent as nil rather than left to look as though it might still be claimed.
        vat_amount: NO_RECLAIM.has(treatment) ? 0 : (vat === '' ? null : Number(vat)),
        vat_treatment: treatment || null,
        // Remembered against the merchant: the point of deciding per transaction is to
        // decide once. The treatment matters more than the code here — a nominal code is a
        // filing choice and a wrong treatment is a wrong VAT return.
        remember: true,
      });
      // Said twice on purpose: once at the top of the page, and once on this row.
      // The row is where the person is looking, and a message a thousand pixels
      // above the button they pressed is why this read as doing nothing at all.
      const say = (kind, text, extra = {}) => { setLastResult({ kind, text, ...extra }); onFlash(kind, text); };

      if (r.posted?.ok) {
        say(r.posted.warning ? 'warn' : 'ok',
          `Saved and posted to Sage${r.posted.attachments?.attached ? ' with the receipt' : ''}.`
          + (r.posted.warning ? ` ${r.posted.warning}` : ''));
      } else if (r.posted?.duplicate) {
        // ── THE ONE REFUSAL THAT IS A QUESTION ──
        //
        // Two charges really can be identical, and the person looking at the receipt
        // knows whether this is the second round or the same round counted twice. So
        // that one gets a way through, here next to the refusal — it used to live in a
        // browser confirm on a code path nothing called, so in practice a genuine second
        // purchase had no way through at all.
        //
        // An entry found under this transaction's OWN reference is not that question. It
        // is our own record of our own posting, and the answer is to link it rather than
        // to insist past it, so no override is offered.
        say('warn', `Saved, but not posted. ${r.posted.error}`,
          { override: r.posted.duplicate_kind === 'amount' ? 'duplicate' : null });
      } else if (r.posted?.cache_not_ready) {
        // The other question. Sage was never asked, so this is not "Sage would not take
        // it": our copy of the ledger could not rule a duplicate out, and the message ends
        // "confirm this one by hand". That needs a button here to do it with. A refund is
        // always in this position while the check is strict, and it used to get through
        // only because its own charge was mistaken for a duplicate and offered an override.
        say('warn', `Saved, but not posted. ${r.posted.error}`, { override: 'unconfirmed' });
      } else if (r.posted && !r.posted.ok) {
        // Saved, but Sage refused. Both facts, in that order.
        say('error', `Saved, but Sage would not take it: ${r.posted.error}`);
      } else if (willPost) {
        // The button promised a post and no post was attempted, which used to
        // report itself as a plain "Saved" and look like nothing had happened.
        let why = [];
        try { why = JSON.parse(r.transaction?.sage_blockers || '[]'); } catch { /* leave it */ }
        say('warn', `Saved, but it did not post.${why.length ? ` ${why.join(' ')}` : ''}`);
      } else {
        say('ok', 'Saved, and remembered for this merchant.');
      }
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not save that.'); }
  });

  // Claiming on a receipt that does not break its VAT out. Recorded as assumed so
  // the estimated reclaims can be found again, which is the question somebody
  // actually gets asked about them.
  const assumeVat = async (vatAmount, note) => {
    setBusy(true);
    try {
      const r = await api.put(`cot/transactions/${t.id}`, {
        vat_amount: vatAmount,
        vat_assumed: true,
        vat_note: note,
        vat_treatment: 'standard',
        remember: false,          // an assumption about one receipt, not this merchant's coding
        and_post: false,          // saved first, so the figure can be looked at before it goes
      });
      if (r.error) throw new Error(r.error);
      setVat(String(vatAmount));
      setLastResult({ kind: 'warn', text: `£${vatAmount.toFixed(2)} recorded as assumed VAT. ${note}` });
      onChanged();
    } catch (e) {
      setLastResult({ kind: 'error', text: e.message || 'That could not be saved.' });
    } finally { setBusy(false); }
  };

  // ── LINKING THE MERCHANT, HERE ──────────────────────────────────────────────
  //
  // The blocker said "finance links or creates it on the Capital on Tap merchants screen",
  // which named the obstacle and then sent you somewhere else to clear it. Both actions
  // are the merchants screen's own endpoints, so a link made here is the same link made
  // there: remembered against the merchant, applied to all its outstanding transactions,
  // and it stays linked for every transaction that follows.
  // ── CHASING ─────────────────────────────────────────────────────────────────
  // Only where there is something to chase: a receipt that has not arrived and has not
  // been written off. Once one is held, the history is a closed matter and stays out of
  // the way — but it is not deleted, and reopens with the transaction if the receipt goes.
  // Finance's two new controls: a drop target for a receipt they hold, and the no-receipt
  // decision, which is a small form rather than a prompt() because it now has a second
  // question in it.
  const [dropOpen, setDropOpen] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [waiveClaimVat, setWaiveClaimVat] = useState(false);
  // The escalated row's way out: the cardholder has said there is no receipt to get. A
  // question before it acts, because the button is only right once that has actually
  // been said, and the screen cannot know that on its own.
  const [confirmNoReceiptOpen, setConfirmNoReceiptOpen] = useState(false);
  // The same decision when the cardholder has said so on their own row: nothing to ask
  // about whether they have, so the panel shows what they said and the two answers.
  const [saidNoneOpen, setSaidNoneOpen] = useState(false);
  const firstName = String(t.person_name || '').trim().split(/\s+/)[0] || 'Cardholder';
  // The refusal that needs a decision rather than an error message: this file is already
  // the receipt for a different charge.
  const [sharedAsk, setSharedAsk] = useState(null);

  const reminders = detail?.reminders || [];
  const asks = Number(t.receipt_reminder_count || 0);
  const escalateAfter = detail?.escalate_after || null;
  // Never a refund: there is no receipt to chase for money coming back.
  const needsChasing = !isRefund && !t.receipt_count && !t.receipt_waived_reason && (isFinance || reminders.length > 0);

  const nudge = async () => {
    setBusy(true);
    try {
      const r = await api.post(`cot/transactions/${t.id}/nudge`, {});
      if (r.error) throw new Error(r.error);
      onFlash('ok', `Emailed ${r.person || r.to} — that is ask ${r.reminder_no}.`);
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'The reminder could not be sent.'); }
    finally { setBusy(false); }
  };

  const merchant = detail?.merchant || null;
  // Where the merchant panel above is offering the fix, the same sentence is dropped from
  // the blocker list rather than said twice in one view. Only where the fix is actually on
  // screen: for a cardholder, who gets no merchant panel, the blocker still reads in full.
  const showingMerchantFix = !!(merchant && !merchant.sage_contact_id && !t.sage_invoice_id);
  const shownBlockers = showingMerchantFix
    ? blockers.filter(b => !/not linked to a Sage contact/i.test(b))
    : blockers;

  const linkContact = async (contactId) => {
    if (!contactId || !merchant) return;
    setBusy(true);
    try {
      await api.put(`cot/merchants/${merchant.id}`, { sage_contact_id: contactId });
      onFlash('ok', 'Merchant linked, and its other outstanding transactions with it.');
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not link that.'); }
    finally { setBusy(false); }
  };
  // Creating a supplier in Sage is permanent, so it is confirmed and it checks for an
  // existing one first — a card statement is full of merchants nobody wants in the
  // supplier list for ever, and a duplicated supplier is somebody else's afternoon.
  const createContact = async (name) => {
    if (!merchant) return;
    const proposed = String(name || merchant.proposed_contact_name || merchant.display_name || '').trim();
    if (!proposed) return;
    if (!window.confirm(`Create “${proposed}” as a new supplier in Sage?\n\nIt will be in the supplier list permanently.`)) return;
    setBusy(true);
    const attempt = async (confirm) => api.post(`cot/merchants/${merchant.id}/create-contact`,
      { name: proposed, ...(confirm ? { confirm_not_existing: true } : {}) });
    try {
      const r = await attempt(false);
      onFlash('ok', `Created “${r.contact.name}” in Sage and linked it.`);
      await load(); onChanged();
    } catch (e) {
      // Sage already has something that looks like this. Dismissing it has to be a
      // deliberate answer, not a warning somebody clicks past.
      if (/already has/i.test(e.message || '') && window.confirm(`${e.message}\n\nCreate a separate one anyway?`)) {
        try {
          const r = await attempt(true);
          onFlash('ok', `Created “${r.contact.name}” in Sage.`);
          await load(); onChanged();
        } catch (e2) { onFlash('error', e2.message || 'Could not create it.'); }
      } else onFlash('error', e.message || 'Could not create it.');
    } finally { setBusy(false); }
  };

  // Adopt the entry Capital on Tap's feed created. Nothing is created in Sage; the link is
  // recorded and our receipt goes onto their entry if it has none.
  const linkSage = async (sageId) => {
    setBusy(true);
    try {
      const r = await api.post(`cot/transactions/${t.id}/link-sage`, { sage_invoice_id: sageId });
      if (r.error) throw new Error(r.error);
      onFlash('ok', r.attachments?.attached
        ? `Linked, and the receipt sent to Sage (${r.attachments.attached} page${r.attachments.attached === 1 ? '' : 's'}).`
        : r.sage_receipt?.present ? 'Linked. Sage already has a receipt against it.'
        : 'Linked. Sage has no receipt against it yet.');
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not link that.'); }
    finally { setBusy(false); }
  };

  const dismissMatch = async () => {
    setBusy(true);
    try {
      // Clearing the candidates is what lets it be posted: until somebody says none of
      // them are it, posting is refused, because Sage may already hold it.
      await api.put(`cot/transactions/${t.id}`, { clear_match_candidates: true });
      onFlash('ok', 'Noted. This one can be posted from here once the feed is off.');
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not save that.'); }
    finally { setBusy(false); }
  };

  const sendReceipt = async () => {
    setBusy(true);
    try {
      const r = await api.post(`cot/transactions/${t.id}/send-receipt`, {});
      if (r.error) throw new Error(r.error);
      onFlash('ok', `Receipt sent to Sage (${r.attached} page${r.attached === 1 ? '' : 's'}).`);
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not send the receipt.'); }
    finally { setBusy(false); }
  };

  // The override: there will not be a receipt, and here is why. A decision with a name
  // against it, which is the only thing that separates "the machine printed nothing" from
  // "we could not be bothered to ask".
  const waiveReceipt = async (reason, claimVat) => {
    if (!reason || reason.trim().length < 5) { onFlash('error', 'Say why there will be no receipt.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`cot/transactions/${t.id}/waive-receipt`, { reason: reason.trim(), claim_vat: !!claimVat });
      if (r.error) throw new Error(r.error);
      setWaiveOpen(false);
      onFlash('ok', claimVat
        ? 'Recorded. It can be posted with the VAT you have entered, and the Sage entry will say no receipt is held.'
        : 'Recorded. It can be posted now, with no VAT reclaimed.');
      await load();
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not record that.'); }
    finally { setBusy(false); }
  };

  // Confirmed no receipt: no typed reason, no VAT choice. The server writes the reason and
  // gives the VAT up, and the row moves on to Sage.
  const confirmNoReceipt = async () => {
    setBusy(true);
    try {
      const r = await api.post(`cot/transactions/${t.id}/waive-receipt`, { cardholder_confirmed: true });
      if (r.error) throw new Error(r.error);
      setConfirmNoReceiptOpen(false);
      onFlash('ok', 'Recorded. It can be posted to Sage now, with no VAT reclaimed.');
      await load();
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not record that.'); }
    finally { setBusy(false); }
  };

  // Not satisfied with what the cardholder said: the statement goes, finance's Action
  // closes, and the Monday chase asks them for it again.
  const askAgain = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`cot/transactions/${t.id}/no-receipt`);
      if (r.error) throw new Error(r.error);
      setSaidNoneOpen(false);
      onFlash('ok', `${firstName} will be asked for the receipt again in Monday's reminder.`);
      await load();
      onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not record that.'); }
    finally { setBusy(false); }
  };

  // Posting on its own, past a duplicate warning somebody has read and disagreed with.
  // The coding is already saved by the time this is reachable, so it is the post alone.
  const post = async (confirmDuplicate = false) => working(async () => {
    try {
      const r = await api.post(`cot/transactions/${t.id}/post`, { confirm_duplicate: confirmDuplicate });
      if (r.error) throw new Error(r.error);
      const text = `Posted to Sage${r.attachments?.attached ? ` with ${r.attachments.attached} receipt page(s)` : ''}.`
        + (r.warning ? ` ${r.warning}` : '');
      setLastResult({ kind: r.warning ? 'warn' : 'ok', text });
      onFlash(r.warning ? 'warn' : 'ok', text);
      onChanged();
    } catch (e) {
      setLastResult({ kind: 'error', text: e.message || 'The post failed.' });
      onFlash('error', e.message || 'The post failed.');
    }
  });

  // Where the ledger entry came from matters during the crossover: an entry Capital on
  // Tap's feed made is already in Sage and needs its receipt, not posting. Reading both as
  // a plain "In Sage" would hide the only thing left to do about it.
  const candidates = (() => { try { return JSON.parse(t.sage_match_candidates || '[]'); } catch { return []; } })();
  // ── AND WHETHER THE MERCHANT IS SQUARE ──
  //
  // In the ledger and unpaid is not "done": it is the merchant showing as owed money they
  // were handed at the till, which is the whole reason aged creditors was wrong. Said on the
  // row, because a green "In Sage" badge over an unpaid entry is what let the debt build up
  // unnoticed in the first place.
  const settled = !!t.sage_payment_id || !!t.sage_payment_type;
  const unpaid = !!t.sage_invoice_id && !settled;
  const state = t.sage_invoice_id
    ? (unpaid
      ? { label: isRefund ? 'In Sage · credit not yet refunded to the card' : 'In Sage · showing as owed to the merchant',
          cls: 'bg-amber-100 text-amber-900' }
      : t.sage_link_source === 'cot_feed'
        ? (t.sage_receipt_present === 0
            ? { label: 'In Sage (their feed) · no receipt there', cls: 'bg-amber-100 text-amber-900' }
            : t.sage_receipt_present === 1
              ? { label: 'In Sage (their feed) · paid, receipt attached', cls: 'bg-emerald-100 text-emerald-800' }
              : { label: 'In Sage (their feed) · paid', cls: 'bg-emerald-50 text-emerald-800' })
        : { label: isRefund ? 'In Sage · refunded to the card' : 'In Sage · paid from the card', cls: 'bg-emerald-100 text-emerald-800' })
    : t.sage_excluded_reason ? { label: 'Not going to Sage', cls: 'bg-gray-200 text-gray-700' }
    : candidates.length ? { label: `${candidates.length} possible match in Sage`, cls: 'bg-blue-100 text-blue-900' }
    : t.sage_ready ? { label: 'Ready to post', cls: 'bg-blue-100 text-blue-800' }
    : { label: 'Not ready', cls: 'bg-amber-100 text-amber-800' };

  return (
    <>
      <tr className={t.possible_repeat ? 'bg-amber-50/40' : ''}>
        <td className={`${T.meta} px-2 py-1 whitespace-nowrap text-gray-600`}>{fmtDay(t.clearance_date)}</td>
        <td className="px-2 py-1">
          <button onClick={decideOnly ? undefined : expand} disabled={decideOnly}
            className={`${T.body} font-semibold text-navy text-left ${decideOnly ? '' : 'hover:underline'}`}>
            {t.merchant_name || t.merchant_raw || t.description || '—'}
          </button>
          {isRefund && (
            <span className="ml-1 text-[8px] font-bold px-1 rounded bg-emerald-100 text-emerald-800"
              title="Money back from the merchant. It posts to Sage as a credit note against the charge it refunds.">
              refund
            </span>
          )}
          {!isRefund && Number(t.refunded_amount) > 0 && (
            <span className="ml-1 text-[8px] font-bold px-1 rounded bg-emerald-100 text-emerald-800">
              {refundStory(t)?.whole ? 'refunded' : 'part refunded'}
            </span>
          )}
          {t.possible_repeat === 1 && (
            <span className="ml-1 text-[8px] font-bold px-1 rounded bg-amber-200 text-amber-900"
              title="Same day, same card, same amount as another transaction. Capital on Tap gives no transaction id, so this could be a second purchase or a duplicated export row.">
              possible repeat
            </span>
          )}
          <RefundNote t={t} />
          {t.note && <div className={`${T.meta} text-gray-400 italic`}>{t.note}</div>}
        </td>
        <td className={`${T.meta} px-2 py-1 whitespace-nowrap`}>
          {t.person_name || <span className="text-red-600">card {t.card_ending} — unmapped</span>}
        </td>
        <td className={`${T.body} px-2 py-1 text-right font-semibold whitespace-nowrap`}>{money(t.amount)}</td>
        <td className={`${T.meta} px-2 py-1 text-right whitespace-nowrap`}>
          {t.vat_amount != null ? money(t.vat_amount)
            : <span className="text-gray-400">—</span>}
        </td>
        <td className="px-2 py-1 whitespace-nowrap">
          {/* A refund is evidenced by the charge it reverses, so there is nothing to upload
              and nobody to chase. */}
          {isRefund && !t.receipt_count
            ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                title="The receipt that matters is on the charge this refunds.">
                no receipt needed
              </span>
            : t.receipt_count > 0
            ? <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                {t.receipt_count} receipt{t.receipt_count === 1 ? '' : 's'}{t.receipt_source === 'ops_upload' ? ' (ops)' : ''}
              </span>
            : t.receipt_waived_reason
              ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" title={t.receipt_waived_reason}>no receipt — accepted</span>
              : <>
                  <label className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 cursor-pointer hover:bg-red-200">
                    {busy ? 'uploading…' : `upload — ${money(Number(t.amount || 0) / 6)} VAT at stake`}
                    <input ref={fileRef} type="file" className="hidden" accept={RECEIPT_ACCEPT} disabled={busy}
                      onChange={e => upload(e.target.files?.[0])} />
                  </label>
                  {/* A refusal from this control, beside it. Shown in the drop zone instead
                      when that is open, since that is then the control in use. */}
                  {uploadError && !dropOpen && (
                    <span role="alert" className="block mt-0.5 max-w-[220px] whitespace-normal text-[9px] font-semibold text-red-700">
                      {uploadError}
                    </span>
                  )}
                  {/* How hard this one has already been chased, without opening it. The
                      list otherwise reads the same for a receipt nobody has asked for and
                      one that has been asked for five times, and those need opposite
                      responses. */}
                  {/* ── THE CARDHOLDER HAS SAID THERE IS NONE ──
                      Said on their own row, with what happened to it, and finance have an
                      Action for it. Their answer is already on the screen, so the panel does
                      not ask whether they have said so: it shows what they said and offers
                      the two decisions, either of which closes the Action. */}
                  {t.receipt_none_said_at
                    ? <span className="block" onClick={e => e.stopPropagation()}>
                        <span className="text-[9px] font-semibold text-amber-800"
                          title={`${firstName} said so on ${String(t.receipt_none_said_at).slice(0, 10)}: ${t.receipt_none_note || ''}`}>
                          {firstName} says no receipt
                        </span>
                        {canDecide && !saidNoneOpen && (
                          <button onClick={() => setSaidNoneOpen(true)} disabled={busy}
                            className="block text-[9px] font-semibold text-amber-800 underline decoration-dotted hover:text-amber-900">
                            Decide →
                          </button>
                        )}
                        {canDecide && saidNoneOpen && (
                          <span className="block mt-1 w-60 rounded-md border border-amber-300 bg-white p-2 space-y-1.5 whitespace-normal">
                            <span className={`${T.meta} block text-gray-800`}>
                              {t.person_name || 'The cardholder'} says there is no receipt for this one:
                            </span>
                            <span className={`${T.meta} block italic text-gray-700`}>“{t.receipt_none_note}”</span>
                            <span className={`${T.meta} block text-gray-500`}>
                              Confirming posts it to Sage with no VAT reclaimed
                              {t.vat_amount != null && Number(t.vat_amount) > 0 ? ` (${money(t.vat_amount)} given up)` : ''}.
                              Asking again puts it back on their Monday reminder.
                            </span>
                            <span className="flex gap-1.5 flex-wrap">
                              <button onClick={confirmNoReceipt} disabled={busy}
                                className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
                                Confirm no receipt
                              </button>
                              <button onClick={askAgain} disabled={busy}
                                className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
                                Ask {firstName} to look again
                              </button>
                            </span>
                            <button onClick={() => setSaidNoneOpen(false)} disabled={busy}
                              className={`${T.meta} underline text-gray-500 hover:text-gray-700`}>
                              Decide later
                            </button>
                          </span>
                        )}
                      </span>
                    : t.receipt_escalated_at
                    ? <span className="block" onClick={e => e.stopPropagation()}>
                        <span className="text-[9px] font-semibold text-red-800" title={`Escalated ${String(t.receipt_escalated_at).slice(0, 10)}`}>
                          escalated
                        </span>
                        {/* The way off this list. Escalation is the last ask; what follows
                            is either a receipt or the cardholder saying there is none, and
                            that second answer had nowhere to go. Finance only, and a
                            question first, because the button is only right once the
                            cardholder has actually said so. */}
                        {isFinance && !confirmNoReceiptOpen && (
                          <button onClick={() => setConfirmNoReceiptOpen(true)} disabled={busy}
                            className="block text-[9px] font-semibold text-amber-800 underline decoration-dotted hover:text-amber-900">
                            Confirmed no receipt?
                          </button>
                        )}
                        {isFinance && confirmNoReceiptOpen && (
                          <span className="block mt-1 w-56 rounded-md border border-amber-300 bg-white p-2 space-y-1.5 whitespace-normal">
                            <span className={`${T.meta} block text-gray-800`}>
                              Has {t.person_name || 'the cardholder'} told you they cannot provide this receipt?
                            </span>
                            <span className={`${T.meta} block text-gray-500`}>
                              It posts to Sage with no VAT reclaimed
                              {t.vat_amount != null && Number(t.vat_amount) > 0 ? ` (${money(t.vat_amount)} given up)` : ''}.
                            </span>
                            <span className="flex gap-1.5">
                              <button onClick={confirmNoReceipt} disabled={busy}
                                className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
                                Yes, they confirmed
                              </button>
                              <button onClick={() => setConfirmNoReceiptOpen(false)} disabled={busy}
                                className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
                                Not yet
                              </button>
                            </span>
                          </span>
                        )}
                      </span>
                    : t.receipt_reminder_count > 0
                      ? <span className="block text-[9px] text-gray-500"
                          title={t.receipt_reminded_at ? `last asked ${String(t.receipt_reminded_at).slice(0, 10)}` : ''}>
                          asked {t.receipt_reminder_count}×
                        </span>
                      : <span className="block text-[9px] text-gray-400">not asked yet</span>}
                </>}
        </td>
        <td className="px-2 py-1 whitespace-nowrap">
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${state.cls}`}>{state.label}</span>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={7} className="px-3 py-2 bg-gray-50 border-y border-gray-200">
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <div className={`${T.label} text-gray-400 mb-1`}>What Capital on Tap sent</div>
                <div className="overflow-x-auto"><table className="w-full">
                  <tbody className={`${T.meta} text-gray-700`}>
                    {[
                      ['Merchant', t.merchant_raw],
                      ['Description', t.description],
                      ['Card', `${t.card_ending}${t.cardholder_name ? ` — ${t.cardholder_name}` : ''}`],
                      ['Authorised', fmtDay(t.authorisation_date)],
                      ['Cleared', fmtDay(t.clearance_date)],
                      ['Charged', t.original_currency && t.original_currency !== 'GBP'
                        ? `${t.original_amount} ${t.original_currency} → ${money(t.amount)}` : money(t.amount)],
                      ['Their category', t.cot_category],
                      ['Their receipt flag', t.cot_has_receipt ? 'Yes' : 'No'],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <tr key={k}><td className="pr-2 py-0.5 text-gray-400 whitespace-nowrap">{k}</td><td className="py-0.5">{v}</td></tr>
                    ))}
                  </tbody>
                </table></div>
              </div>

              <div>
                {isFinance && !t.sage_invoice_id && (
                  <>
                    <div className={`${T.label} text-gray-400 mb-1`}>Coding</div>

                    {/* ── THE MERCHANT'S SAGE CONTACT ──
                        Card spend posts as a purchase invoice against the merchant's own
                        contact, so an unlinked merchant cannot post at all. The decision is
                        made here, where the blocker is read, rather than on another screen.
                        Linking is remembered against the merchant: this is asked once. */}
                    {merchant && !merchant.sage_contact_id && (
                      <div className="mb-2 px-2 py-1.5 rounded border border-amber-300 bg-amber-50">
                        <div className={`${T.meta} font-semibold text-amber-900`}>
                          “{merchant.display_name}” is not linked to a Sage contact yet, so this cannot post.
                          {merchant.txn_count > 1 && ` Linking it fixes all ${merchant.txn_count} of its transactions.`}
                        </div>
                        {merchant.suggested_contact && (
                          <button onClick={() => linkContact(merchant.suggested_contact.id)} disabled={busy}
                            className={`${btn} mt-1 border-navy bg-navy text-white hover:bg-navy/90`}
                            title={merchant.suggested_reason || ''}>
                            Link to “{merchant.suggested_contact.name}”
                            {merchant.suggested_confidence && merchant.suggested_confidence !== 'none'
                              ? ` (${merchant.suggested_confidence} match)` : ''}
                          </button>
                        )}
                        <div className="mt-1">
                          <span className={`${T.label} text-amber-900 block mb-0.5`}>
                            {merchant.suggested_contact ? 'or search Sage for the right one' : 'search Sage for the contact'}
                          </span>
                          <SearchPicker
                            value="" options={contactOptions(contacts)} onChange={linkContact}
                            disabled={busy} allowClear={false} className="max-w-[22rem]"
                            placeholder="type a supplier name…"
                            onCreate={createContact}
                            createLabel={(q) => `Create “${q}” in Sage as a new supplier`}
                          />
                        </div>
                        <button onClick={() => createContact(merchant.proposed_contact_name)} disabled={busy}
                          className={`${T.meta} mt-1 underline font-semibold text-amber-900 hover:text-amber-950`}>
                          It’s new — create “{merchant.proposed_contact_name}” in Sage →
                        </button>
                      </div>
                    )}
                    {merchant?.sage_contact_id && (
                      <div className={`${T.meta} text-gray-500 mb-1`}>
                        Posts against <strong className="text-gray-700">{merchant.sage_contact_name}</strong> in Sage
                        {t.kind === 'refund' ? ' as a credit note' : ''}.
                      </div>
                    )}

                    {/* ── MONEY BACK FROM THE MERCHANT ──
                        A refund posts as a purchase credit note, and it has to carry the
                        nominal code and VAT treatment of the charge it reverses or it
                        corrects the total and leaves the VAT wrong. */}
                    {t.kind === 'refund' && (
                      <RefundLink t={t} detail={detail} busy={busy} onLink={linkRefund} />
                    )}

                    <div className="flex items-end gap-2 flex-wrap">
                      <label className="block">
                        <span className={`${T.label} text-gray-400 block`}>Nominal code</span>
                        {/* Searchable: a couple of hundred codes is not a list anybody
                            should scroll, and "fuel" is how somebody thinks of 7300. */}
                        <SearchPicker
                          value={ledger} options={ledgerOptions(ledgers)} onChange={setLedger}
                          disabled={busy} className="w-[16rem]"
                          placeholder="search the ledger — code or name…" emptyLabel="— no code —"
                        />
                      </label>
                      {/* ── THE VAT AND THE WHY, UNLESS THE LINES HAVE THEM ──
                          Once a charge is split, every line carries its own treatment and
                          its own figures, and these two boxes answer for the whole charge.
                          They were shown greyed out, which put a "Standard 20%" next to
                          lines that were nothing of the sort and invited somebody to reach
                          for the wrong one. Gone entirely while a split exists; the totals
                          are stated under the lines instead. */}
                      {!isSplit && (
                        <>
                          <label className="block">
                            <span className={`${T.label} text-gray-400 block`}>VAT</span>
                            {/* Read-only once a treatment says nothing is reclaimable,
                                because then the figure has no effect. */}
                            <input value={vat} onChange={e => setVat(e.target.value)}
                              onBlur={e => setVat(pence(e.target.value))} placeholder="0.00"
                              disabled={NO_RECLAIM.has(treatment)}
                              title={NO_RECLAIM.has(treatment) ? 'Nothing is reclaimed under this treatment' : ''}
                              className={`${sel} w-24 ${NO_RECLAIM.has(treatment) ? 'bg-gray-100 text-gray-500' : ''}`}
                              inputMode="decimal" />
                          </label>
                          {/* ── AND WHY ──
                              The question the card path could not answer. £0 of reclaimable
                              VAT means five different things on a return, and this is the
                              only place the difference is recorded. */}
                          <label className="block">
                            <span className={`${T.label} text-gray-400 block`}>Treatment</span>
                            <select value={treatment} onChange={e => setTreatment(e.target.value)}
                              className={`${sel} w-[13rem]`} disabled={busy}
                              title={(treatments.find(x => x.id === treatment) || {}).hint || ''}>
                              <option value="">— from the figures —</option>
                              {treatments.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
                            </select>
                          </label>
                        </>
                      )}
                      {isSplit && (
                        <div className={`${T.meta} text-gray-500 self-end pb-1`}>
                          {money(t.net_amount)} net + {money(t.vat_amount)} VAT, from the {t.line_count} lines below.
                        </div>
                      )}
                      {t.receipt_count > 0 && (
                        <button onClick={() => readReceipt(false)} disabled={busy}
                          className={`${btn} border-gray-300 text-gray-700 bg-white hover:bg-gray-50`}
                          title="Read the net and VAT off the receipt. Kept, so the same receipt is never read twice.">
                          {reading || '✨ Read the receipt'}
                        </button>
                      )}
                      {/* For the charge that is more than one thing. Out of the way, because
                          almost none of them are — but named, because when it is needed
                          there is no other way to record it. */}
                      {isFinance && !t.sage_invoice_id && (
                        <button onClick={() => setSplitOpen(o => !o)} disabled={busy}
                          className={`${btn} ${t.line_count > 0
                            ? 'border-navy bg-navy/5 text-navy' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                          title="For an import charge, or anything where one charge is several things at different VAT treatments">
                          {t.line_count > 0 ? `Split into ${t.line_count} ▾` : 'Split…'}
                        </button>
                      )}
                      {/* The one action, and it says which of the two things it will do. */}
                      <button onClick={save} disabled={busy}
                        className={`${btn} ${willPost
                          ? 'border-navy bg-navy text-white hover:bg-navy/90'
                          : 'border-navy text-navy bg-white hover:bg-navy/5'}`}
                        title={willPost
                          ? 'Saves the coding and posts it to Sage with the receipt attached'
                          : 'Saves the coding. It cannot post yet — see below for what is missing'}>
                        {busy
                          ? (workingSince ? `Working… ${workedFor}s` : 'Working…')
                          : willPost ? 'Save and post to Sage →' : 'Save the coding'}
                      </button>
                    </div>

                    {/* ── WHAT IT IS DOING, WHILE IT DOES IT ──
                        A post is four round trips to Sage: the duplicate check, the entry
                        itself, one call per receipt page, and the payment off the card
                        account. On a slow day that is fifteen seconds of a button saying
                        "Working…", which reads as stuck — and the response to stuck is to
                        press again, which is the one thing that must not happen here.

                        Steps listed rather than a bar: nothing on this side knows which of
                        the four Sage is on, and a bar moving on a timer would be a lie. The
                        seconds are real, and after twenty of them the sentence that matters
                        is the one about not pressing again. */}
                    {busy && workingSince && (
                      <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-gray-200 bg-white text-gray-600`}>
                        <span className="inline-block animate-pulse">●</span>{' '}
                        {workedFor}s — checking Sage for an entry it may already have, posting this one
                        {t.receipt_count > 0 ? `, attaching ${t.receipt_count} receipt page${t.receipt_count === 1 ? '' : 's'}` : ''},
                        and paying it off the card account.
                        {workedFor >= 20 && (
                          <span className="block mt-0.5 font-semibold text-amber-900">
                            Sage is being slow. Leave it: if it times out the entry may still have been created,
                            and ops looks for it before it would ever post a second one.
                          </span>
                        )}
                      </div>
                    )}

                    {/* ── NIL VAT, BEFORE IT IS SAVED ──
                        Said under the boxes rather than only as a blocker afterwards. The
                        Capital on Tap export carries no VAT figure, so everything arrives
                        at nil and stays there unless somebody decides otherwise, and a nil
                        nobody decided is real money not reclaimed. The figure is what makes
                        the point: "no VAT" is abstract, "£7.54" is not. */}
                    {nilVatUndecided && !t.sage_invoice_id && (
                      <div className={`${T.meta} mt-2 px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
                        <span className="font-semibold">No VAT on this yet, so up to {money(vatAtStake)} would not be reclaimed.</span>
                        {' '}Enter it off the receipt, or pick the treatment that says why there is none: zero rated,
                        exempt, reverse charge, supplier not VAT registered, not recoverable, or outside the scope.
                        {t.receipt_count > 0 ? ' Reading the receipt fills both in.' : ''}
                      </div>
                    )}

                    {/* What that click did, right under the button that did it. */}
                    {lastResult && (
                      <div className={`${T.body} mt-2 px-2 py-1.5 rounded border ${
                        lastResult.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                          : lastResult.kind === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-900'
                          : 'bg-red-50 border-red-200 text-red-800'}`}>
                        {lastResult.text}
                        {/* The manual push. Only for the duplicate question, which is the
                            only refusal a person can legitimately overrule from here —
                            everything else Sage says no to is a fault to fix, not a
                            judgement to make. Named for what it asserts rather than
                            "confirm", so nobody presses it to make a message go away. */}
                        {lastResult.override && !t.sage_invoice_id && (
                          <button onClick={() => post(true)} disabled={busy}
                            className={`${btn} mt-1.5 block border-navy bg-white text-navy hover:bg-navy/5`}
                            title={lastResult.override === 'duplicate'
                              ? 'Posts it as a separate entry. The override is recorded on the transaction.'
                              : 'Posts it without the duplicate check. That is recorded on the transaction.'}>
                            {busy ? 'Working…'
                              : lastResult.override === 'duplicate' ? 'It is a different charge — post it anyway'
                              : isRefund ? 'This credit is not in Sage: post it' : 'This is not in Sage: post it'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* An assumed figure never passes for a read one. */}
                    {!!t.vat_assumed && !t.sage_invoice_id && (
                      <div className={`${T.meta} mt-2 px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
                        <span className="font-semibold">The VAT on this one was assumed, not read off the receipt.</span>
                        {t.vat_note ? ` ${t.vat_note}` : ''}
                      </div>
                    )}

                    {/* And the last refusal, which outlives the message and the
                        reload. Without this a failed post left no trace at all,
                        so the row looked untouched and the only thing to do was
                        press the button again. */}
                    {!lastResult && t.sage_post_error && !t.sage_invoice_id && (
                      <div className={`${T.body} mt-2 px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-800`}>
                        <span className="font-semibold">Sage refused this the last time it was tried
                        {t.sage_post_attempted_at ? ` (${String(t.sage_post_attempted_at).slice(0, 16).replace('T', ' ')})` : ''}:</span>{' '}
                        {t.sage_post_error}
                      </div>
                    )}

                    {/* What the chosen treatment means, under the picker rather than in a
                        tooltip: these are the decisions people get wrong, and a hover is not
                        where an explanation belongs. */}
                    {treatment && (
                      <div className={`${T.meta} mt-1 px-2 py-1 rounded ${
                        NO_RECLAIM.has(treatment) ? 'bg-amber-50 text-amber-900' : 'bg-gray-50 text-gray-600'}`}>
                        {(treatments.find(x => x.id === treatment) || {}).hint}
                        {treatment === 'reverse_charge' && settings && !settings.reverse_charge_rate && (
                          <strong className="block mt-0.5 text-red-800">
                            Sage has no reverse charge tax rate synced, so this cannot post yet. Switch the rate on in
                            Sage, then press Refresh from Sage on the Sage screen.
                          </strong>
                        )}
                        {treatment === 'blocked' && (
                          <span className="block mt-0.5">
                            Worth a note saying who was entertained — that is what HMRC ask about.
                          </span>
                        )}
                      </div>
                    )}

                    {splitOpen && (
                      <SplitEditor t={t} ledgers={ledgers} existing={detail?.lines} busy={busy}
                        treatments={treatments}
                        onSave={saveSplit} onCancel={() => setSplitOpen(false)} />
                    )}

                    {/* How it will post, once it has been split. Shown without opening the
                        editor: somebody checking this transaction months later needs to see
                        that the duty was not reclaimed, not take it on trust. */}
                    {!splitOpen && detail?.lines?.length > 0 && (
                      <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden">
                        <div className={`${T.label} text-gray-400 px-2 py-1 bg-gray-50 border-b border-gray-200`}>
                          Posts as {detail.lines.length} lines
                        </div>
                        <div className="overflow-x-auto"><table className="w-full">
                          <tbody>
                            {detail.lines.map(l => (
                              <tr key={l.id} className="border-b border-gray-100 last:border-0">
                                <td className={`${T.body} px-2 py-1 text-gray-800`}>
                                  {l.description}
                                  {l.kind === 'duty' && (
                                    <span className={`${T.meta} text-gray-500`}> · duty, not reclaimed</span>
                                  )}
                                  {l.kind === 'import_vat' && (
                                    <span className={`${T.meta} text-gray-500`}> · import VAT, reclaimed</span>
                                  )}
                                </td>
                                <td className={`${T.meta} px-2 py-1 text-gray-500`}>
                                  {l.ledger_code ? `${l.ledger_code} ${l.ledger_name || ''}` : 'no code'}
                                </td>
                                <td className={`${T.body} px-2 py-1 text-right text-gray-700 whitespace-nowrap`}>
                                  {money(l.net_amount)} net
                                </td>
                                <td className={`${T.body} px-2 py-1 text-right text-gray-700 whitespace-nowrap`}>
                                  {Number(l.vat_amount) ? `${money(l.vat_amount)} VAT` : 'no VAT'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table></div>
                      </div>
                    )}

                    {/* What the receipt says, against what the card was charged. The
                        reconciliation is the useful part: a receipt that does not total
                        the charge is a receipt for something else, and the VAT on it is
                        not ours to reclaim here. */}
                    {extract?.suggestion && (() => {
                      // ── WHAT IT SAID, NOT WHAT WE ASSUMED ──
                      //
                      // "No VAT info is given, so don't just say something that's
                      // completely incorrect — you're literally costing me money."
                      //
                      // Two faults made that sentence. money(null) renders as
                      // £0.00, so a receipt that said nothing about VAT and one
                      // that stated nil read identically. And the tick sat at the
                      // end of a sentence asserting all three figures, so "the
                      // total matches" looked like "the VAT is verified". The
                      // tick belongs to the total alone: it is the only one of
                      // the three that was checked against anything.
                      const sg = extract.suggestion;
                      const noVat = sg.vat_source === 'absent' || sg.vat == null;
                      const derived = sg.vat_source === 'derived';
                      const fig = (n) => (n == null ? 'not stated' : money(n));
                      return (
                      <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border ${
                        noVat ? 'border-amber-400 bg-amber-50'
                          : sg.total_agrees ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
                        <div className="font-semibold text-gray-800">
                          The receipt says: net {fig(sg.net)} · VAT {noVat ? 'not stated' : fig(sg.vat)}
                          {derived && <span className="text-amber-800"> (worked out from its net and total, not read off it)</span>}
                          {sg.receipt_total != null && (
                            <> · total {money(sg.receipt_total)}
                              {sg.total_agrees && <span className="text-emerald-700"> ✓ the total matches the charge</span>}
                            </>
                          )}
                        </div>
                        {noVat && (
                          <div className="mt-1 space-y-1.5">
                            <div className="text-amber-900 font-semibold">
                              Nothing has been filled in, because this receipt does not give a VAT figure. That is not
                              the same as there being none to reclaim.
                            </div>
                            {/* ── CLAIMING ON IT ANYWAY ──
                                "I want the ability to reclaim anything, so if not clear, give
                                the ability to assume 20%."
                                
                                Their call to take, so it is offered rather than refused. But
                                the whole-receipt version over-claims the moment anything on it
                                was zero rated, which on a supermarket shop is most of the food,
                                so the part-of-it version is offered first and is the accurate
                                one. Either way the figure is recorded as assumed, not read. */}
                            <AssumeVat gross={Math.abs(Number(t.amount) || 0)} busy={busy} onAssume={assumeVat} />
                          </div>
                        )}
                        {extract.suggestion.supplier_vat_number && (
                          <div className="text-gray-600">VAT number {extract.suggestion.supplier_vat_number}</div>
                        )}
                        {(extract.warnings || []).map((w, i) => (
                          <div key={i} className="text-amber-900 mt-0.5">· {w}</div>
                        ))}
                        {/* ── THE TIP, IN ONE PRESS ──
                            The commonest reason a restaurant receipt totals less than the
                            charge. A gratuity is outside the scope of VAT, so it has to come
                            out of the figure the VAT is worked out on — otherwise the reclaim
                            is over by a fifth of the tip, every time. The figures are already
                            known, so this is a button rather than four fields. */}
                        {extract.suggestion.tip && !t.line_count && (
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <button onClick={() => applyTip(extract.suggestion.tip)} disabled={busy}
                              className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
                              Record {money(extract.suggestion.tip.amount)} as a gratuity
                            </button>
                            <span className={`${T.meta} text-gray-600`}>
                              Splits the charge: the bill with its VAT, the tip with none.
                            </span>
                          </div>
                        )}
                        {/* What the receipt itself says the treatment is. A suggestion, in one
                            press, because reading it off the paper is the slow part. */}
                        {extract.suggestion.vat_treatment
                          && extract.suggestion.vat_treatment !== treatment && (
                          <div className="mt-1">
                            <button onClick={() => setTreatment(extract.suggestion.vat_treatment)} disabled={busy}
                              className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
                              The receipt looks like{' '}
                              {(treatments.find(x => x.id === extract.suggestion.vat_treatment) || {}).label
                                || extract.suggestion.vat_treatment} — use that
                            </button>
                          </div>
                        )}
                        <div className="text-gray-400 mt-0.5">
                          {extract.cached ? `From ${extract.source} — no charge for this reading.` : 'Read just now.'}
                          {' '}
                          <button onClick={() => readReceipt(true)} className="underline hover:text-gray-700">read it again</button>
                        </div>
                      </div>
                      );
                    })()}
                    <p className={`${T.meta} text-gray-500 mt-1`}>
                      The nominal code is remembered against this merchant and applied to its other transactions
                      that nobody has coded, so this is decided once.
                      {merchant?.learned_at && merchant.default_ledger_code
                        ? ` Last time “${merchant.display_name}” went to ${merchant.default_ledger_code} · ${merchant.default_ledger_name}.`
                        : ''}
                      {' '}VAT cannot be reclaimed without the receipt, and the net is worked out from the amount charged.
                    </p>
                  </>
                )}

                {/* ── THE CROSSOVER ──
                    Sage already has something that could be this, because Capital on
                    Tap's feed put it there. Linking is the action; posting is not offered
                    at all, which is what makes dual running safe rather than careful. */}
                {candidates.length > 0 && !t.sage_invoice_id && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-blue-300 bg-blue-50`}>
                    <div className="font-semibold text-blue-900">
                      Capital on Tap’s feed may already have put this in Sage
                    </div>
                    {candidates.map(c => (
                      <div key={c.id} className="flex items-center justify-between gap-2 mt-1 bg-white rounded border border-blue-100 px-2 py-1">
                        <span className="text-gray-700">
                          <strong>{c.reference || c.supplier_reference || c.id}</strong>
                          {c.contact_name ? ` · ${c.contact_name}` : ''} · {c.date} · {money(c.total_amount)}
                          <span className="text-gray-400">
                            {' '}({c.confidence.replace(/_/g, ' ')}
                            {c.days_apart ? `, ${c.days_apart}d apart` : ', same day'}
                            {c.same_contact ? ', same supplier contact' : ''})
                          </span>
                        </span>
                        <button onClick={() => linkSage(c.id)} disabled={busy}
                          className={`${btn} border-navy bg-navy text-white hover:bg-navy/90 flex-shrink-0`}>
                          This is it — link
                        </button>
                      </div>
                    ))}
                    <button onClick={dismissMatch} disabled={busy}
                      className="mt-1 underline text-blue-800 hover:text-blue-900">
                      None of these are it
                    </button>
                  </div>
                )}

                {/* Linked to the feed's entry, and Sage has no receipt against it: the
                    reclaim sitting unclaimable behind an entry that looks complete. */}
                {t.sage_invoice_id && t.sage_link_source === 'cot_feed' && t.sage_receipt_present === 0 && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
                    Sage has this transaction but no receipt against it.
                    {t.receipt_count > 0
                      ? <> <button onClick={sendReceipt} disabled={busy} className="underline font-semibold">Send the receipt we hold →</button></>
                      : ' We do not have one either, so it is on the chase list.'}
                  </div>
                )}

                {/* A reading from an earlier visit, shown without asking for another. */}
                {!extract && t.ai_suggested_vat != null && !t.sage_invoice_id && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-gray-200 bg-white text-gray-700`}>
                    Read from the receipt earlier: net {money(t.ai_suggested_net)} · VAT {money(t.ai_suggested_vat)}
                    {t.ai_suggestion_note ? <span className="block text-amber-800">· {t.ai_suggestion_note}</span> : null}
                  </div>
                )}

                {shownBlockers.length > 0 && !t.sage_invoice_id && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
                    <div className="font-semibold">Not ready to post:</div>
                    <ul className="ml-3 list-disc">{shownBlockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                    {/* The way out, next to the obstacle. Offered only where it applies, and
                        only to finance. */}
                    {/* ── FINANCE HOLD THE RECEIPT THEMSELVES ──
                        "In case the finance person has the receipt." Often they do: it came
                        by email, or the cardholder handed over a paper one. Making them chase
                        somebody for a file already on their desk was the wrong shape. */}
                    {isFinance && !t.receipt_count && !isRefund && (
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <button onClick={() => { setDropOpen(o => !o); setWaiveOpen(false); }} disabled={busy}
                          className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
                          {dropOpen ? 'Close' : '⬆ I have the receipt'}
                        </button>
                        {!t.receipt_waived_reason && (
                          <button onClick={() => { setWaiveOpen(o => !o); setDropOpen(false); }} disabled={busy}
                            className={`${btn} border-amber-400 bg-white text-amber-900 hover:bg-amber-50`}>
                            No receipt available →
                          </button>
                        )}
                      </div>
                    )}
                    {isFinance && dropOpen && !t.receipt_count && !sharedAsk && (
                      <div className="mt-1.5">
                        <ReceiptDrop busy={busy} onFiles={upload} label="Add the receipt you hold" error={uploadError} />
                      </div>
                    )}

                    {/* ── THIS FILE IS ALREADY SOMEBODY ELSE'S RECEIPT ──
                        Refused rather than attached, because one document standing as the
                        evidence for two charges is a VAT reclaim made twice. Almost always
                        it means the file went onto the wrong transaction earlier, so the
                        first thing offered is where to go and fix that. */}
                    {sharedAsk && (
                      <div className="mt-1.5 rounded-lg border border-red-300 bg-white p-2.5 space-y-2">
                        <div className={`${T.body} text-red-900 font-semibold`}>{sharedAsk.message}</div>
                        <div className={`${T.meta} text-gray-600`}>
                          Already held against <strong>{sharedAsk.merchant || 'another transaction'}</strong>
                          {sharedAsk.clearance_date ? ` dated ${fmtDay(sharedAsk.clearance_date)}` : ''}
                          {sharedAsk.amount != null ? ` for ${money(sharedAsk.amount)}` : ''}.
                          If that is the wrong one, open it and remove the receipt there first.
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => upload(sharedAsk.files, { confirmShared: true })} disabled={busy}
                            className={`${btn} border-amber-400 bg-white text-amber-900 hover:bg-amber-50`}>
                            One invoice covers both — attach it anyway
                          </button>
                          <button onClick={() => setSharedAsk(null)} disabled={busy}
                            className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
                            Cancel
                          </button>
                        </div>
                        {uploadError && <div role="alert" className={`${T.meta} text-red-700 font-semibold`}>{uploadError}</div>}
                      </div>
                    )}

                    {/* ── NO RECEIPT, AND WHAT THAT MEANS FOR THE VAT ──
                        "Give the option for finance to say receipt not available and proceed
                        with whatever is typed for VAT reclaim." Their call to make, so it is
                        offered plainly — with what each choice means said next to it, and the
                        reason recorded either way. */}
                    {isFinance && waiveOpen && !t.receipt_count && (
                      <div className="mt-1.5 rounded-lg border border-amber-300 bg-white p-2.5 space-y-2">
                        <div>
                          <label className={`${T.label} text-gray-500 block mb-0.5`}>Why is there no receipt?</label>
                          <input value={waiveReason} onChange={e => setWaiveReason(e.target.value)}
                            placeholder="Parking machine printed nothing · supplier never issued one · lost"
                            className={`${T.body} w-full border border-gray-300 rounded-md px-2 py-1.5`} />
                        </div>
                        <div className="space-y-1">
                          {[[false, 'Post the cost, reclaim no VAT',
                             'The safe answer. The cost lands in the ledger and no VAT is claimed against it.'],
                            [true, `Post it and reclaim the ${t.vat_amount != null ? money(t.vat_amount) : 'VAT'} entered`,
                             'Only where you hold other evidence — a statement line, the supplier’s record, an invoice still to arrive. '
                             + 'The Sage entry will say no receipt is held, and your name is on the decision.']].map(([val, label, note]) => (
                            <label key={String(val)} className={`flex gap-2 items-start cursor-pointer rounded-md border px-2 py-1.5 ${
                              waiveClaimVat === val ? 'border-navy bg-navy/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                              <input type="radio" className="mt-0.5" checked={waiveClaimVat === val}
                                onChange={() => setWaiveClaimVat(val)} />
                              <span>
                                <span className={`${T.body} font-semibold text-gray-800 block`}>{label}</span>
                                <span className={`${T.meta} text-gray-500`}>{note}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => waiveReceipt(waiveReason, waiveClaimVat)}
                            disabled={busy || waiveReason.trim().length < 5}
                            className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
                            Record it
                          </button>
                          <button onClick={() => setWaiveOpen(false)} disabled={busy}
                            className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {t.receipt_waived_reason && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border ${t.receipt_waived_vat_ok
                    ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                    No receipt, accepted: {t.receipt_waived_reason}
                    {t.receipt_waived_vat_ok
                      ? <span className="block font-semibold mt-0.5">
                          VAT is being reclaimed with no receipt held. The Sage entry says so too.
                        </span>
                      : <span className="block mt-0.5">No VAT reclaimed.</span>}
                  </div>
                )}
                {t.sage_push_error && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-800`}>
                    Sage refused the last attempt: {t.sage_push_error}
                  </div>
                )}

                {/* ── IN THE LEDGER AND NOT PAID ──
                    Nothing is wrong with the transaction: the other half of the entry is
                    missing, and until it is there the merchant is on aged creditors for
                    money that left the business at the till. The reason Sage gave sits next
                    to the button that retries it, rather than in a message that has gone. */}
                {unpaid && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
                    <div className="font-semibold">
                      This is in Sage but nothing has paid it, so the merchant is showing as owed {money(Math.abs(t.amount))}.
                    </div>
                    {t.sage_payment_error
                      ? <div className="mt-0.5">Sage refused the payment: {t.sage_payment_error}</div>
                      : <div className="mt-0.5">
                          A card charge is paid from the Capital on Tap account on the day it cleared. This one has not
                          been, usually because the account had not been chosen on the Settings tab when it posted.
                        </div>}
                    {isFinance && (
                      <button className={`${btn} bg-white text-amber-900 border-amber-300 mt-1`} disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await api.post(`cot/transactions/${t.id}/settle`, {});
                            onFlash('ok', 'Paid from the card account.');
                            await onChanged();
                          } catch (e) { onFlash('error', e.message || 'Could not pay that.'); }
                          finally { setBusy(false); }
                        }}>
                        Pay it from the card account
                      </button>
                    )}
                  </div>
                )}

                {/* Which direct debit collected it. More than one is not a fault: Capital on
                    Tap collect a balance, and a balance can stop halfway through a charge. */}
                {!!detail?.direct_debits?.length && (
                  <div className={`${T.meta} mt-2 text-gray-600`}>
                    Collected by direct debit{detail.direct_debits.length > 1 ? 's' : ''}{' '}
                    {detail.direct_debits.map((d, i) => (
                      <span key={d.settlement_id}>
                        {i > 0 ? ', ' : ''}{fmtDay(d.settled_on)} ({money(d.amount)}
                        {round2(d.amount) !== round2(Math.abs(t.amount)) ? ' of it' : ''})
                      </span>
                    ))}.
                  </div>
                )}

                {/* ── CHASING THIS RECEIPT ──
                    What has actually been sent, and to whom. The counter could only say
                    how many times; this says when, by which route, and who pressed the
                    button — which is what somebody needs before deciding whether to chase
                    again, chase harder, or accept there is no receipt to get. */}
                {needsChasing && (
                  <div className={`${T.meta} mt-2 px-2 py-1.5 rounded border ${
                    t.receipt_escalated_at ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
                    <div className="font-semibold text-gray-800">
                      {asks === 0 ? 'Nobody has been asked for this yet.'
                        : `${asks === 1 ? 'Asked once' : `Asked ${asks} times`}${t.cardholder_name ? ` — ${t.cardholder_name}` : ''}.`}
                    </div>
                    {reminders.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {reminders.map(r => (
                          <li key={r.id} className="text-gray-600">
                            <span className="text-gray-400">{String(r.sent_at || '').slice(0, 16).replace('T', ' ')}</span>
                            {' · '}
                            {r.kind === 'weekly'
                              ? <>the Monday reminder{r.transactions_in_email > 1 ? `, one of ${r.transactions_in_email} in it` : ''}</>
                              : r.kind === 'manual'
                                ? <><strong>nudged by {r.sent_by_name || 'finance'}</strong> about this one</>
                                : <><strong className="text-red-800">escalated</strong> to admin and finance</>}
                            {r.sent_to ? <span className="text-gray-400"> → {r.sent_to}</span> : null}
                            {r.note ? <span className="block text-gray-500 ml-3">“{r.note}”</span> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* What happens next, said before it happens. */}
                    {t.receipt_escalated_at ? (
                      <div className="mt-1 text-red-800">
                        Escalated on {String(t.receipt_escalated_at).slice(0, 10)}. No further automatic email will be
                        sent about it — it needs the receipt, or a decision that there is not one.
                      </div>
                    ) : escalateAfter && asks >= escalateAfter - 1 ? (
                      <div className="mt-1 text-amber-800">
                        One more unanswered ask and this escalates to admin and finance.
                      </div>
                    ) : null}
                    {isFinance && (
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <button onClick={nudge} disabled={busy || !t.person_id}
                          className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}
                          title={t.person_id
                            ? 'Emails the cardholder about this one transaction, from you, as the finance team'
                            : 'This card is not mapped to anybody, so there is nobody to remind'}>
                          {busy ? 'Working…' : '✉ Nudge the cardholder'}
                        </button>
                        {!t.person_id && (
                          <span className="text-amber-800">
                            This card is not mapped to anybody, so no reminder can reach a person. Map it on the Cards tab.
                          </span>
                        )}
                        {t.receipt_reminded_at && (
                          <span className="text-gray-400">
                            last asked {String(t.receipt_reminded_at).slice(0, 10)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* The receipts themselves. A card receipt is a photograph, so it is shown
                rather than listed: nobody can check a VAT figure against a filename. */}
            {detail?.receipts?.length > 0 && (
              <div className="mt-2">
                <div className={`${T.label} text-gray-400 mb-1`}>Receipts</div>
                <div className="flex gap-2 flex-wrap">
                  {detail.receipts.map((r, i) => (
                    <div key={r.id} className="border border-gray-200 rounded overflow-hidden bg-white">
                      <button onClick={() => setViewing(i)} className="block hover:opacity-90 text-left w-full">
                        {/^image\//.test(r.content_type || '')
                          ? <img src={`/api/cot/receipts/${r.id}/download`} alt={r.filename}
                              className="h-28 w-auto object-contain" />
                          : <div className={`${T.meta} px-3 py-8 text-gray-600`}>📄 {r.filename}</div>}
                      </button>
                      {/* The same bytes on another charge. Nearly always an upload that
                          landed on the wrong row, and impossible to spot before this said so. */}
                      {r.also_on_count > 0 && (
                        <div className={`${T.meta} px-1 py-1 bg-red-50 text-red-900 border-t border-red-200`}>
                          Also the receipt for {r.also_on || 'another transaction'}. One receipt cannot
                          evidence two charges — remove it from whichever is wrong.
                        </div>
                      )}
                      <div className={`${T.meta} px-1 py-0.5 text-gray-500 border-t border-gray-100 flex items-center justify-between gap-2`}>
                        <span>
                          {r.page_count ? `page ${r.page_no} of ${r.page_count}` : (r.source === 'ops_upload' ? 'uploaded here' : 'from Capital on Tap')}
                        </span>
                        {/* Attached to the wrong charge, and until now unfixable from here:
                            whoever could see the mistake had no way to undo it. Finance only,
                            and refused once the entry is in Sage. */}
                        {isFinance && !t.sage_invoice_id && (
                          <button onClick={() => removeReceipt(r)} disabled={busy}
                            title="Take this receipt off this transaction"
                            className="text-red-700 hover:text-red-900 underline">remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewing != null && detail?.receipts?.length > 0 && (
              <ReceiptModal transaction={t} receipts={detail.receipts} index={viewing}
                onIndex={setViewing} onClose={() => setViewing(null)} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── MY COMPANY CARD ──────────────────────────────────────────────────────────
//
// Not the finance table with columns hidden. A different job needs a different screen:
// finance are coding and posting, a cardholder has exactly one thing to do, and everything
// else on the page is there to tell them they are up to date.
//
// So: what to do first, then the overview, then the history. The finance columns — who
// holds the card, the VAT, the Sage state — are absent, because none of them are this
// person's business or their responsibility.
// ── ASSUMING THE VAT ─────────────────────────────────────────────────────────
//
// For the receipt that does not break its VAT out. Two ways, because they give
// different answers and only one of them is right for a supermarket shop:
//
//   ALL of it standard rated   VAT = gross / 6      (20/120)
//   PART of it standard rated  VAT = that part / 6
//
// A Tesco receipt with sushi on it is mostly zero-rated food, so claiming a
// fifth of the whole thing over-claims — and over-claiming is the direction
// HMRC take an interest in. So the partial version leads, and the total is
// there for the case where everything genuinely was standard rated.
/* ── ASSUMING THE RATE ──
   "Assume 20%, but allow a change — that way it's at the finance person's
   response and investigation."

   So the whole charge at 20% is the starting position and the claim is one
   press away. The standard-rated amount is a field rather than a fixed figure,
   because the person who knows whether half that supermarket shop was food is
   the one looking at the paper, not this screen. Lower it and the VAT follows.

   The figure is marked assumed either way, so a quarter's estimated reclaims
   are a query rather than an archaeology exercise. */
function AssumeVat({ gross, busy, onAssume }) {
  // Pre-filled with the whole charge: assume 20% unless somebody says otherwise.
  const [part, setPart] = useState(gross > 0 ? gross.toFixed(2) : '');
  const partNum = Number(part);
  const valid = part !== '' && Number.isFinite(partNum) && partNum > 0 && partNum <= gross + 0.005;
  const vatOf = (g) => Math.round((g / 6) * 100) / 100;
  const whole = valid && Math.abs(partNum - gross) < 0.005;

  return (
    <div className="rounded border border-amber-300 bg-white px-2 py-1.5">
      <div className={`${T.meta} font-semibold text-gray-800 mb-1`}>
        Claim on it anyway, by assuming 20%
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <label className="block">
          <span className={`${T.label} text-gray-400 block`}>Standard rated amount</span>
          <input value={part} onChange={e => setPart(e.target.value)} inputMode="decimal" placeholder="0.00"
            disabled={busy} className={`${sel} w-28`} />
        </label>
        <button
          onClick={() => onAssume(vatOf(partNum), whole
            ? `The whole £${gross.toFixed(2)} treated as standard rated at 20%. The receipt gives no VAT breakdown, so this figure is assumed, not read.`
            : `£${partNum.toFixed(2)} of the £${gross.toFixed(2)} treated as standard rated at 20%. The receipt gives no VAT breakdown, so this figure is assumed, not read.`)}
          disabled={busy || !valid}
          className={`${btn} border-navy bg-navy text-white hover:bg-navy/90 disabled:opacity-40`}>
          Assume {valid ? `£${vatOf(partNum).toFixed(2)}` : 'VAT'}
        </button>
        {valid && !whole && (
          <button onClick={() => setPart(gross.toFixed(2))} disabled={busy}
            className={`${btn} border-gray-300 text-gray-600 bg-white hover:bg-gray-50`}>
            Back to the whole £{gross.toFixed(2)}
          </button>
        )}
      </div>
      <div className={`${T.meta} text-gray-500 mt-1`}>
        Starts at the whole £{gross.toFixed(2)}, which is £{vatOf(gross).toFixed(2)} of VAT. Lower it if part of the
        receipt was zero rated: most food, books and children's clothes carry none, so claiming on the whole total
        over-claims by a sixth of that part. The figure is recorded as assumed rather than read either way.
      </div>
    </div>
  );
}

function MyCardPanel({ data, onChanged, onFlash, search = '', setSearch = null }) {
  const s = data?.summary || {};
  const all = data?.transactions || [];
  const cards = data?.cards || [];
  const [showAll, setShowAll] = useState(false);
  // ── A RECORD, NOT A LISTING ──────────────────────────────────────────────────
  //
  // "Not sure listing all the transactions will work as there could be thousands."
  //
  // Right. This block rendered every settled transaction the moment it was expanded, and
  // the only thing keeping that survivable was that nobody has a year of card spend in it
  // yet. So the record is now a count and a total, and rows arrive a page at a time — the
  // summary above is the way in, and this is what you get when you follow it.
  const PAGE = 25;
  const [shown, setShown] = useState(PAGE);
  useEffect(() => { setShown(PAGE); }, [search, data?.transactions]);
  // ── A ROW DEALT WITH STAYS WHERE IT WAS ──────────────────────────────────────
  //
  // Receipting a transaction moves it out of this block and into the collapsed panel
  // below, so the row vanished the instant the upload worked and the rows beneath it slid
  // up. That is how the same file came to be uploaded twice: the second attempt landed on
  // whichever transaction had taken the first one's place.
  //
  // Anything settled during this visit therefore stays in the list, in its original
  // position, showing that it is done. It leaves on the next page load, by which time the
  // person has read the confirmation and is not aiming at it any more.
  const [settledNow, setSettledNow] = useState(() => new Set());
  const markSettled = useCallback((id) => {
    setSettledNow(prev => prev.has(id) ? prev : new Set(prev).add(id));
  }, []);
  // Saying there is no receipt takes a row off this list as surely as uploading one: there
  // is nothing more the cardholder can do, and it is finance's to decide.
  // A refund never is: there is no receipt for money coming back.
  const isOutstanding = (t) => t.kind !== 'refund' && !t.receipt_count && !t.receipt_waived_reason && !t.receipt_none_said_at;
  const purchases = all.filter(t => t.kind !== 'refund').length;
  const refundsHeld = all.length - purchases;
  const outstanding = all.filter(t => isOutstanding(t) || settledNow.has(t.id));
  const settled = all.filter(t => !isOutstanding(t) && !settledNow.has(t.id));
  const stillToDo = all.filter(isOutstanding).length;

  return (
    <div className="space-y-3">
      {/* ── A DRILL-DOWN IS A FILTER OVER THE WHOLE PAGE ───────────────────────
          The search runs on the server, so everything below it is the matching subset —
          including the receipts-to-upload block. That is fine as long as it is SAID: a
          to-do list quietly showing a fraction of itself is how somebody concludes they
          are up to date. The tiles at the top of the screen are counted separately and
          stay true, so the two only appear to disagree while this banner is up. */}
      {!!search && (
        <div className={`${T.body} px-3 py-2 rounded border border-navy/30 bg-navy/5 text-navy flex items-center gap-2 flex-wrap`}>
          <span>
            Showing only what matches <strong>“{search}”</strong>. Everything on this page, including anything still
            needing a receipt, is filtered to it.
          </span>
          {setSearch && (
            <button onClick={() => setSearch('')}
              className="px-2 py-1 text-[10px] font-semibold rounded bg-navy text-white hover:bg-navy/90">
              Show everything
            </button>
          )}
        </div>
      )}

      {/* ── WHAT TO DO ─────────────────────────────────────────────────────────
          First, and the only thing on the page that asks anything of anybody. */}
      {outstanding.length > 0 ? (
        <div className={`bg-white rounded-lg border overflow-hidden ${
          stillToDo ? 'border-red-300' : 'border-emerald-300'}`}>
          <div className={`px-3 py-2 border-b ${
            stillToDo ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className={`${T.strong} ${stillToDo ? 'text-red-900' : 'text-emerald-900'}`}>
              {stillToDo
                ? `${stillToDo} receipt${stillToDo === 1 ? '' : 's'} to upload`
                : 'That is all of them — nothing left to upload.'}
            </div>
            {/* ── WHICH ROUTE, AND WHY IT MATTERS ──
                This used to send people to the Capital on Tap app as "the quickest way".
                Quickest for the person, and the slowest possible route for the receipt: a
                receipt in Capital on Tap only reaches Sage when finance download it from
                there and upload it again by hand, which happens when the next export is
                pulled — so it can sit for a week or more before anything can be filed.
                Uploading here goes straight onto the transaction and on to Sage with it.
                Said plainly, because nobody chooses the slower route on purpose. */}
            {stillToDo ? (
              <div className={`${T.meta} text-red-800 mt-0.5`}>
                Without a receipt we cannot reclaim the VAT — about {money(s.awaiting_upload_vat_at_risk ?? s.vat_at_risk)} on these — and the transaction
                cannot be filed.{' '}
                <strong>Upload here rather than in the Capital on Tap app.</strong>{' '}
                A receipt added here goes straight onto the transaction and into Sage with it. One added in the Capital on
                Tap app has to be downloaded and re-uploaded by finance by hand, which only happens when the next
                statement is pulled, so it can hold things up for a week or more. A photo, a screenshot or a PDF all work.
              </div>
            ) : (
              <div className={`${T.meta} text-emerald-800 mt-0.5`}>
                Finance take it from here. The ones you have just done stay listed below so you can see they landed.
              </div>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {/* Paged for the same reason as the record below: a person handed a card in
                January and asked for receipts in August has a to-do list nobody wants
                rendered in one go. */}
            {outstanding.slice(0, shown).map(t => (
              <MyCardRow key={t.id} t={t} onChanged={onChanged} onFlash={onFlash} onSettled={markSettled} />
            ))}
          </div>
          {outstanding.length > shown && (
            <button onClick={() => setShown(n => n + PAGE)}
              className={`${T.meta} w-full px-3 py-2 border-t border-gray-100 text-navy hover:bg-gray-50 font-semibold`}>
              Show {Math.min(PAGE, outstanding.length - shown)} more of {outstanding.length}
            </button>
          )}
        </div>
      ) : (
        <div className={`${T.body} px-3 py-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-900`}>
          <strong>Nothing to do.</strong>{' '}
          {purchases
            ? <>All {purchases} of your card purchases have a receipt against them
                {s.said_no_receipt ? `, apart from ${s.said_no_receipt} you have told finance there is no receipt for` : ''}
                {s.latest_date ? `, up to ${fmtDay(s.latest_date)}` : ''}. Finance take it from here.</>
            : <>Nothing on your card has been imported yet.</>}
        </div>
      )}

      {/* ── THE OVERVIEW ───────────────────────────────────────────────────────
          Enough to know the picture is complete, which is the other half of "am I up to
          date?" — a clear list is only reassuring if you can see it covers everything. */}
      {cards.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200">
            <span className={`${T.label} text-gray-500`}>Your card{cards.length === 1 ? '' : 's'}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {cards.map(c => (
              <div key={c.card_ending} className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
                <span className={`${T.body} font-mono font-semibold text-gray-800`}>•••• {c.card_ending}</span>
                <span className={`${T.meta} text-gray-500`}>
                  {c.txns} transaction{c.txns === 1 ? '' : 's'} held
                  {c.latest ? ` · latest ${fmtDay(c.latest)}` : ''}
                  {' · '}
                  {c.missing
                    ? <span className="text-red-700 font-semibold">{c.missing} without a receipt</span>
                    : <span className="text-emerald-700 font-semibold">all receipted</span>}
                </span>
              </div>
            ))}
          </div>
          <p className={`${T.meta} text-gray-400 px-3 py-1.5 border-t border-gray-100`}>
            Finance import the statement from Capital on Tap every week or so, so a purchase from the last day or two
            may not be here yet. Total spend held: {money(s.total_spend)}.
          </p>
        </div>
      )}

      {/* ── WHAT YOU HAVE SPENT, AND WHAT IS MISSING ───────────────────────────
          Below the thing to do, above the record of what is done. Both halves of why it is
          here point the same way: the VAT figure is what gets a receipt uploaded, and seeing
          six months of your own spend at one merchant is what gets it reconsidered. */}
      <SpendSummaryPanel title="Your spend over time"
        onDrill={setSearch ? (term) => { setSearch(term); setShowAll(true); } : null} />

      {/* ── THE REST ───────────────────────────────────────────────────────────
          Collapsed, because it is a record rather than a task, and paged once open. The
          way in is the summary above: clicking a nominal code or a merchant searches for
          it, which happens on the server, so it reaches transactions this page never
          loaded. */}
      {(settled.length > 0 || search) && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button onClick={() => setShowAll(v => !v)}
            className="w-full px-3 py-2 border-b border-gray-200 flex items-center justify-between text-left gap-2">
            <span className={`${T.label} text-gray-500`}>
              {search
                ? `${settled.length} receipted, matching “${search}”`
                : refundsHeld
                  ? `${settled.length - refundsHeld} already receipted, and ${refundsHeld} refund${refundsHeld === 1 ? '' : 's'}`
                  : `${settled.length} already receipted`}
              {settled.length > 0 && (
                <span className="text-gray-400 font-normal">
                  {/* What was spent on the receipted ones. Netting the refunds in here would
                      take them off charges that may be in the list above, and go negative. */}
                  {' · '}{money(settled.filter(t => t.kind !== 'refund').reduce((n, t) => n + Number(t.amount || 0), 0))}
                </span>
              )}
            </span>
            <span className={`${T.meta} text-gray-400`}>{showAll ? 'hide' : 'show'}</span>
          </button>
          {showAll && (
            <>
              {setSearch && (
                <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search merchant, nominal code, date or amount"
                    className="flex-1 min-w-[14rem] border border-gray-200 rounded px-2 py-1 text-[11px]" />
                  {!!search && (
                    <button onClick={() => setSearch('')}
                      className="px-2 py-1 text-[10px] font-semibold rounded bg-gray-100 text-gray-600 hover:bg-gray-200">
                      Clear
                    </button>
                  )}
                  {/* The cap, admitted here too: a search that quietly stopped at 500 would
                      be a drill-down that under-reports somebody's own spend. */}
                  {data?.matched != null && data.matched !== data.returned && (
                    <span className={`${T.meta} text-amber-800`}>
                      showing the most recent {data.returned} of {data.matched} — narrow the search
                    </span>
                  )}
                </div>
              )}
              <div className="divide-y divide-gray-100">
                {settled.slice(0, shown).map(t => (
                  <MyCardRow key={t.id} t={t} onChanged={onChanged} onFlash={onFlash} />
                ))}
                {!settled.length && (
                  <div className={`${T.meta} text-gray-500 px-3 py-2`}>
                    Nothing receipted matches that.
                  </div>
                )}
              </div>
              {settled.length > shown && (
                <button onClick={() => setShown(n => n + PAGE)}
                  className={`${T.meta} w-full px-3 py-2 border-t border-gray-100 text-navy hover:bg-gray-50 font-semibold`}>
                  Show {Math.min(PAGE, settled.length - shown)} more of {settled.length}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// One of a person's own transactions: what it was, and the one thing they can do about it.
function MyCardRow({ t, onChanged, onFlash, onSettled }) {
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const fileRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);
  // ── SAYING SO, HERE, AFTER THE UPLOAD ────────────────────────────────────────
  //
  // A successful upload used to remove this row from the list: it stopped being
  // outstanding, so it moved into the collapsed "already receipted" panel, which is shut.
  // From where the person was standing the row simply disappeared and nothing took its
  // place — indistinguishable from the upload having been thrown away.
  //
  // What follows from that is worse than the confusion. The rows below shift up into the
  // space, so a second attempt at "the same" row is a second attempt at a different
  // transaction, and that is how one invoice came to be the receipt for two charges.
  //
  // So the row stays exactly where it is and says what happened, and the person decides
  // when to move on.
  const [justAdded, setJustAdded] = useState(null);

  // A list, not a file: a receipt is often two photographs, and dropping both should not
  // quietly upload one of them.
  const upload = async (files) => {
    const list = Array.isArray(files) ? files : [files].filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    setUploadError(null);
    let added = 0, dupes = 0;
    try {
      for (const file of list) {
        const r = await api.post(`cot/transactions/${t.id}/receipt`, {
          filename: file.name,
          content_type: file.type || contentTypeFor(file.name),
          data: toBase64(await file.arrayBuffer()),
        });
        if (r.error) throw new Error(r.error);
        if (r.duplicate) dupes++; else added++;
      }
      const msg = added
        ? `Receipt added${dupes ? ` (${dupes} of those was already held)` : ''} — thank you, that is it dealt with.`
        : 'That receipt is already against this one, so nothing has changed.';
      setJustAdded(msg);
      setDropOpen(false);
      onFlash(added ? 'ok' : 'warn', msg);
      // The row keeps its place until the person leaves the screen, so the list is not
      // rearranged under whoever is working down it.
      onSettled?.(t.id);
      onChanged();
    } catch (e) {
      // The one refusal worth spelling out rather than showing raw: this file is already
      // the receipt for a different charge. Whoever sees it needs to know which.
      // Said in the drop zone, which stays open on a failure: that is where the person is
      // looking, and the flash at the top of the page is not.
      const other = e.data?.already_on;
      setUploadError(other
        ? `${e.message} It is on ${other.merchant || 'another transaction'} dated ${fmtDay(other.clearance_date)}.`
        : (e.message || 'The receipt could not be uploaded.'));
    }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  // Open once the person wants to attach something, rather than a permanent drop target on
  // every row — a list of eight transactions each with a dashed box is unreadable.
  const [dropOpen, setDropOpen] = useState(false);

  // ── THERE IS NO RECEIPT ──────────────────────────────────────────────────────
  //
  // The answer only the cardholder has. Until now they could upload or say nothing, and
  // saying nothing meant three Monday emails and an escalation before finance could act
  // on a receipt that was never coming. Said here, with what happened to it, it goes to
  // finance as an Action and the reminders stop. Finance still decide: it is the
  // cardholder's word, and the VAT goes with the receipt.
  const [noneOpen, setNoneOpen] = useState(false);
  const [noneNote, setNoneNote] = useState('');
  const [noneError, setNoneError] = useState(null);
  const sayNoReceipt = async () => {
    setBusy(true);
    setNoneError(null);
    try {
      const r = await api.post(`cot/transactions/${t.id}/no-receipt`, { note: noneNote.trim() });
      if (r.error) throw new Error(r.error);
      setNoneOpen(false);
      setJustAdded(r.finance_told
        ? 'Finance have been told there is no receipt. You will not be asked for this one again unless they need you to look.'
        : 'Recorded. Finance will see it on Company Cards, and you will not be asked for this one again unless they need you to look.');
      onSettled?.(t.id);
      onChanged();
    } catch (e) {
      // Said in the panel, which stays open: that is where the person is looking.
      setNoneError(e.message || 'Could not tell finance. Try again.');
    }
    finally { setBusy(false); }
  };

  const view = async () => {
    try {
      const d = await api.get(`cot/transactions/${t.id}`);
      setReceipts(d?.receipts || []);
      setViewing(0);
    } catch { onFlash('error', 'Could not open that receipt.'); }
  };

  const hasReceipt = t.receipt_count > 0;
  return (
    <div className={`px-3 py-2 flex items-center justify-between gap-3 flex-wrap${
      justAdded ? ' bg-emerald-50' : ''}`}>
      <span>
        <span className={`${T.body} font-semibold text-gray-800`}>
          {t.merchant_name || t.merchant_raw || t.description || 'Card transaction'}
        </span>
        <span className={`${T.meta} text-gray-500 block`}>
          {fmtDay(t.clearance_date)} · {money(t.amount)}
          {t.original_currency && t.original_currency !== 'GBP' ? ` · charged ${t.original_amount} ${t.original_currency}` : ''}
          {t.note ? ` · “${t.note}”` : ''}
        </span>
        <RefundNote t={t} />
        {/* ── WHERE IT LANDED ──
            The nominal code the charge was coded to, on the cardholder's own row. It was
            only ever visible to finance, so the person who made the purchase could not see
            what their spend had been booked as, and a miscoding was only ever caught by
            the one person who did not make the purchase. Read only: the code is finance's
            decision, and seeing it is what makes it checkable. */}
        <span className={`${T.meta} block`}>
          {t.ledger_code || t.ledger_name ? (
            <span className="text-gray-500">
              coded to <strong className="text-gray-700">{[t.ledger_code, t.ledger_name].filter(Boolean).join(' · ')}</strong>
              {t.sage_invoice_id ? ' · in Sage' : ''}
            </span>
          ) : (
            <span className="text-gray-400">not coded yet · finance decide the nominal code</span>
          )}
        </span>
      </span>
      <span className="flex items-center gap-2 flex-shrink-0">
        {t.kind === 'refund' ? (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800"
            title="Money back on your card. There is nothing to upload for a refund.">
            refund · nothing to do
          </span>
        ) : hasReceipt ? (
          <>
            <button onClick={view} className={`${T.meta} underline text-navy hover:text-gold`}>
              see the receipt
            </button>
            {/* One purchase can have two receipts (an order confirmation and a delivery note,
                or a second page found later), so being done does not close the door. */}
            <button type="button" onClick={() => setDropOpen(o => !o)} disabled={busy}
              className={`${T.meta} underline text-navy hover:text-gold`}>
              {busy ? 'Uploading…' : dropOpen ? 'close' : 'add another receipt'}
            </button>
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
              done
            </span>
          </>
        ) : t.receipt_waived_reason ? (
          <span className={`${T.meta} text-gray-500 italic`} title={t.receipt_waived_reason}>
            no receipt needed
          </span>
        ) : t.receipt_none_said_at ? (
          <>
            {/* Found it after all: uploading still works, and closes finance's Action. */}
            <button type="button" onClick={() => setDropOpen(o => !o)} disabled={busy}
              className={`${T.meta} underline text-navy hover:text-gold`}>
              {busy ? 'Uploading…' : dropOpen ? 'close' : 'found it? upload'}
            </button>
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
              title={t.receipt_none_note || ''}>
              no receipt · with finance
            </span>
          </>
        ) : (
          <>
            <button type="button" onClick={() => { setNoneOpen(o => !o); setDropOpen(false); }} disabled={busy}
              className={`${T.meta} underline text-gray-600 hover:text-navy`}>
              {noneOpen ? 'close' : 'I don’t have a receipt'}
            </button>
            <button type="button" onClick={() => { setDropOpen(o => !o); setNoneOpen(false); }} disabled={busy}
              className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
              {busy ? 'Uploading…' : dropOpen ? 'Close' : 'Upload the receipt'}
            </button>
          </>
        )}
      </span>
      {/* What just happened, on the row it happened to. The flash at the top of the page is
          not enough on its own: the person is looking at this line, not at the header, and
          the whole confusion was an upload that left no mark where they were looking. */}
      {justAdded && (
        <div className={`${T.meta} w-full mt-1 px-2 py-1 rounded bg-emerald-100 text-emerald-900 font-semibold`}>
          ✓ {justAdded}
        </div>
      )}
      {noneOpen && !hasReceipt && (
        <div className="w-full mt-1 rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1.5">
          <div className={`${T.body} font-semibold text-amber-900`}>No receipt for this one?</div>
          <div className={`${T.meta} text-amber-900`}>
            Tell finance what happened to it, for example the machine did not print one or it was lost. They decide
            what happens next, and the Monday reminders stop for this one. Without a receipt the VAT usually cannot be
            reclaimed, so if the supplier can send you a copy, upload that instead.
          </div>
          <textarea value={noneNote} onChange={e => setNoneNote(e.target.value)} rows={2} disabled={busy}
            placeholder="What happened to the receipt?"
            className="w-full border border-gray-200 rounded px-2 py-1 text-[11px] bg-white" />
          {noneError && (
            <div role="alert" className={`${T.meta} font-semibold text-red-700`}>{noneError}</div>
          )}
          <div className="flex gap-1.5 flex-wrap">
            <button type="button" onClick={sayNoReceipt} disabled={busy || noneNote.trim().length < 5}
              className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
              {busy ? 'Telling finance…' : 'Tell finance there is no receipt'}
            </button>
            <button type="button" onClick={() => setNoneOpen(false)} disabled={busy}
              className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
              I will keep looking
            </button>
          </div>
        </div>
      )}
      {/* The drop zone, on its own line so it has room to be a target rather than a
          button-sized sliver. Only on the row somebody is actually dealing with. */}
      {dropOpen && (
        <div className="w-full mt-1">
          <ReceiptDrop busy={busy} onFiles={upload} error={uploadError}
            label={`Add ${hasReceipt ? 'another' : 'the'} receipt for ${t.merchant_name || t.merchant_raw || 'this transaction'}`} />
        </div>
      )}
      {viewing != null && receipts?.length > 0 && (
        <ReceiptModal transaction={t} receipts={receipts} index={viewing}
          onIndex={setViewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

// ── Merchants ────────────────────────────────────────────────────────────────
//
// Finance only, and said on the screen, because it is the mapping that decides which
// company the spend sits against in the ledger.
function MerchantsPanel({ ledgers, contacts, onFlash, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setRows((await api.get('cot/merchants'))?.merchants || []); } catch { /* quiet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const link = async (m, contactId) => {
    setBusy(true);
    try {
      await api.put(`cot/merchants/${m.id}`, { sage_contact_id: contactId });
      onFlash('ok', 'Linked, and its transactions with it.');
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not link that.'); }
    finally { setBusy(false); }
  };
  const setLedger = async (m, ledgerId) => {
    setBusy(true);
    try {
      await api.put(`cot/merchants/${m.id}`, { default_ledger_account_id: ledgerId });
      onFlash('ok', 'Default set, and applied to anything not yet coded.');
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not set that.'); }
    finally { setBusy(false); }
  };
  // The name can come from the search box — typing a supplier Sage does not have and
  // creating it from there is the same decision, made in one place instead of two.
  const create = async (m, typedName = null) => {
    const name = typedName
      || window.prompt(
        'Create this merchant as a new supplier in Sage.\n\nIt will exist in the supplier list permanently, so check the name:',
        m.proposed_contact_name || m.display_name);
    if (!name) return;
    if (typedName && !window.confirm(`Create “${typedName}” as a new supplier in Sage?\n\nIt will be in the supplier list permanently.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`cot/merchants/${m.id}/create-contact`, { name });
      onFlash('ok', `Created "${r.contact.name}" in Sage and linked it.`);
      await load(); onChanged();
    } catch (e) {
      if (/already has/i.test(e.message || '')
          && window.confirm(`${e.message}\n\nCreate a separate one anyway?`)) {
        try {
          const r = await api.post(`cot/merchants/${m.id}/create-contact`, { name, confirm_not_existing: true });
          onFlash('ok', `Created "${r.contact.name}" in Sage.`);
          await load(); onChanged();
        } catch (e2) { onFlash('error', e2.message || 'Could not create it.'); }
      } else onFlash('error', e.message || 'Could not create it.');
    } finally { setBusy(false); }
  };
  const autoLink = async () => {
    setBusy(true);
    try {
      const r = await api.post('cot/merchants/auto-link', {});
      onFlash('ok', `${r.linked} linked automatically, ${r.suggested} need a decision.`);
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'That failed.'); }
    finally { setBusy(false); }
  };

  // ── THE CODING SAGE ALREADY HOLDS ──
  //
  // "Could you grab the ledger from that transaction if it's already been processed?"
  //
  // Somebody has been coding Capital on Tap's feed entries in Sage by hand for months. This
  // reads that work back rather than asking finance to repeat all of it from an empty screen.
  const learnCoding = async () => {
    setBusy(true);
    try {
      const r = await api.post('cot/learn-coding', {});
      onFlash(r.learned ? 'ok' : 'info',
        r.learned
          ? `Took a nominal code from Sage for ${r.learned} merchant${r.learned === 1 ? '' : 's'}.`
            + `${r.inconclusive ? ` ${r.inconclusive} are coded more than one way in Sage, so those still need you.` : ''}`
          : 'Nothing to take: no merchant with a Sage contact and no code of its own had a clear pattern in Sage.');
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'That failed.'); }
    finally { setBusy(false); }
  };

  const unlinked = rows.filter(r => !r.sage_contact_id);
  const uncoded = rows.filter(r => r.sage_contact_id && !r.default_ledger_account_id);
  // Filtered here rather than at the server: this list arrives whole, so there is nothing
  // behind it for a browser-side filter to miss.
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? rows.filter(m => [m.display_name, m.merchant_key, m.sage_contact_name, m.proposed_contact_name,
                        m.default_ledger_code, m.default_ledger_name]
        .some(v => String(v || '').toLowerCase().includes(needle)))
    : rows;

  // Unlinked merchants show as "no contact", which sorts after every linked one.
  const merchantSort = useSort(shown, {
    merchant: m => m.display_name,
    seen: m => Number(m.spend) || 0,
    outstanding: m => Number(m.outstanding) || 0,
    contact: m => (m.sage_contact_id ? (m.sage_contact_name || m.sage_contact_id) : null),
    nominal: m => {
      const l = ledgers.find(x => x.id === m.default_ledger_account_id);
      return l ? `${l.nominal_code || ''} ${l.name || ''}`.trim() : null;
    },
  }, null, { seen: 'desc', outstanding: 'desc' });
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Merchants and their Sage contacts</span>
        <div className="flex gap-1">
          <button onClick={autoLink} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
            Link the obvious ones
          </button>
          {uncoded.length > 0 && (
            <button onClick={learnCoding} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}
              title="Reads the nominal code these contacts' invoices already carry in Sage and takes it as the default.">
              Take the coding from Sage ({uncoded.length})
            </button>
          )}
        </div>
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        Card spend posts as a purchase invoice against the merchant's own Sage contact, so every merchant needs one.
        An exact name match is linked automatically; anything less is suggested here and waits for you, because a
        wrong link puts spend against the wrong company. Creating a contact is permanent, so it is never done
        automatically — a card statement is full of merchants nobody wants in the supplier list for ever.
        {unlinked.length > 0 && <strong> {unlinked.length} still need a contact.</strong>}
      </p>
      <div className="px-3 pb-2 flex items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search merchant, Sage contact or nominal code"
          className="flex-1 border border-gray-200 rounded px-2 py-1 text-[11px]" />
        <span className={`${T.meta} text-gray-500`}>
          {needle ? `${shown.length} of ${rows.length}` : `${rows.length} merchant${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <div className="overflow-x-auto max-h-[36rem]">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>{[['merchant', 'Merchant'], ['seen', 'Seen'], ['outstanding', 'Outstanding'],
                  ['contact', 'Sage contact'], ['nominal', 'Default nominal code']].map(([k, h]) => (
              <SortTh key={k} col={k} label={h} sort={merchantSort.sort} onSort={merchantSort.toggle}
                className={`${T.label} text-gray-400 px-3 py-1`}
                title={k === 'seen' ? 'Sort by spend' : undefined} />
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {merchantSort.sorted.map(m => (
              <tr key={m.id} className={m.sage_contact_id ? '' : 'bg-amber-50/40'}>
                <td className="px-3 py-1">
                  <div className={`${T.body} font-semibold text-gray-800`}>{m.display_name}</div>
                  {m.proposed_contact_name && m.proposed_contact_name !== m.display_name && (
                    <div className={`${T.meta} text-gray-400`}>would be created as “{m.proposed_contact_name}”</div>
                  )}
                </td>
                <td className={`${T.meta} px-3 py-1 text-gray-500 whitespace-nowrap`}>{m.txn_count}× · {money(m.spend)}</td>
                <td className={`${T.meta} px-3 py-1 ${m.outstanding ? 'font-semibold text-amber-800' : 'text-gray-400'}`}>{m.outstanding || 0}</td>
                <td className="px-3 py-1">
                  {m.sage_contact_id
                    ? <span className={`${T.body} text-emerald-800`}>✓ {m.sage_contact_name || m.sage_contact_id}</span>
                    : (
                      <div className="flex items-center gap-1 flex-wrap">
                        {m.suggested_contact && (
                          <button onClick={() => link(m, m.suggested_contact.id)} disabled={busy}
                            className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}
                            title={m.suggested_reason || ''}>
                            Link to “{m.suggested_contact.name}”
                          </button>
                        )}
                        {/* Searchable, for the same reason as the ledger: several hundred
                            contacts is not a list anybody should scroll past. */}
                        <SearchPicker
                          value="" options={contactOptions(contacts)} onChange={id => link(m, id)}
                          disabled={busy} allowClear={false} className="w-[18rem]"
                          placeholder={m.suggested_contact ? 'or search for another…' : 'search Sage contacts…'}
                          onCreate={name => create(m, name)}
                          createLabel={q => `Create “${q}” in Sage as a new supplier`}
                        />
                        <button onClick={() => create(m)} disabled={busy}
                          className={`${btn} border-gray-300 text-gray-600 bg-white hover:bg-gray-50`}>
                          It’s new — create it
                        </button>
                      </div>
                    )}
                </td>
                <td className="px-3 py-1">
                  <SearchPicker
                    value={m.default_ledger_account_id || ''} options={ledgerOptions(ledgers)}
                    onChange={id => setLedger(m, id)} disabled={busy} className="w-[14rem]"
                    placeholder="search the ledger…" emptyLabel="— none —"
                  />
                  {m.learned_at && !m.notes && (
                    <div className={`${T.meta} text-gray-400`}>learned from a previous transaction</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Cards ────────────────────────────────────────────────────────────────────
function CardsPanel({ people, onFlash, onChanged }) {
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setCards((await api.get('cot/cards'))?.cards || []); } catch { /* quiet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const map = async (card, personId) => {
    setBusy(true);
    try {
      await api.put(`cot/cards/${card.id}`, { person_id: personId || null });
      onFlash('ok', 'Card mapped, and all its spend with it.');
      await load(); onChanged();
    } catch (e) { onFlash('error', e.message || 'Could not map that.'); }
    finally { setBusy(false); }
  };

  const unmapped = cards.filter(c => !c.person_id);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? cards.filter(c => [c.card_ending, c.cardholder_name,
                         people.find(p => p.id === c.person_id)
                           ? `${people.find(p => p.id === c.person_id).first_name} ${people.find(p => p.id === c.person_id).last_name}`
                           : '']
        .some(v => String(v || '').toLowerCase().includes(needle)))
    : cards;
  const holderName = (c) => {
    const p = people.find(x => x.id === c.person_id);
    return p ? `${p.first_name} ${p.last_name}` : null;
  };
  const cardSort = useSort(shown, {
    card: c => c.card_ending, name: c => c.cardholder_name, person: holderName,
    txns: c => Number(c.txn_count) || 0, missing: c => Number(c.missing_receipts) || 0,
  }, null, { txns: 'desc', missing: 'desc' });
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Cards and who holds them</span>
        <div className="flex-1" />
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search card number or holder"
          className="border border-gray-200 rounded px-2 py-1 text-[11px] min-w-[14rem]" />
        <span className={`${T.meta} text-gray-500`}>
          {needle ? `${shown.length} of ${cards.length}` : `${cards.length} card${cards.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        Spend is attributed by card, because that is what the export identifies it by. Mapping a card moves all of
        its past spend to that person too.
        {unmapped.length > 0 && <strong className="text-amber-800"> {unmapped.length} card{unmapped.length === 1 ? '' : 's'} unmapped — that spend belongs to nobody and nobody will be chased for its receipts.</strong>}
      </p>
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="bg-gray-50">
          <tr>{[['card', 'Card'], ['name', 'Name on the export'], ['person', 'Person in ops'],
                ['txns', 'Transactions'], ['missing', 'Missing receipts']].map(([k, h]) => (
            <SortTh key={k} col={k} label={h} sort={cardSort.sort} onSort={cardSort.toggle}
              className={`${T.label} text-gray-400 px-3 py-1`} />
          ))}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {cardSort.sorted.map(c => (
            <tr key={c.id} className={c.person_id ? '' : 'bg-amber-50/40'}>
              <td className={`${T.body} px-3 py-1 font-mono`}>•••• {c.card_ending}</td>
              <td className={`${T.meta} px-3 py-1 text-gray-600`}>{c.cardholder_name || '—'}</td>
              <td className="px-3 py-1">
                <select className={sel} value={c.person_id || ''} disabled={busy}
                  onChange={e => map(c, e.target.value)}>
                  <option value="">— nobody —</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </td>
              <td className={`${T.meta} px-3 py-1`}>{c.txn_count}</td>
              <td className={`${T.meta} px-3 py-1 ${c.missing_receipts ? 'font-semibold text-red-700' : 'text-gray-400'}`}>
                {c.missing_receipts || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

// ── WHAT HAS BEEN SPENT, AND ON WHAT ─────────────────────────────────────────
//
// "Basically to encourage people to do receipts, and reduce spend."
//
// Two jobs, and they want different numbers on the screen.
//
// RECEIPTS respond to being told what a missing one costs, in pounds, against your own name.
// "You have 6 missing" is an admin request; "£47 of VAT we cannot reclaim" is a reason.
//
// SPEND responds to a total nobody had added up. Forty separate £5.60 taps are invisible;
// "£312 at Costa since August" is a fact that changes what somebody does on Monday. Which is
// why the merchant list is here and not only the nominal codes: nobody recognises their own
// habits in "7502 Staff Subsistence".
// onDrill turns a summary row into a search over the record below it. Passing a term
// rather than an id on purpose: the search runs on the SERVER, so drilling into "7550" or
// "Samsung UK" finds every matching transaction rather than the ones that happen to be in
// the page already. A list capped at 500 that filters itself is a drill-down that lies.
function SpendSummaryPanel({ personId = null, everyone = false, title = 'Your spend over time', onDrill = null }) {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const q = new URLSearchParams({ months: String(months) });
    if (personId) q.set('person_id', personId);
    if (everyone) q.set('all', '1');
    api.get(`cot/spend-summary?${q}`)
      .then(d => { if (live) setData(d); })
      .catch(() => { if (live) setData(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [months, personId, everyone]);

  const peak = Math.max(1, ...(data?.by_month || []).map(m => m.spend));
  const ledgerSort = useSort(data?.by_ledger, {
    code: l => l.nominal_code, name: l => l.ledger_name, spend: l => Number(l.spend) || 0,
    txns: l => Number(l.txns) || 0, missing: l => Number(l.missing) || 0,
    vat: l => (l.missing ? Number(l.missing_value) / 6 : 0),
  }, null, { spend: 'desc', txns: 'desc', missing: 'desc', vat: 'desc' });
  const monthLabel = (m) => {
    const [y, mo] = String(m).split('-');
    return new Date(Date.UTC(Number(y), Number(mo) - 1, 1)).toLocaleDateString('en-GB', { month: 'short' });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>{title}</span>
        <div className="flex gap-1">
          {[[3, '3 months'], [6, '6 months'], [12, '12 months']].map(([n, label]) => (
            <button key={n} onClick={() => setMonths(n)}
              className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${months === n
                ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={`${T.meta} text-gray-500 px-3 py-3`}>Reading…</div>
      ) : !data ? (
        <div className={`${T.meta} text-gray-500 px-3 py-3`}>That could not be worked out just now.</div>
      ) : (
        <div className="p-3 space-y-3">
          {/* ── HOW MUCH OF THE WINDOW IS REAL ──
              "Say data only valid from the 1st August 2026." It matters more than a
              footnote: on a 12-month window today that is a fortnight of data, and a
              "12 months" heading over a fortnight invites somebody to conclude the company
              barely uses its cards. So the screen says what it actually covers. */}
          {data.clamped && (
            <div className={`${T.meta} px-2 py-1.5 rounded border border-gray-200 bg-gray-50 text-gray-600`}>
              Card data starts {fmtDay(data.data_from)}, so this covers {fmtDay(data.from)} to {fmtDay(data.to)} rather than the full
              {' '}{data.months} months. Everything before that was not held here.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              ['Spent', money(data.spend), `${data.transactions} transaction${data.transactions === 1 ? '' : 's'}`, 'text-navy'],
              ['Missing receipts', String(data.missing_receipts),
                data.missing_receipts ? `on ${money(data.missing_receipt_value)} of spend` : 'all present',
                data.missing_receipts ? 'text-red-700' : 'text-emerald-700'],
              // The number that gets receipts uploaded. First in pounds, because that is
              // what it is.
              ['VAT at stake', money(data.vat_at_risk),
                data.missing_receipts ? 'cannot be reclaimed without the receipts' : 'nothing at stake',
                data.vat_at_risk ? 'text-red-700' : 'text-emerald-700'],
              ['VAT reclaimed', money(data.vat_reclaimed), 'on what has been receipted', 'text-gray-800'],
            ].map(([label, value, note, colour]) => (
              <div key={label} className="rounded border border-gray-200 px-2 py-1.5">
                <div className={`${T.label} text-gray-400`}>{label}</div>
                <div className={`${T.title} ${colour}`}>{value}</div>
                <div className={`${T.meta} text-gray-500`}>{note}</div>
              </div>
            ))}
          </div>

          {/* ── MONTH BY MONTH ──
              Empty months are drawn, not skipped: a gap is the thing worth seeing and a
              chart built from only the months that have rows silently closes it up. */}
          {data.by_month.length > 1 && (
            <div>
              <div className={`${T.label} text-gray-400 mb-1`}>By month</div>
              <div className="flex items-end gap-1 h-20">
                {data.by_month.map(m => (
                  <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full"
                    title={`${m.month}: ${money(m.spend)} over ${m.txns} transaction${m.txns === 1 ? '' : 's'}`
                      + `${m.missing ? `, ${m.missing} missing a receipt` : ''}`}>
                    {/* The missing-receipt share of the bar is drawn in red on top of the
                        rest, so a month that was spent but not evidenced looks different
                        from one that was both. */}
                    <div className="w-full flex flex-col justify-end" style={{ height: `${Math.max(2, (m.spend / peak) * 100)}%` }}>
                      {m.missing_value > 0 && (
                        <div className="w-full bg-red-400 rounded-t"
                          style={{ height: `${Math.min(100, (m.missing_value / Math.max(m.spend, 0.01)) * 100)}%` }} />
                      )}
                      <div className="w-full bg-navy flex-1 rounded-t" style={m.missing_value > 0 ? { borderRadius: 0 } : {}} />
                    </div>
                    <div className={`${T.meta} text-gray-500 mt-0.5`}>{monthLabel(m.month)}</div>
                  </div>
                ))}
              </div>
              <div className={`${T.meta} text-gray-400 mt-0.5`}>
                Red is the part of the month with no receipt against it.
              </div>
            </div>
          )}

          {/* ── PER NOMINAL CODE ── */}
          <div>
            <div className={`${T.label} text-gray-400 mb-1`}>By nominal code</div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>{[['code', 'Code'], ['name', 'What it is'], ['spend', 'Spent'], ['txns', 'Transactions'],
                        ['missing', 'No receipt'], ['vat', 'VAT at stake']].map(([k, h]) => (
                    <SortTh key={k} col={k} label={h} sort={ledgerSort.sort} onSort={ledgerSort.toggle}
                      className={`${T.label} text-gray-400 py-0.5`} />
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ledgerSort.sorted.map(l => (
                    <tr key={l.ledger_account_id || 'uncoded'}
                        className={onDrill && l.nominal_code ? 'cursor-pointer hover:bg-gray-50' : ''}
                        onClick={onDrill && l.nominal_code ? () => onDrill(l.nominal_code) : undefined}
                        title={onDrill && l.nominal_code ? `Show the transactions coded to ${l.nominal_code}` : undefined}>
                      <td className={`${T.meta} py-0.5 font-mono`}>
                        {l.nominal_code
                          ? (onDrill ? <span className="underline decoration-dotted text-navy">{l.nominal_code}</span> : l.nominal_code)
                          : '—'}
                      </td>
                      <td className={`${T.body} py-0.5 ${l.ledger_account_id ? '' : 'text-amber-800'}`}>{l.ledger_name}</td>
                      <td className={`${T.body} py-0.5 font-semibold`}>{money(l.spend)}</td>
                      <td className={`${T.meta} py-0.5 text-gray-600`}>{l.txns}</td>
                      <td className={`${T.body} py-0.5 ${l.missing ? 'font-semibold text-red-700' : 'text-gray-400'}`}>
                        {l.missing || '—'}
                      </td>
                      <td className={`${T.meta} py-0.5 ${l.missing ? 'text-red-700' : 'text-gray-400'}`}>
                        {l.missing ? money(l.missing_value / 6) : '—'}
                      </td>
                    </tr>
                  ))}
                  {!data.by_ledger.length && (
                    <tr><td colSpan={6} className={`${T.meta} text-gray-500 py-2`}>Nothing in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── AND WHERE IT ACTUALLY WENT ──
              Nobody recognises their own habits in "7502 Staff Subsistence". They do
              recognise a merchant name and a total. */}
          {!!data.by_merchant.length && (
            <div>
              <div className={`${T.label} text-gray-400 mb-1`}>Where it went</div>
              <div className="space-y-0.5">
                {data.by_merchant.map(m => (
                  <div key={m.merchant}
                       className={`flex items-center gap-2${onDrill ? ' cursor-pointer hover:bg-gray-50 rounded' : ''}`}
                       onClick={onDrill ? () => onDrill(m.merchant) : undefined}
                       title={onDrill ? `Show the transactions at ${m.merchant}` : undefined}>
                    <div className={`${T.body} flex-1 truncate${onDrill ? ' underline decoration-dotted text-navy' : ''}`}
                         title={m.merchant}>{m.merchant}</div>
                    <div className={`${T.meta} text-gray-500 w-16 text-right`}>{m.txns}×</div>
                    <div className={`${T.body} font-semibold w-20 text-right`}>{money(m.spend)}</div>
                    <div className={`${T.meta} w-24 text-right ${m.missing ? 'text-red-700 font-semibold' : 'text-gray-300'}`}>
                      {m.missing ? `${m.missing} no receipt` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── WHAT IS WRONG WITH WHAT WE ALREADY HOLD ──────────────────────────────────
//
// "We already have some data we uploaded via the Capital on Tap zip, can you do anything
//  with those to fix it?"
//
// Reads only, until somebody presses the button. The one that has to be run is the lost Sage
// link: until this branch a successful post read the new entry's id from one of the four
// places Sage puts it, so on the other three ops recorded NULL and the transaction went back
// to reading "not in Sage" with a Post button next to it. Every attempt was logged with
// Sage's whole response and the ops reference, so the pair can be rebuilt from disk.
function RepairPanel({ onFlash, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const check = useCallback(async () => {
    try { setState(await api.get('cot/repair')); } catch { /* quiet: this is a diagnostic */ }
  }, []);
  useEffect(() => { check(); }, [check]);

  const lost = state?.lost_sage_links;
  const wouldRepair = lost?.repaired || 0;
  const needsAttention = wouldRepair > 0 || (lost?.no_id_in_response || 0) > 0 || (lost?.details?.length || 0) > 0;
  if (!state) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left">
        <span className={`${T.label} text-gray-500`}>What is already imported, and what needs putting right</span>
        <span className={`${T.meta} ${needsAttention ? 'font-semibold text-amber-800' : 'text-emerald-700'}`}>
          {wouldRepair > 0
            ? `${wouldRepair} posted to Sage but not recorded as posted`
            : 'Nothing to repair'}
          <span className="text-gray-400 ml-1">{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
          {wouldRepair > 0 ? (
            <div className={`${T.body} px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900`}>
              <strong>{wouldRepair} transaction{wouldRepair === 1 ? '' : 's'} were posted to Sage and not recorded as
              posted.</strong> Sage returns a new entry's id in one of four places and the old code read one of them,
              so these show as "not in Sage" with a Post button that would create a second entry. The Sage entry is in
              the push log, so linking them needs no call to Sage and creates nothing.
              <button className={`${btn} bg-white text-amber-900 border-amber-300 mt-1.5`} disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await api.post('cot/repair', {});
                    onFlash('ok', `${r.lost_sage_links.repaired} linked to the Sage entry they were already posted to.`);
                    await check(); onChanged?.();
                  } catch (e) { onFlash('error', e.message || 'The repair failed.'); }
                  finally { setBusy(false); }
                }}>
                Link them to their Sage entries
              </button>
            </div>
          ) : (
            <div className={`${T.meta} text-gray-600`}>
              Every card transaction that was posted to Sage is recorded against its entry. Nothing to repair.
            </div>
          )}
          {lost?.details?.length > 0 && (
            <div className={`${T.meta} text-gray-600`}>
              <div className="font-semibold text-gray-700">What the push log says</div>
              <ul className="ml-3 list-disc">
                {lost.details.slice(0, 8).map((d, i) => (
                  <li key={i}>{d.transaction_id?.slice(0, 8)}… {d.sage_invoice_id ? `→ ${d.sage_invoice_id} ` : ''}{d.note}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Everything else is somebody's deliberate button. Counted here so the work is
              visible in one place, and never done as a side effect of a diagnostic. */}
          {state.next_steps?.length > 0 && (
            <div className={`${T.meta} text-gray-600`}>
              <div className="font-semibold text-gray-700">Still to do, each from its own button</div>
              <ul className="ml-3 list-disc">
                {state.next_steps.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
          <div className={`${T.meta} text-gray-400`}>
            {state.transactions.total} card transactions held · {state.transactions.uncoded} without a nominal code ·
            {' '}{state.transactions.awaiting_receipt} still waiting on a receipt.
          </div>
        </div>
      )}
    </div>
  );
}

// ── THE CARD ACCOUNT ─────────────────────────────────────────────────────────
//
// "It's showing lots of debt to suppliers in Sage, but in reality, Capital on Tap has a
//  weekly direct debit, so they're never really in debt."
//
// This screen exists to get one number to nil: card spend sitting in Sage that nothing has
// paid. Every one of those is a merchant showing as owed money they were handed at the till.
//
// Underneath it, the weekly direct debit and the charges it collected — the job that used to
// be done by eye against a bank account, with the matching done for you.
function CardAccountPanel({ onFlash, onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);        // the settlement whose charges are showing
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get('cot/settlements')); } catch { /* quiet */ }
  }, []);
  // Sorted by what the In Sage column says, so the same words group together.
  const transferState = (s) => (s.sage_transfer_id ? 'transferred' : s.sage_transfer_error ? 'refused'
    : s.transfer_checked_at ? 'not in Sage yet' : 'not checked');
  const settlementSort = useSort(data?.settlements, {
    date: s => s.settled_on, collected: s => Number(s.amount) || 0, allocated: s => Number(s.allocated_amount) || 0,
    variance: s => Math.abs(Number(s.variance) || 0), txns: s => Number(s.txn_count) || 0, sage: transferState,
  }, null, { date: 'desc', collected: 'desc', allocated: 'desc', variance: 'desc', txns: 'desc' });
  const allocationSort = useSort(detail?.allocations, {
    cleared: a => a.clearance_date, merchant: a => a.merchant_name || a.merchant_raw, card: a => a.card_ending,
    charge: a => Number(a.charge_amount) || 0, amount: a => Number(a.amount) || 0,
    sage: a => (a.sage_payment_id ? 'paid' : a.sage_invoice_id ? 'posted, unpaid' : 'not posted'),
  }, null, { cleared: 'desc', charge: 'desc', amount: 'desc' });
  useEffect(() => { load(); }, [load]);

  const expand = async (id) => {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id); setDetail(null);
    try { setDetail(await api.get(`cot/settlements/${id}`)); }
    catch (e) { onFlash('error', e.message || 'Could not read that one.'); }
  };

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const r = await fn();
      onFlash('ok', typeof okMessage === 'function' ? okMessage(r) : okMessage);
      await load(); onChanged?.();
      if (open) { try { setDetail(await api.get(`cot/settlements/${open}`)); } catch { /* quiet */ } }
    } catch (e) { onFlash('error', e.message || 'That did not work.'); }
    finally { setBusy(false); }
  };

  const accept = async (s) => {
    const reason = window.prompt(
      `This direct debit collected ${money(s.amount)} and only ${money(s.allocated_amount)} of card charges account for it.`
      + `\n\n${money(s.variance)} is unexplained, which usually means card spend has not been imported yet.`
      + '\n\nWhat is the difference?');
    if (!reason) return;
    await run(() => api.post(`cot/settlements/${s.id}/accept`, { reason }), 'Recorded, with your name against it.');
  };

  const sum = data?.summary;
  const configured = !!sum?.card_account;
  // Who makes the weekly transfer. Normally the Barclays feed does, and ops must not offer a
  // button that would make a second one.
  const opsPostsTransfer = sum?.transfer_mode === 'ops_post';

  return (
    <div className="space-y-3">
      {/* ── WHAT IS STILL SHOWING AS DEBT ──
          The headline, in pounds, because "23 unpaid entries" is an admin note and
          "£8,412 of card spend showing as owed to suppliers" is the reason to act. */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
        {!configured ? (
          <div className={`${T.body} px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-900`}>
            <strong>The Capital on Tap card is not a bank account in Sage yet.</strong> Until it is, a card charge has
            nothing to be paid from, so every merchant stays on aged creditors for money that left the business at the
            till. Create a credit card account called Capital on Tap in Sage, sync Sage, then choose it on the Settings
            tab.
          </div>
        ) : (
          <>
            {/* ── WHY THERE IS A PAY BUTTON AT ALL ──
                "When a credit card transaction is uploaded, it's already been paid by the
                Capital on Tap card, so not sure why I'd need to click this button?"
                Right, and that is the point: the money is gone, and Sage does not know.
                Nothing here moves money. It writes down, against the invoice, the payment
                the card already made. Without that sentence on the screen the button reads
                as "pay this supplier", which is a thing nobody should press twice. */}
            <p className={`${T.meta} text-gray-500`}>
              Nothing on this tab moves any money. The card has already paid these merchants; what Sage is missing is
              the record of it, so its invoices sit in aged creditors as debt we do not owe. Recording the payment
              against each one, drawn on the Capital on Tap account, is what squares the merchant and moves the balance
              on to the card, where a card balance belongs. Capital on Tap then collect it by direct debit, which is the
              panel below.
            </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              ['Showing as owed to suppliers', money(sum.unpaid_in_sage), sum.unpaid_in_sage_count
                ? `${sum.unpaid_in_sage_count} entr${sum.unpaid_in_sage_count === 1 ? 'y' : 'ies'} in Sage that nothing has paid`
                : 'nothing — which is the point', sum.unpaid_in_sage_count ? 'text-red-700' : 'text-emerald-700'],
              ['On the card, not yet collected', money(sum.outstanding), 'spent, and the direct debit has not taken it yet', 'text-navy'],
              ['Spent on the card', money(sum.charged), `${sum.transactions} transactions`, 'text-gray-800'],
              ['Collected by direct debit', money(sum.collected), `${sum.direct_debits} direct debit${sum.direct_debits === 1 ? '' : 's'}`, 'text-gray-800'],
            ].map(([label, value, note, colour]) => (
              <div key={label} className="rounded border border-gray-200 px-2 py-1.5">
                <div className={`${T.label} text-gray-400`}>{label}</div>
                <div className={`${T.title} ${colour}`}>{value}</div>
                <div className={`${T.meta} text-gray-500`}>{note}</div>
              </div>
            ))}
          </div>
          </>
        )}

        {configured && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${T.meta} text-gray-500`}>
              Paid from <strong className="text-gray-700">{sum.card_account.name}</strong>
              {sum.card_account.type ? ` (${sum.card_account.type})` : ''}
              {sum.card_account.sage_balance != null && <> · Sage says its balance is {money(sum.card_account.sage_balance)}</>}
            </span>
            <div className="flex-1" />
            {/* The one button that fixes what is already wrong. It covers the entries
                Capital on Tap's own feed created as well as ours: their feed has been
                posting card spend for months and paying none of it.

                It works in batches of 200, because every one of them is a call to Sage. So
                the label says which 200 and the result says how many are left: on eight
                months of backfill this takes several presses, and "412 paid" with no
                remainder next to it is how somebody concludes it is finished. */}
            <button className={`${btn} bg-navy text-white border-navy`} disabled={busy || !sum.unpaid_in_sage_count}
              onClick={() => run(() => api.post('cot/settle-posted', {}),
                r => `${r.settled} recorded as paid by the card`
                  + `${r.nothing_owed ? `, ${r.nothing_owed} Sage already showed as settled` : ''}`
                  + `${r.failed ? `, ${r.failed} refused` : ''}.`
                  + (r.remaining ? ` ${r.remaining} still to record — press it again.` : ''))}>
              {/* "Pay N" reads as an instruction to pay a supplier who is owed money. Nobody
                  is owed anything: the card paid them. What the button does is tell Sage so. */}
              {sum.unpaid_in_sage_count
                ? (sum.unpaid_in_sage_count > 200
                    ? `Record 200 of ${sum.unpaid_in_sage_count} as paid by the card`
                    : `Record ${sum.unpaid_in_sage_count} as paid by the card`)
                : 'Nothing left to record'}
            </button>
            <button className={`${btn} bg-white text-gray-700 border-gray-300`} disabled={busy}
              onClick={() => run(() => api.post('cot/settlements/reconcile', {}),
                r => `${r.built} direct debit${r.built === 1 ? '' : 's'} matched${r.short ? `, ${r.short} short` : ''}.`)}>
              Match the direct debits again
            </button>
          </div>
        )}
        {configured && !!sum.payment_errors && (
          <p className={`${T.meta} text-red-700`}>
            {sum.payment_errors} charge{sum.payment_errors === 1 ? '' : 's'} could not be paid. Open the transaction on
            the Payments tab: the reason Sage gave is on the row.
          </p>
        )}
      </div>

      {/* ── WHAT IS WRONG WITH WHAT WE ALREADY HOLD ──
          One repair needs running once, and it is the dangerous one: a transaction posted to
          Sage whose entry id was lost reads as "not in Sage" and offers a button that would
          post it a second time. The pair is rebuilt out of the push log, with no Sage call
          and nothing created — so checking costs nothing. */}
      <RepairPanel onFlash={onFlash} onChanged={() => { load(); onChanged?.(); }} />

      {/* ── THE WEEKLY DIRECT DEBIT ──
          What it collected, and whether the charges add up to it. A short one means card
          spend is missing from ops, because Capital on Tap only collect what was spent. */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          <span className={`${T.label} text-gray-500`}>The weekly direct debit, and what it paid for</span>
          {!!sum?.settlements?.short && (
            <span className={`${T.meta} font-semibold text-red-700`}>
              {sum.settlements.short} short by {money(sum.settlements.short_by)} in total
            </span>
          )}
        </div>
        <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
          Nothing here has to happen for a charge to stop showing as debt: each one is paid on the day it cleared. This
          is the other question — which charges a given direct debit collected — and it is worth having because Capital
          on Tap only ever collect money that was spent. A direct debit larger than the charges we hold is card spend
          that never reached ops.
          {opsPostsTransfer
            ? ' The transfer into the card account is posted from here, which is right only because the account the direct debit leaves has no bank feed.'
            : ' The transfer itself stays where it is: the direct debit arrives on the Barclays feed and somebody marks it as a transfer to the card account. Ops looks for that transfer and links to it rather than making a second one.'}
          {!!data?.unreconciled_direct_debits && (
            <strong className="text-amber-800"> {data.unreconciled_direct_debits} direct debit
              {data.unreconciled_direct_debits === 1 ? ' has' : 's have'} never been matched.</strong>
          )}
        </p>
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {[['date', 'Date'], ['collected', 'Collected'], ['allocated', 'Accounted for'], ['variance', 'Difference'],
                ['txns', 'Charges'], ['sage', 'In Sage']].map(([k, h]) => (
                <SortTh key={k} col={k} label={h} sort={settlementSort.sort} onSort={settlementSort.toggle}
                  className={`${T.label} text-gray-400 px-3 py-1`} />
              ))}
              <th className={`${T.label} text-gray-400 px-3 py-1 text-left`} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {settlementSort.sorted.map(s => (
              <React.Fragment key={s.id}>
                <tr className={s.status === 'short' && !s.accepted_reason ? 'bg-red-50/40' : ''}>
                  <td className={`${T.body} px-3 py-1 font-semibold`}>
                    <button className="underline decoration-dotted" onClick={() => expand(s.id)}>{fmtDay(s.settled_on)}</button>
                  </td>
                  <td className={`${T.body} px-3 py-1 font-semibold`}>{money(s.amount)}</td>
                  <td className={`${T.meta} px-3 py-1`}>{money(s.allocated_amount)}</td>
                  <td className={`${T.body} px-3 py-1 font-semibold ${Math.abs(s.variance) < 0.005 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {Math.abs(s.variance) < 0.005 ? '—' : money(s.variance)}
                  </td>
                  <td className={`${T.meta} px-3 py-1 text-gray-600`}>{s.txn_count}</td>
                  {/* ── THE TRANSFER, WHICH IS NOT OPS'S TO MAKE ──
                      It comes from the Barclays feed: the direct debit arrives there and
                      somebody marks it as a transfer to the card account. So this column
                      reports whether that has happened, and never says "not yet" as though
                      it were ops's to do. */}
                  <td className={`${T.meta} px-3 py-1`}>
                    {s.sage_transfer_id
                      ? <span className="text-emerald-700 font-semibold"
                          title={s.transfer_check_note || ''}>
                          transferred{s.sage_transfer_source === 'bank_feed' ? ' (bank feed)' : ''}
                        </span>
                      : s.sage_transfer_error
                        ? <span className="text-red-700" title={s.sage_transfer_error}>refused</span>
                        : opsPostsTransfer
                          ? <span className="text-gray-400">not posted</span>
                          : <span className="text-gray-500" title={s.transfer_check_note || ''}>
                              {s.transfer_checked_at ? 'not in Sage yet' : 'not checked'}
                            </span>}
                  </td>
                  <td className="px-3 py-1 text-right whitespace-nowrap">
                    {/* A short direct debit is worth explaining whoever makes the transfer:
                        the difference is usually card spend missing from the ledger. */}
                    {s.status === 'short' && !s.accepted_reason && (
                      <button className={`${btn} bg-white text-amber-900 border-amber-300 mr-1`} disabled={busy}
                        onClick={() => accept(s)}>Explain the difference</button>
                    )}
                    {!s.sage_transfer_id && (opsPostsTransfer
                      ? <button className={`${btn} bg-white text-gray-700 border-gray-300`} disabled={busy}
                          onClick={() => run(() => api.post(`cot/settlements/${s.id}/post`, {}), 'Transfer posted to Sage.')}>
                          Post the transfer
                        </button>
                      // Finds the one the feed made, and creates nothing. The button says
                      // "look for" rather than "post" because that is the whole difference.
                      : <button className={`${btn} bg-white text-gray-700 border-gray-300`} disabled={busy}
                          onClick={() => run(() => api.post(`cot/settlements/${s.id}/find-transfer`, {}),
                            r => r.ok ? 'Found the transfer the bank feed made.' : r.error)}>
                          Look for the transfer
                        </button>)}
                  </td>
                </tr>
                {s.accepted_reason && (
                  <tr><td colSpan={7} className={`${T.meta} px-3 pb-1 text-gray-600`}>
                    Difference accepted by {s.accepted_by_name || 'somebody'}: {s.accepted_reason}
                  </td></tr>
                )}
                {open === s.id && (
                  <tr><td colSpan={7} className="px-3 py-2 bg-gray-50">
                    {!detail ? <span className={`${T.meta} text-gray-500`}>Reading…</span> : (
                      <div className="overflow-x-auto"><table className="w-full">
                        <thead>
                          <tr>{[['cleared', 'Cleared'], ['merchant', 'Merchant'], ['card', 'Card'], ['charge', 'Charge'],
                                ['amount', 'Collected here'], ['sage', 'In Sage']].map(([k, h]) => (
                            <SortTh key={k} col={k} label={h} sort={allocationSort.sort} onSort={allocationSort.toggle}
                              className={`${T.label} text-gray-400 py-0.5`} />
                          ))}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {allocationSort.sorted.map(a => (
                            <tr key={a.id}>
                              <td className={`${T.meta} py-0.5`}>{fmtDay(a.clearance_date)}</td>
                              <td className={`${T.body} py-0.5`}>
                                {a.merchant_name || a.merchant_raw}
                                {a.kind === 'refund' && <span className={`${T.meta} text-emerald-700`}> · refund</span>}
                                {a.source === 'manual' && <span className={`${T.meta} text-gray-500`}> · moved by hand</span>}
                              </td>
                              <td className={`${T.meta} py-0.5 font-mono`}>•••• {a.card_ending}</td>
                              <td className={`${T.meta} py-0.5`}>{money(a.charge_amount)}</td>
                              {/* Where the two differ, this charge was collected across
                                  more than one week, which is a real thing rather than a
                                  fault: Capital on Tap collect a balance. */}
                              <td className={`${T.body} py-0.5 font-semibold`}>
                                {money(a.amount)}
                                {round2(a.amount) !== round2(a.charge_amount) && (
                                  <span className={`${T.meta} text-gray-500 font-normal`}> · part</span>
                                )}
                              </td>
                              <td className={`${T.meta} py-0.5`}>
                                {a.sage_payment_id
                                  ? <span className="text-emerald-700">paid</span>
                                  : a.sage_invoice_id
                                    ? <span className="text-red-700" title={a.sage_payment_error || ''}>posted, unpaid</span>
                                    : <span className="text-gray-400">not posted</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table></div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {/* Two different empty states, because they need two different things doing.
                Saying "none imported yet" while the header counts two that have never been
                matched is the screen contradicting itself in the space of one panel, and it
                sends somebody looking for an import that already happened. */}
            {!data?.settlements?.length && (
              <tr><td colSpan={7} className={`${T.meta} text-gray-500 px-3 py-2`}>
                {data?.unreconciled_direct_debits
                  ? `${data.unreconciled_direct_debits} direct debit${data.unreconciled_direct_debits === 1 ? ' is' : 's are'} imported`
                    + ` and ${data.unreconciled_direct_debits === 1 ? 'it has' : 'none has'} been matched to its charges yet.`
                    + ' Press "Match the direct debits again" above. Nothing is posted to Sage by matching, and no charge'
                    + ' is waiting on it: each one is paid on the day it cleared.'
                  : 'No direct debits imported yet. They arrive in the Capital on Tap export as "Payment made (Direct'
                    + ' Debit)" rows and are matched to their charges as part of the import.'}
              </td></tr>
            )}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

// ── MARKED NO RECEIPT ────────────────────────────────────────────────────────
//
// Every time a cardholder said there is no receipt. The open ones are the transactions
// themselves, so finance decide them on the row as they would on Payments; the rest is
// the record of how each one ended, kept apart from the transaction because sending one
// back clears it from the row.
const NO_RECEIPT_OUTCOMES = {
  confirmed:     ['Confirmed no receipt', 'bg-blue-50 text-blue-700'],
  accepted:      ['Accepted by finance', 'bg-blue-50 text-blue-700'],
  sent_back:     ['Asked to look again', 'bg-amber-50 text-amber-700'],
  taken_back:    ['Taken back by cardholder', 'bg-gray-100 text-gray-600'],
  receipt_added: ['Receipt found after all', 'bg-emerald-50 text-emerald-700'],
};

// ── PER-PERSON TOTALS AND THE EXPORT ──
//
// From the statements, open and past. Counts are per statement, because a transaction
// sent back and marked again was marked twice; money is per transaction, so the same
// charge is never added up twice. The VAT is the 20% ceiling (a sixth of the gross), as
// on Missing receipts: confirming one sets its VAT to nil, so the figure it would have
// carried is not held anywhere to sum.
const vatEst = (gross) => round2(Math.abs(Number(gross) || 0) / 6);
const writtenOff = (r) => r.outcome === 'confirmed' || r.outcome === 'accepted';

function noReceiptTotals(statements) {
  const people = new Map();
  for (const r of statements || []) {
    const key = r.person_id || r.person_name || '?';
    if (!people.has(key)) {
      people.set(key, { key, person_name: r.person_name || 'Unknown', marked: 0, waiting: 0, confirmed: 0, sent_back: 0,
        taken_back: 0, receipt_added: 0, txns: new Map() });
    }
    const p = people.get(key);
    p.marked += 1;
    if (!r.outcome) p.waiting += 1;
    else if (writtenOff(r)) p.confirmed += 1;
    else if (p[r.outcome] != null) p[r.outcome] += 1;
    // The latest statement on a transaction says where it stands: the list is open first,
    // then newest first, so the first one seen is it.
    if (!p.txns.has(r.transaction_id)) p.txns.set(r.transaction_id, r);
  }
  const rows = [...people.values()].map(p => {
    const txns = [...p.txns.values()];
    const sum = (pick) => round2(txns.filter(pick).reduce((a, t) => a + Math.abs(Number(t.amount) || 0), 0));
    const amount = sum(() => true);
    const waitingAmount = sum(t => !t.outcome);
    const writtenOffAmount = sum(writtenOff);
    // Accepted with VAT still reclaimed is the one waiver that does not give the VAT up.
    const vatGivenUp = round2(txns.filter(t => writtenOff(t) && !t.receipt_waived_vat_ok)
      .reduce((a, t) => a + vatEst(t.amount), 0));
    const { txns: _drop, ...rest } = p;
    return { ...rest, transactions: txns.length, amount, waiting_amount: waitingAmount,
      written_off: writtenOffAmount, vat_given_up: vatGivenUp };
  }).sort((a, b) => b.amount - a.amount || a.person_name.localeCompare(b.person_name));
  const total = rows.reduce((t, r) => {
    for (const k of ['marked', 'waiting', 'confirmed', 'sent_back', 'taken_back', 'receipt_added', 'transactions',
                     'amount', 'waiting_amount', 'written_off', 'vat_given_up']) t[k] = round2((t[k] || 0) + r[k]);
    return t;
  }, { person_name: 'Everyone' });
  return { rows, total };
}

// Real dates and numbers in the workbook, so it sorts, filters and sums as it should.
const xlDate = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0)) : null;
};

function exportNoReceipt(statements) {
  const head = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { patternType: 'solid', fgColor: { rgb: '0D1D32' } },
                 alignment: { vertical: 'center', wrapText: true } };
  const bold = { font: { bold: true } };
  const GBP = '£#,##0.00';
  const day = (v) => { const d = xlDate(v); return d ? { v: d, z: 'dd mmm yyyy' } : null; };
  const when = (v) => { const d = xlDate(v); return d ? { v: d, z: 'dd mmm yyyy hh:mm' } : null; };
  const gbp = (n, s) => (n == null || n === '' ? null : { v: round2(n), z: GBP, ...(s ? { s } : {}) });

  const detailHead = ['Status', 'Transaction date', 'Merchant', 'Card', 'Cardholder', 'Amount', 'VAT (est. 20%)',
    'VAT coded', 'Marked on', 'What they said', 'Decided by', 'Decided on', 'In Sage', 'Transaction ID'];
  const detail = (statements || []).map(r => [
    r.outcome ? (NO_RECEIPT_OUTCOMES[r.outcome]?.[0] || r.outcome) : 'Waiting on finance',
    day(r.clearance_date), r.merchant_name || '', r.card_ending ? `•••• ${r.card_ending}` : '', r.person_name || '',
    gbp(Math.abs(Number(r.amount) || 0)), gbp(vatEst(r.amount)), gbp(r.vat_amount),
    when(r.said_at), r.note || '', r.outcome_by_name || '', when(r.outcome_at), r.sage_invoice_id ? 'Yes' : 'No',
    r.transaction_id,
  ]);

  const { rows, total } = noReceiptTotals(statements);
  const personHead = ['Cardholder', 'Times marked', 'Waiting on finance', 'Confirmed or accepted', 'Asked to look again',
    'Taken back', 'Receipt found', 'Transactions', 'Amount marked', 'Amount waiting', 'Amount written off', 'VAT given up (est.)'];
  const personRow = (r, s) => [
    s ? { v: r.person_name, s } : r.person_name,
    ...['marked', 'waiting', 'confirmed', 'sent_back', 'taken_back', 'receipt_added', 'transactions'].map(k => (s ? { v: r[k], s } : r[k])),
    gbp(r.amount, s), gbp(r.waiting_amount, s), gbp(r.written_off, s), gbp(r.vat_given_up, s),
  ];

  const sheet = (name, header, body, cols, extra = []) => ({
    name,
    rows: [header.map(v => ({ v, s: head })), ...body, ...extra],
    cols: cols.map(wch => ({ wch })),
    autofilter: `A1:${colName(header.length - 1)}${body.length + 1}`,
    freeze: { xSplit: 0, ySplit: 1 },
  });
  downloadXlsx([
    sheet('By cardholder', personHead, rows.map(r => personRow(r)), [24, 12, 12, 14, 14, 11, 12, 12, 14, 14, 15, 15],
      [personRow(total, bold)]),
    sheet('Every statement', detailHead, detail, [22, 14, 26, 10, 20, 11, 13, 11, 17, 50, 18, 17, 8, 34]),
  ], `marked-no-receipt-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function NoReceiptPanel({ ledgers, contacts, isFinance, settings, onChanged, onFlash }) {
  const { navigate } = useApp();
  const [open, setOpen] = useState(null);
  const [statements, setStatements] = useState(null);
  const [q, setQ] = useState('');
  const [exportError, setExportError] = useState(null);
  const load = useCallback(async () => {
    const [o, p] = await Promise.all([
      api.get('cot/transactions', { said_no_receipt: '1' }).catch(() => null),
      api.get('cot/no-receipt-statements').catch(() => null),
    ]);
    setOpen(o?.transactions || []);
    setStatements(p?.statements || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const changed = async () => { await load(); onChanged(); };

  const openSort = useSort(open, TXN_SORT, null, TXN_FIRST_DIR);
  const past = statements && statements.filter(r => r.outcome);
  const totals = statements && noReceiptTotals(statements);
  const needle = q.trim().toLowerCase();
  const shown = (past || []).filter(r => !needle || [r.person_name, r.merchant_name, r.note, r.card_ending, r.outcome_by_name]
    .some(v => String(v || '').toLowerCase().includes(needle)));
  const personKeys = ['marked', 'waiting', 'confirmed', 'sent_back', 'taken_back', 'receipt_added',
    'amount', 'waiting_amount', 'written_off', 'vat_given_up'];
  const personSort = useSort(totals?.rows, {
    person_name: r => r.person_name,
    ...Object.fromEntries(personKeys.map(k => [k, r => Number(r[k]) || 0])),
  }, null, Object.fromEntries(personKeys.map(k => [k, 'desc'])));
  const pastSort = useSort(shown, {
    date: r => r.clearance_date, merchant: r => r.merchant_name, person: r => r.person_name,
    amount: r => Number(r.amount) || 0, said: r => r.said_at,
    outcome: r => (NO_RECEIPT_OUTCOMES[r.outcome]?.[0] || r.outcome),
  }, null, { date: 'desc', amount: 'desc', said: 'desc' });

  const doExport = () => {
    setExportError(null);
    try { exportNoReceipt(statements); }
    catch (e) { setExportError(e.message || 'The export failed.'); }
  };

  return (
    <div className="space-y-3">
      {/* Everything on this tab, open and past, with the per-person totals, to work on in Excel. */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        {exportError && <span role="alert" className={`${T.meta} font-semibold text-red-700`}>{exportError}</span>}
        <span className={`${T.meta} text-gray-500`}>Every statement, open and past, and the totals by cardholder</span>
        <button onClick={doExport} disabled={!statements?.length}
          className={`${btn} border-navy bg-white text-navy hover:bg-navy/5`}>
          Export to Excel
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          <span className={`${T.label} text-gray-500`}>Waiting on finance</span>
          <span className={`${T.meta} text-gray-600`}>
            {open ? `${open.length} transaction${open.length === 1 ? '' : 's'}` : 'Loading…'}
          </span>
        </div>
        <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
          The cardholder has said there is no receipt, so they are no longer chased for it. Each one is held from
          Sage until it is decided: open <strong className="text-gray-700">Decide</strong> on the row to confirm it,
          which posts it with no VAT reclaimed, or to ask them to look again.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <TxnHead sort={openSort.sort} onSort={openSort.toggle} />
            </thead>
            <tbody className="divide-y divide-gray-100">
              {openSort.sorted.map(t => (
                <TransactionRow key={t.id} t={t} ledgers={ledgers} contacts={contacts} isFinance={isFinance}
                  postingOn={!!settings?.posting_enabled} settings={settings}
                  onChanged={changed} onFlash={onFlash} />
              ))}
            </tbody>
          </table>
          {open && !open.length && (
            <div className={`${T.meta} text-emerald-700 font-semibold px-3 py-6 text-center`}>
              Nothing waiting. Every transaction a cardholder has said has no receipt has been decided.
            </div>
          )}
        </div>
      </div>

      {/* ── BY CARDHOLDER ──
          Who says it, how often, and what it comes to. Counts are statements; money is
          per transaction, so one marked twice is not added up twice. A name filters Past. */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          <span className={`${T.label} text-gray-500`}>By cardholder</span>
          <span className={`${T.meta} text-gray-500`}>VAT is estimated at 20% of the amount, as on Missing receipts</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>{[['person_name', 'Cardholder'], ['marked', 'Marked'], ['waiting', 'Waiting'], ['confirmed', 'Confirmed'],
                    ['sent_back', 'Looked again'], ['taken_back', 'Taken back'], ['receipt_added', 'Receipt found'],
                    ['amount', 'Amount marked'], ['waiting_amount', 'Amount waiting'], ['written_off', 'Written off'],
                    ['vat_given_up', 'VAT given up']].map(([k, h], i) => (
                <SortTh key={k} col={k} label={h} sort={personSort.sort} onSort={personSort.toggle} right={i > 0}
                  className={`${T.label} text-gray-400 px-2 py-1`} />
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {personSort.sorted.map(r => (
                <tr key={r.key}>
                  <td className={`${T.body} px-2 py-1`}>
                    <button onClick={() => setQ(r.person_name)} className="font-semibold text-navy text-left hover:underline"
                      title="Show this person's past statements">
                      {r.person_name}
                    </button>
                  </td>
                  {['marked', 'waiting', 'confirmed', 'sent_back', 'taken_back', 'receipt_added'].map(k => (
                    <td key={k} className={`${T.body} px-2 py-1 text-right ${r[k] ? 'text-gray-800' : 'text-gray-300'}`}>{r[k]}</td>
                  ))}
                  <td className={`${T.body} px-2 py-1 text-right font-semibold`}>{money(r.amount)}</td>
                  <td className={`${T.body} px-2 py-1 text-right ${r.waiting_amount ? 'text-amber-700' : 'text-gray-300'}`}>{money(r.waiting_amount)}</td>
                  <td className={`${T.body} px-2 py-1 text-right ${r.written_off ? 'text-gray-800' : 'text-gray-300'}`}>{money(r.written_off)}</td>
                  <td className={`${T.body} px-2 py-1 text-right ${r.vat_given_up ? 'text-red-700 font-semibold' : 'text-gray-300'}`}>{money(r.vat_given_up)}</td>
                </tr>
              ))}
              {totals?.rows.length > 1 && (
                <tr className="bg-gray-50 font-semibold">
                  <td className={`${T.body} px-2 py-1`}>Everyone</td>
                  {['marked', 'waiting', 'confirmed', 'sent_back', 'taken_back', 'receipt_added'].map(k => (
                    <td key={k} className={`${T.body} px-2 py-1 text-right`}>{totals.total[k]}</td>
                  ))}
                  <td className={`${T.body} px-2 py-1 text-right`}>{money(totals.total.amount)}</td>
                  <td className={`${T.body} px-2 py-1 text-right`}>{money(totals.total.waiting_amount)}</td>
                  <td className={`${T.body} px-2 py-1 text-right`}>{money(totals.total.written_off)}</td>
                  <td className={`${T.body} px-2 py-1 text-right text-red-700`}>{money(totals.total.vat_given_up)}</td>
                </tr>
              )}
            </tbody>
          </table>
          {statements && !statements.length && (
            <div className={`${T.meta} text-gray-500 px-3 py-6 text-center`}>
              No cardholder has said there is no receipt yet.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          <span className={`${T.label} text-gray-500`}>Past</span>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search person, merchant, card or what they said"
            className="border border-gray-200 rounded px-2 py-1 text-[11px] min-w-[16rem]" />
          <span className={`${T.meta} text-gray-600`}>
            {past ? `${shown.length}${needle ? ` of ${past.length}` : ''} statement${shown.length === 1 ? '' : 's'}` : 'Loading…'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>{[['date', 'Date'], ['merchant', 'Merchant'], ['person', 'Who'], ['amount', 'Amount'],
                    ['said', 'They said'], ['outcome', 'Outcome']].map(([k, h]) => (
                <SortTh key={k} col={k} label={h} sort={pastSort.sort} onSort={pastSort.toggle}
                  title={k === 'said' ? 'Sort by when they said it' : undefined}
                  className={`${T.label} text-gray-400 px-2 py-1`} />
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pastSort.sorted.map(r => {
                const [label, cls] = NO_RECEIPT_OUTCOMES[r.outcome] || [r.outcome, 'bg-gray-100 text-gray-600'];
                return (
                  <tr key={r.id} className="align-top">
                    <td className={`${T.meta} px-2 py-1 whitespace-nowrap`}>{fmtDay(r.clearance_date)}</td>
                    <td className={`${T.body} px-2 py-1`}>
                      {/* Opens the transaction on Payments, where everything else about it is. */}
                      <button onClick={() => navigate('card-spend', r.transaction_id)}
                        className="font-semibold text-navy text-left hover:underline">
                        {r.merchant_name || 'Card transaction'}
                      </button>
                      {r.card_ending && <span className={`${T.meta} block text-gray-400 font-mono`}>•••• {r.card_ending}</span>}
                    </td>
                    <td className={`${T.body} px-2 py-1 whitespace-nowrap`}>{r.person_name || '—'}</td>
                    <td className={`${T.body} px-2 py-1 whitespace-nowrap font-semibold`}>{money(r.amount)}</td>
                    <td className={`${T.meta} px-2 py-1 max-w-[22rem]`}>
                      <span className="italic text-gray-700">“{r.note}”</span>
                      <span className="block text-gray-400">{fmtWhen(r.said_at)}</span>
                    </td>
                    <td className={`${T.meta} px-2 py-1 whitespace-nowrap`}>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
                      <span className="block text-gray-400 mt-0.5">
                        {r.outcome_by_name ? `${r.outcome_by_name} · ` : ''}{fmtWhen(r.outcome_at)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {past && !shown.length && (
            <div className={`${T.meta} text-gray-500 px-3 py-6 text-center`}>
              {needle ? 'Nothing matches that search.' : 'No cardholder has said there is no receipt before now.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────
// ── ONE COMPONENT, TWO PAGES ─────────────────────────────────────────────────
//
// Finance > Company Cards and My Space > My Company Card are the same component. What
// separates them is mineOnly, passed by the route — and it was missing, so for anybody
// holding a finance role "My Company Card" rendered the whole company's card spend:
// everyone's transactions, the import and settings tabs, the finance headline. The
// personal view only appeared for people who happened NOT to be finance, which is why it
// looked right when the page was first wired up and wrong for the person who asked for it.
//
// mineOnly is not cosmetic. It scopes the REQUEST — the server returns everybody's
// transactions to a finance caller unless mine=1 is asked for — so hiding rows in the
// browser would have left one person's spend on another person's screen.
export default function CardSpendPage({ currentUser, mineOnly = false, transactionId = null }) {
  const { navigate } = useApp();
  const roles = String(currentUser?.user_role || '').split(',').map(r => r.trim());
  const isFinance = roles.some(r => ['admin', 'finance', 'billing'].includes(r));
  // Whether to show the FINANCE view. Distinct from isFinance, which is about what
  // somebody is allowed to do: a finance person looking at their own card is still
  // finance, they are just not doing finance's job on this page.
  const financeView = isFinance && !mineOnly;
  // ── DECIDING A "NO RECEIPT" WITHOUT A FINANCE ROLE ───────────────────────────
  //
  // The no-receipt Action goes to holders of card-spend:no-receipt-actions (the Finance
  // team), and the grant is the decision, whatever role they hold. Somebody who holds it
  // but is not finance opens the one transaction the Action names, sees what the
  // cardholder said, and decides it. Nothing else of finance's is on that view.
  const canDecide = isFinance || canAccess('card-spend:no-receipt-actions');
  const decideView = !financeView && !mineOnly && !!transactionId && canDecide;

  const [tab, setTab] = useState(decideView ? 'decide' : financeView ? 'payments' : 'mine');
  // Following an Action link while the page is already open changes transactionId without
  // remounting, so the tab follows it there and back.
  // A transaction opened from elsewhere on the page (a past "no receipt") is shown on
  // Payments, where its row is.
  useEffect(() => {
    setTab(cur => decideView ? 'decide' : cur === 'decide' ? (financeView ? 'payments' : 'mine')
      : financeView && transactionId ? 'payments' : cur);
  }, [decideView, financeView, transactionId]);
  const [data, setData] = useState(null);
  const [chase, setChase] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [people, setPeople] = useState([]);
  const [settings, setSettings] = useState(null);
  // Finance land on their own work, not on everything. A list that is mostly things you
  // cannot act on is a list that stops being read.
  const [filter, setFilter] = useState(financeView ? 'todo' : 'all');
  // "The to-do should toggle hide missing receipts as that's going to be chased to the card
  // holder." Off by default: those are somebody else's to fix and they are emailed weekly.
  const [includeAwaiting, setIncludeAwaiting] = useState(false);
  const [posting, setPosting] = useState(false);
  const [flash, setFlashState] = useState(null);
  const onFlash = (kind, text) => { setFlashState({ kind, text }); setTimeout(() => setFlashState(null), 6000); };

  // ── SEARCHING ───────────────────────────────────────────────────────────────
  //
  // Sent to the server, not applied to what arrived: the list is capped at 500, so
  // filtering in the browser would search the newest 500 and quietly miss everything
  // behind them. A search that finds nothing reads as "we do not hold it", which is a
  // worse answer than no search box at all.
  //
  // Debounced so a five-letter merchant name is one query rather than five.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // The chase list arrives whole, so this one filters what is already here.
  const [chaseQ, setChaseQ] = useState('');
  // ── WHO AND WHEN, ON PAYMENTS ───────────────────────────────────────────────
  //
  // "Add a filter on waiting on receipts so it can filter by date and person." Sent to the
  // server with the rest of the filter, for the same reason as the search: the list is
  // capped. The dates are the cleared date, which is the Date column. They apply to every
  // filter on the tab, not only that one, since narrowing to a person or a month is as
  // useful on the to-do. personFilter is a person id, or 'none' for cards nobody holds.
  const [personFilter, setPersonFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Payments is sorted on the server too, so a heading click is a fresh read rather than
  // a reshuffle of the newest 500.
  const [paySort, setPaySort] = useState({ key: 'date', dir: 'desc' });
  const togglePaySort = (key) => setPaySort(cur => (cur?.key === key
    ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: TXN_FIRST_DIR[key] || 'asc' }));
  const hasWhoWhen = !!(personFilter || dateFrom || dateTo);
  const clearWhoWhen = () => { setPersonFilter(''); setDateFrom(''); setDateTo(''); };
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const q = filter === 'todo' ? { todo: '1', ...(includeAwaiting ? { include_awaiting_receipt: '1' } : {}) }
      : filter === 'missing' ? { missing_receipt: '1' }
      : filter === 'done' ? { done: '1' }
      // In the ledger and nothing has paid it: the merchant is showing as owed money they
      // were handed at the till. A different job from the rest of this screen, so a
      // different filter — nothing is wrong with the transaction.
      : filter === 'unpaid' ? { unpaid: '1' }
      : {};
    // ── WHOSE TRANSACTIONS ────────────────────────────────────────────────────
    //
    // Asked for explicitly, because the server's default is the caller's PERMISSION and
    // not their intent: it returns everybody's card spend to a finance caller unless
    // mine=1 is requested. So My Company Card asks for mine=1, and the summary figures
    // come back scoped the same way as the list. Filtering in the browser instead would
    // have meant one person's spend was still sent to another person's screen.
    // Opened from an Action (#card-spend/<id>): that one transaction, whatever the
    // filter, so the decision the Action asks for is the row on the screen.
    const scoped = transactionId ? { id: transactionId, ...(mineOnly ? { mine: '1' } : {}) } : {
      ...(mineOnly ? { ...q, mine: '1' } : q),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      // Finance's Payments tab only: My company card has its own list and its own order.
      ...(financeView ? {
        ...(personFilter ? { person_id: personFilter } : {}),
        ...(dateFrom ? { from: dateFrom } : {}),
        ...(dateTo ? { to: dateTo } : {}),
        ...(paySort ? { sort: paySort.key, dir: paySort.dir } : {}),
      } : {}),
    };
    const [txns, missing] = await Promise.all([
      api.get('cot/transactions', scoped).catch(() => null),
      // The chase list is finance's — who owes which receipt, across everybody. Nobody
      // needs it to upload their own, and it is not theirs to see.
      financeView ? api.get('cot/missing-receipts').catch(() => null) : Promise.resolve(null),
    ]);
    setData(txns); setChase(missing);
  }, [filter, includeAwaiting, mineOnly, financeView, debouncedSearch, transactionId,
      personFilter, dateFrom, dateTo, paySort]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!financeView) return;
    api.get('sage/reference').then(r => {
      setLedgers(r?.ledger_accounts || []);
      setContacts((r?.contacts || []).filter(c => !c.contact_type || /vendor/i.test(c.contact_type)));
    }).catch(() => {});
    api.get('people').then(p => setPeople((p || []).filter(x => x.employee_status !== 'deleted'))).catch(() => {});
    api.get('cot/settings').then(setSettings).catch(() => {});
  }, [financeView]);

  // The crossover sweep, on demand as well as hourly. It creates nothing in Sage, so
  // pressing it is always safe — which is the property that makes dual running workable.
  const [matching, setMatching] = useState(false);
  // Working the crossover a day or a week at a time is how somebody stays on top of it
  // instead of facing the whole backlog, and it keeps each run short enough to read.
  const [matchDays, setMatchDays] = useState('');
  const matchFeed = async () => {
    setMatching(true);
    try {
      const r = await api.post('cot/match-feed', matchDays ? { days: Number(matchDays) } : {});
      const bits = [];
      if (r.linked) bits.push(`${r.linked} linked to their feed's entries`);
      if (r.receipts_sent) bits.push(`${r.receipts_sent} receipt page(s) sent to Sage`);
      if (r.ambiguous) bits.push(`${r.ambiguous} need you to pick between candidates`);
      if (r.absent) bits.push(`${r.absent} are genuinely not in Sage`);
      if (r.no_receipt_to_send) bits.push(`${r.no_receipt_to_send} have no receipt anywhere`);
      const window = r.from ? ` since ${r.from}` : '';
      onFlash(r.cache_not_ready ? 'warn' : 'ok',
        (bits.length ? `${bits.join(', ')}${window}` : `Nothing to match${window}`) + (r.cache_not_ready
          ? '. Our copy of the Sage ledger is still filling, so this is not the whole picture yet — run it again once the Sage screen says it is verified.' : '.'));
      await load();
    } catch (e) { onFlash('error', e.message || 'The match failed.'); }
    finally { setMatching(false); }
  };

  // Post everything ready, and report what refused rather than only what worked. A bulk
  // action that says "38 posted" and nothing about the other two is how two invoices go
  // missing.
  const postAllReady = async () => {
    if (!window.confirm(`Post ${s.ready_to_post} transaction${s.ready_to_post === 1 ? '' : 's'} to Sage?\n\nEach one is checked against Sage for an existing entry first, exactly as it would be on its own.`)) return;
    setPosting(true);
    try {
      const r = await api.post('cot/post-ready', {});
      const bits = [`${r.posted} posted`];
      if (r.receipts_attached) bits.push(`${r.receipts_attached} receipt page(s) attached`);
      if (r.failed?.length) bits.push(`${r.failed.length} refused`);
      onFlash(r.failed?.length ? 'warn' : 'ok',
        bits.join(', ') + (r.failed?.length ? `: ${r.failed.slice(0, 3).map(f => f.error).join('; ')}` : '.'));
      await load();
    } catch (e) { onFlash('error', e.message || 'The bulk post failed.'); }
    finally { setPosting(false); }
  };

  // ── SAVING ONE SETTING MUST NOT BLANK THE OTHERS ───────────────────────────
  //
  // This replaced the whole settings object with the save's answer. The answer carried
  // five fields, so choosing the account the direct debit leaves emptied both bank account
  // pickers, cleared the analysis category, unticked the transfer mode, and put "No bank
  // accounts have come back from Sage yet" on a screen whose accounts were fine. Nothing
  // had changed in the database and a page reload restored the lot, which is precisely
  // what makes it frightening: it reads as a save that wiped the configuration.
  //
  // The server now answers with the whole object. Merging rather than replacing as well,
  // because this screen must not depend on a response staying complete: a field that stops
  // being returned should go stale, never blank.
  const saveSetting = async (patch) => {
    try {
      const saved = await api.put('cot/settings', patch);
      setSettings(prev => ({ ...(prev || {}), ...(saved || {}) }));
      onFlash('ok', 'Saved.');
      await load();
    } catch (e) { onFlash('error', e.message || 'Could not save that.'); }
  };

  const s = data?.summary;
  // Who can be picked. On "Waiting on a receipt", the people who owe one, with how many,
  // from the chase list; otherwise everybody. Somebody already picked stays in the list
  // after switching filter, so the box never hides a filter that is still applied.
  const personOptions = (() => {
    const owing = filter === 'missing' && chase?.people ? (() => {
      const byPerson = new Map();
      for (const p of chase.people) {
        const id = p.person_id || 'none';
        const cur = byPerson.get(id) || { id, name: p.person_name || 'Cards nobody holds', missing: 0 };
        cur.missing += Number(p.missing) || 0;
        byPerson.set(id, cur);
      }
      return [...byPerson.values()].map(o => ({ id: String(o.id), label: `${o.name} (${o.missing})`, name: o.name }));
    })() : null;
    const opts = owing || [
      ...people.map(p => ({ id: String(p.id), label: `${p.first_name} ${p.last_name}`, name: `${p.first_name} ${p.last_name}` })),
      { id: 'none', label: 'Cards nobody holds', name: 'Cards nobody holds' },
    ];
    if (personFilter && !opts.some(o => o.id === String(personFilter))) {
      const p = people.find(x => String(x.id) === String(personFilter));
      opts.push({ id: String(personFilter), label: p ? `${p.first_name} ${p.last_name} (0)` : 'Cards nobody holds (0)', name: p ? `${p.first_name} ${p.last_name}` : 'Cards nobody holds' });
    }
    // People A to Z, and cards nobody holds last.
    return opts.sort((a, b) => (a.id === 'none') - (b.id === 'none') || a.name.localeCompare(b.name, 'en-GB'));
  })();
  // From a row on Missing receipts to that person's transactions waiting on a receipt.
  const showOwed = (p) => {
    setFilter('missing'); setPersonFilter(p.person_id ? String(p.person_id) : 'none');
    setDateFrom(''); setDateTo(''); setSearch(''); setTab('payments');
  };
  const chaseShown = chase?.people ? (chaseQ.trim()
    ? chase.people.filter(p => [p.person_name, p.card_ending]
        .some(v => String(v || '').toLowerCase().includes(chaseQ.trim().toLowerCase())))
    : chase.people) : [];
  const chaseSort = useSort(chaseShown, {
    person: p => p.person_name, card: p => p.card_ending, missing: p => Number(p.missing) || 0,
    value: p => Number(p.value) || 0, vat: p => Number(p.vat_at_risk) || 0, oldest: p => p.oldest,
    // Escalated first when reversed, then by how many times they have been asked.
    chased: p => (p.unattributed ? -1 : p.escalated > 0 ? 1000 + Number(p.escalated) : Number(p.max_reminders) || 0),
  }, null, { missing: 'desc', value: 'desc', vat: 'desc', chased: 'desc' });
  const decideSort = useSort(data?.transactions, TXN_SORT, null, TXN_FIRST_DIR);

  const TABS = decideView ? [['decide', 'No receipt to decide']] : financeView
    ? [['payments', 'Payments'], ['import', 'Import'], ['card-account', 'Card account'], ['merchants', 'Merchants'],
       ['cards', 'Cards'], ['chase', 'Missing receipts'], ['no-receipt', 'Marked no receipt'], ['settings', 'Settings']]
    : [['mine', 'My card spend']];

  return (
    <div className="p-3 space-y-3">
      <div>
        <h1 className={`${T.page} text-navy`}>{decideView ? 'Company card: no receipt' : financeView ? 'Company card spend' : 'My company card'}</h1>
        <p className={`${T.meta} text-gray-500`}>
          {decideView
            ? 'A cardholder has said there is no receipt for this transaction. Confirm it, or ask them to look again.'
            : financeView
            ? 'Capital on Tap · imported from their export · coded, receipted and posted to Sage'
            : 'Your Capital on Tap transactions. The one thing to do here is make sure each has a receipt.'}
        </p>
      </div>

      {flash && (
        <div className={`${T.body} sticky top-2 z-30 shadow-sm px-3 py-2 rounded border ${
          flash.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : flash.kind === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-red-50 border-red-200 text-red-700'}`}>{flash.text}</div>
      )}

      {/* The number this screen exists to move. Stated in pounds of VAT rather than in
          transactions, because that is what makes it worth somebody's afternoon. */}
      {/* ── WHAT THE HEADLINE IS FOR ─────────────────────────────────────────────
          Finance's headline shows what FINANCE can act on: coding, matching, posting. A
          missing receipt is somebody else's job — it is chased weekly by email and it
          lives on its own tab as a to-do, rather than sitting in red at the top of a
          screen where nobody can do anything about it. A number you cannot act on, shown
          constantly, is a number you learn to ignore, and it takes the ones that matter
          with it.

          A person's headline is the opposite: the receipt IS their job, and it is the
          only thing on it. */}
      {s && !decideView && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(financeView ? [
            ['My to-do', s.todo, s.todo ? 'text-navy' : 'text-emerald-600'],
            ['Ready to post', s.ready_to_post, s.ready_to_post ? 'text-blue-700' : 'text-gray-400'],
            ['In Sage', s.in_sage, 'text-emerald-600'],
            ...(s.awaiting_match
              ? [['Waiting on a match', s.awaiting_match, 'text-blue-700']]
              : s.feed_without_receipt
                ? [['Feed entries with no receipt', s.feed_without_receipt, 'text-amber-700']]
                : [['To code', s.needs_coding, s.needs_coding ? 'text-amber-700' : 'text-emerald-600']]),
          ] : [
            ['Receipts to upload', s.missing_receipt, s.missing_receipt ? 'text-red-600' : 'text-emerald-600'],
            ['VAT at stake', money(s.vat_at_risk), s.vat_at_risk ? 'text-red-600' : 'text-emerald-600'],
            ['Transactions held', s.total, 'text-gray-800'],
            ['All receipted', s.total - s.missing_receipt, 'text-emerald-600'],
          ]).map(([label, value, cls]) => (
            <div key={label} className="bg-white border border-gray-200 rounded px-3 py-2">
              <div className={`${T.label} text-gray-400`}>{label}</div>
              <div className={`text-lg font-extrabold ${cls}`}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Finance's one line about receipts: stated, not shouted, with the tab to go to.
          The people who can fix it are emailed weekly. */}
      {financeView && s?.missing_receipt > 0 && (
        <div className={`${T.meta} px-3 py-1.5 rounded border border-gray-200 bg-gray-50 text-gray-600 flex items-center justify-between gap-2 flex-wrap`}>
          <span>
            {s.missing_receipt} transaction{s.missing_receipt === 1 ? '' : 's'} still have no receipt
            ({money(s.vat_at_risk)} of VAT). The cardholders are emailed their own list every Monday.
          </span>
          <button onClick={() => setTab('chase')} className="underline font-semibold text-navy">
            See who owes what →
          </button>
        </div>
      )}

      {/* ── A PERSON'S ALL CLEAR ─────────────────────────────────────────────────
          Knowing there is nothing to do is worth saying out loud. Without it, silence and
          a broken screen look the same, and somebody who is up to date has no way to
          confirm it. */}
      {/* No card at all, or nothing imported. Said plainly rather than left as an empty
          screen somebody reads as a fault. */}
      {!financeView && !decideView && s && s.total === 0 && !data?.cards?.length && (
        <div className={`${T.body} px-3 py-2 rounded border border-gray-200 bg-gray-50 text-gray-600`}>
          You do not have a company card recorded here. If you do have one and it has been used, ask finance to map
          the card to you — until they do, its spend belongs to nobody and its receipts are not being asked for.
        </div>
      )}

      <div className="flex gap-1 flex-wrap border-b border-gray-200">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 py-1.5 text-[11px] font-semibold border-b-2 -mb-px ${
              tab === id ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {label}
            {id === 'chase' && chase?.total_missing ? ` (${chase.total_missing})` : ''}
            {id === 'no-receipt' && s?.said_no_receipt ? ` (${s.said_no_receipt})` : ''}
          </button>
        ))}
      </div>

      {tab === 'import' && <ImportPanel onDone={load} onFlash={onFlash} />}
      {tab === 'card-account' && <CardAccountPanel onFlash={onFlash} onChanged={load} />}
      {tab === 'merchants' && <MerchantsPanel ledgers={ledgers} contacts={contacts} onFlash={onFlash} onChanged={load} />}
      {tab === 'cards' && <CardsPanel people={people} onFlash={onFlash} onChanged={load} />}

      {tab === 'settings' && settings && (
        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={!!settings.posting_enabled} className="mt-0.5"
              onChange={e => saveSetting({ posting_enabled: e.target.checked })} />
            <span>
              <span className={`${T.body} font-semibold text-gray-800`}>Post card spend to Sage</span>
              <span className={`${T.meta} text-gray-500 block`}>
                Leave this OFF until Capital on Tap's own Sage feed has been stopped. With both running, every
                transaction lands in the ledger twice.
              </span>
            </span>
          </label>
          <div className={`${T.meta} px-2 py-1.5 rounded border ${settings.posting_enabled
            ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-blue-200 bg-blue-50 text-blue-900'}`}>
            {settings.posting_enabled
              ? 'Posting is ON. Every post still checks Sage for an entry that looks like the transaction first — that '
                + 'is not optional. Keep the setting below ON until Capital on Tap’s own feed has been stopped.'
              : 'Dual running. Capital on Tap’s feed is putting these transactions into Sage; ops finds those entries, '
                + 'links to them, sends the receipts Sage is missing, and carries the coding and the chase. Nothing is '
                + 'created in Sage from here, so there is no way to duplicate anything. Turn posting on the day their '
                + 'feed is switched off, and set the date below to that day.'}
          </div>

          {/* ── THE DUPLICATE CHECK ───────────────────────────────────────────────
              Worth being exact about what is and is not optional here, because the two get
              conflated: the SEARCH always runs, and an entry that looks like this
              transaction always stops the post. This setting only decides what happens when
              the search cannot see the whole ledger. */}
          <label className="flex items-start gap-2 cursor-pointer pt-1 border-t border-gray-100">
            <input type="checkbox" checked={settings.strict_duplicate_check !== false} className="mt-0.5"
              onChange={e => saveSetting({ strict_duplicate_check: e.target.checked })} />
            <span>
              <span className={`${T.body} font-semibold text-gray-800`}>
                Refuse to post when the duplicate check cannot be sure
              </span>
              <span className={`${T.meta} text-gray-500 block`}>
                Sage is always searched for an entry that looks like the transaction, and a match always stops the
                post — that part is not a setting. This is about the harder case: our copy of the Sage ledger being
                mid-sync, or not reaching back far enough to speak for the date.
                <strong> Leave this ON while Capital on Tap is still feeding Sage</strong>, because then something else
                is writing to the same ledger and "cannot tell" is not good enough. Once their feed is off, switching it
                off lets a post go through with a note on the transaction saying the check was not exhaustive — by then
                an incomplete cache is a delay rather than a risk, and holding real cost out of the accounts to wait for
                a cache is the worse trade.
              </span>
            </span>
          </label>
          <div className="flex items-end gap-2 pt-1">
            <label className="block">
              <span className={`${T.label} text-gray-400 block`}>Post transactions dated from</span>
              <input type="date" className={sel} value={settings.cutover_date || ''}
                onChange={e => saveSetting({ cutover_date: e.target.value })} />
            </label>
            <p className={`${T.meta} text-gray-500`}>
              The day you switch their feed off. Anything earlier stays with what that feed already put in Sage, so
              nothing is posted twice.
            </p>
          </div>
          {/* ── THE COMBINATION THAT POSTS HISTORY TWICE ──
              Posting on with no date set is the one arrangement where a backfill is
              dangerous. Every gate that keeps eight months of history out of Sage is
              hung off this date: with it empty, a January charge Capital on Tap's own
              feed posted at the time reads as ready to post, and only the duplicate
              check stands between it and a second copy. That check is a safety net and
              was never meant to be the gate. Said here, next to the empty box, because
              this is where somebody can fix it. */}
          {settings.posting_enabled && !settings.cutover_date && (
            <p className={`${T.body} px-3 py-2 rounded border border-red-200 bg-red-50 text-red-900`}>
              <strong>Posting is on and no date is set.</strong> Every rule that keeps older card spend out of Sage
              reads this date, so while it is empty nothing is held back: a charge Capital on Tap's own feed already
              posted can be posted again from here. Set it to the day their feed stops before importing any history.
            </p>
          )}
          <div className="flex items-end gap-2 pt-1">

            {/* ── HOW LONG BEFORE ANYBODY IS ASKED ──
                A charge that cleared on Friday has not been forgotten by Monday. Chasing it
                teaches people that the chase arrives whether or not anything is wrong, and
                then the one that matters reads the same as the one that did not. */}
            <div className="pt-1 border-t border-gray-100">
              <label className="flex items-center gap-2">
                <span className={`${T.body} font-semibold text-gray-800`}>Leave a transaction alone for</span>
                <input type="number" min="0" max="60" className={`${sel} w-16`}
                  value={settings.chase_after_days ?? 7}
                  onChange={e => saveSetting({ chase_after_days: Number(e.target.value) })} />
                <span className={`${T.body} text-gray-800`}>days before asking for the receipt</span>
              </label>
              <p className={`${T.meta} text-gray-500`}>
                Nothing newer than this is chased, and nothing newer than this reaches finance's summary. The card feed
                runs a day or two behind anyway, so a charge from this morning is not a missing receipt yet.
              </p>
            </div>

            {/* ── HOW LONG ASKING GETS ──
                A judgement about this business rather than about software, so it is a
                setting and not a constant. Three is where it starts. */}
            <div className="pt-1 border-t border-gray-100">
              <label className="flex items-center gap-2">
                <span className={`${T.body} font-semibold text-gray-800`}>Escalate a missing receipt after</span>
                <input type="number" min="1" max="12" className={`${sel} w-16`}
                  value={settings.escalate_after ?? 3}
                  onChange={e => saveSetting({ escalate_after: Number(e.target.value) })} />
                <span className={`${T.body} text-gray-800`}>unanswered reminders</span>
              </label>
              <p className={`${T.meta} text-gray-500`}>
                At that point admin and finance are emailed once, by name, with the VAT it is costing. Once, not every
                week: an escalation that repeats on a schedule becomes as easy to ignore as the reminder it replaced.
                Escalated transactions stay marked on this screen, which is where somebody is already looking.
              </p>
              <p className={`${T.meta} text-gray-500`}>
                It is also the only thing finance's weekly summary carries: transactions asked about that many times,
                plus spend on cards mapped to nobody. Everything more recent is being chased and needs nothing from
                finance, so it is on this screen rather than in an inbox.
              </p>
            </div>

            {/* ── THE CARD AS A BANK ACCOUNT ──
                The setting that stops card spend looking like supplier debt. Choosing it is
                the deliberate act, so there is no second switch to find afterwards: once
                this says which account the card is, every charge is paid from it on the day
                it cleared. */}
            <div className="pt-1 border-t border-gray-100 space-y-1">
              <span className={`${T.body} font-semibold text-gray-800`}>The card, as a bank account in Sage</span>
              <p className={`${T.meta} text-gray-500`}>
                A card charge is already paid: the merchant was paid at the till and Capital on Tap collect it from us
                by direct debit. So the charge posts against the merchant and is then paid from this account, which
                leaves the merchant square and the balance on the card, where a card balance belongs. Without it, every
                merchant we have ever tapped a card at sits in aged creditors.
              </p>
              <label className="flex items-center gap-2">
                <span className={`${T.body} text-gray-800 w-44`}>Capital on Tap card</span>
                <select className={`${sel} flex-1`} value={settings.card_bank_account_id || ''}
                  onChange={e => saveSetting({ card_bank_account_id: e.target.value })}>
                  <option value="">— not chosen: nothing is being paid —</option>
                  {(settings.bank_accounts || []).map(a => (
                    <option key={a.id} value={a.id}>
                      {a.display_name || a.name}{a.account_type_name ? ` · ${a.account_type_name}` : ''}
                      {a.is_active === 0 ? ' · not active in Sage' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <span className={`${T.body} text-gray-800 w-44`}>The direct debit leaves</span>
                <select className={`${sel} flex-1`} value={settings.current_bank_account_id || ''}
                  onChange={e => saveSetting({ current_bank_account_id: e.target.value })}>
                  <option value="">— not chosen —</option>
                  {(settings.bank_accounts || []).map(a => (
                    <option key={a.id} value={a.id}>
                      {a.display_name || a.name}{a.account_type_name ? ` · ${a.account_type_name}` : ''}
                      {a.is_active === 0 ? ' · not active in Sage' : ''}
                    </option>
                  ))}
                </select>
              </label>
              {!settings.bank_accounts?.length && (
                <p className={`${T.meta} text-amber-800`}>
                  No bank accounts have come back from Sage yet. Sync Sage on the Sage screen, and if there is no
                  Capital on Tap account there, create one in Sage as a credit card account first.
                </p>
              )}
              {/* The card account tab reads the chosen account by id, so it goes on naming
                  it whatever this list contains. If the two ever disagree again, say which
                  is which here rather than leaving a working setting reading as blank. */}
              {!!settings.card_bank_account_id
                && !(settings.bank_accounts || []).some(a => a.id === settings.card_bank_account_id) && (
                <p className={`${T.meta} text-amber-800`}>
                  A card account is chosen and card charges are being paid from it, but it is not in this list, so the
                  picker cannot show you which one. Sync Sage on the Sage screen. Do not change this dropdown until it
                  has: choosing from an incomplete list would move card spend to a different account.
                </p>
              )}
              <p className={`${T.meta} text-gray-500`}>
                The second one is only used if ops posts the weekly transfer, which it does not by default. The charges
                are paid either way.
              </p>

              {/* ── WHO MAKES THE WEEKLY TRANSFER ──
                  "Currently it's a Sage transfer between the two bank accounts, which is
                  tidy." It is, and it stays that way. This is here for a business whose
                  direct debit leaves an account with no feed, and switching it on where
                  there is one produces a second transfer every week. */}
              <label className="flex items-start gap-2 cursor-pointer pt-1">
                <input type="checkbox" className="mt-0.5"
                  checked={settings.dd_transfer_mode === 'ops_post'}
                  onChange={e => saveSetting({ dd_transfer_mode: e.target.checked ? 'ops_post' : 'bank_feed' })} />
                <span>
                  <span className={`${T.body} font-semibold text-gray-800`}>Ops posts the weekly transfer</span>
                  <span className={`${T.meta} text-gray-500 block`}>
                    Leave this OFF if the direct debit arrives on a bank feed. It already becomes a transfer when
                    somebody marks that feed line as one, so posting a second here would move the money into the card
                    account twice a week, and the duplicate would look exactly like the real one. With it off, ops finds
                    the transfer you made and links to it.
                  </span>
                </span>
              </label>
            </div>

            {/* ── TELLING CARD SPEND APART IN SAGE'S OWN REPORTS ──
                "Can we distinguish the transactions in Sage that they are from Capital on
                Tap easily, ideally from a report filter?" This is that filter. */}
            <div className="pt-1 border-t border-gray-100 space-y-1">
              <span className={`${T.body} font-semibold text-gray-800`}>Marking card spend for Sage's reports</span>
              {settings.analysis_supported === false ? (
                <p className={`${T.meta} text-gray-500`}>
                  This Sage business does not have analysis types, so card spend cannot be tagged for its management
                  reports. It is still easy to find two other ways: everything on the card is paid from the Capital on
                  Tap account above, so that account's activity is the list of card spend, and every entry carries a
                  reference beginning <span className="font-mono">COT-</span>.
                </p>
              ) : (
                <>
                  <p className={`${T.meta} text-gray-500`}>
                    Sage can run profit and loss, nominal activity and aged creditors by analysis type, so a category
                    here puts card spend behind a filter inside Sage rather than in an export somebody has to sort.
                    Only transaction analysis types can go on an entry; group ones live on the contact and would tag
                    nothing.
                  </p>
                  <label className="flex items-center gap-2">
                    <span className={`${T.body} text-gray-800 w-44`}>Analysis category</span>
                    <select className={`${sel} flex-1`} value={settings.analysis_category_id || ''}
                      onChange={e => saveSetting({ analysis_category_id: e.target.value })}>
                      <option value="">— none —</option>
                      {(settings.analysis_categories || []).map(c => (
                        <option key={c.id} value={c.id}>
                          {c.display_name || c.name}{c.analysis_type_name ? ` · ${c.analysis_type_name}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!settings.analysis_categories?.length && (
                    <p className={`${T.meta} text-gray-500`}>
                      No categories yet. Create one called Capital on Tap under a transaction analysis type in Sage,
                      then sync Sage.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'chase' && chase && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
            <span className={`${T.label} text-gray-500`}>Who owes a receipt</span>
            <input value={chaseQ} onChange={e => setChaseQ(e.target.value)}
              placeholder="Search person or card"
              className="border border-gray-200 rounded px-2 py-1 text-[11px] min-w-[12rem]" />
            <span className={`${T.meta} text-gray-600`}>
              {chase.total_missing} transactions · {money(chase.total_value)} · about {money(chase.total_vat_at_risk)} of VAT
            </span>
          </div>
          <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
            Everybody here is emailed their own list every Monday once a transaction is more than
            {' '}{settings?.chase_after_days ?? 7} days old, and the ask is counted per transaction. After
            {' '}{settings?.escalate_after || 3} unanswered asks, admin and finance are told once — because a fourth
            email is not a plan. Open a transaction on the Payments tab to see exactly what was sent and when, or to
            nudge somebody about one in particular.
          </p>
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-gray-50">
              <tr>{[['person', 'Person'], ['card', 'Card'], ['missing', 'Missing'], ['value', 'Value'],
                    ['vat', 'VAT at stake'], ['oldest', 'Oldest'], ['chased', 'Chased']].map(([k, h]) => (
                <SortTh key={k} col={k} label={h} sort={chaseSort.sort} onSort={chaseSort.toggle}
                  className={`${T.label} text-gray-400 px-3 py-1`} />
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chaseSort.sorted.map(p => (
                <tr key={`${p.person_id}-${p.card_ending}`} className={p.unattributed ? 'bg-amber-50/40' : ''}>
                  <td className={`${T.body} px-3 py-1 font-semibold`}>
                    <button onClick={() => showOwed(p)} className="text-left hover:underline"
                      title="Show these transactions on Payments">
                      {p.person_name || <span className="text-amber-800">nobody — card not mapped</span>}
                    </button>
                  </td>
                  <td className={`${T.meta} px-3 py-1 font-mono`}>•••• {p.card_ending}</td>
                  <td className={`${T.body} px-3 py-1 font-semibold`}>{p.missing}</td>
                  <td className={`${T.meta} px-3 py-1`}>{money(p.value)}</td>
                  <td className={`${T.body} px-3 py-1 font-semibold text-red-700`}>{money(p.vat_at_risk)}</td>
                  <td className={`${T.meta} px-3 py-1 text-gray-500`}>{fmtDay(p.oldest)}</td>
                  {/* Whether asking has been tried, and whether it has stopped working.
                      An unmapped card has never reached anybody, which is a different
                      problem from somebody ignoring five emails. */}
                  <td className={`${T.meta} px-3 py-1`}>
                    {p.unattributed
                      ? <span className="text-amber-800">nobody to ask</span>
                      : p.escalated > 0
                        ? <span className="font-semibold text-red-800">
                            escalated{p.escalated < p.missing ? ` (${p.escalated} of ${p.missing})` : ''}
                          </span>
                        : p.max_reminders > 0
                          ? <span className="text-gray-600">
                              asked up to {p.max_reminders}×
                              {p.last_reminded_at ? <span className="text-gray-400"> · last {String(p.last_reminded_at).slice(0, 10)}</span> : null}
                            </span>
                          : <span className="text-gray-400">not asked yet</span>}
                    {/* Answered, and waiting on finance rather than on them. */}
                    {p.said_none > 0 && (
                      <span className="block font-semibold text-amber-800">
                        says no receipt for {p.said_none}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* search/setSearch are the page's, so the personal record searches the SERVER: the
          list is capped, and a drill-down that filtered the page would under-report
          somebody's own spend back at them. */}
      {tab === 'mine' && (
        <MyCardPanel data={data} onChanged={load} onFlash={onFlash}
          search={search} setSearch={setSearch} />
      )}

      {tab === 'no-receipt' && (
        <NoReceiptPanel ledgers={ledgers} contacts={contacts} isFinance={isFinance} settings={settings}
          onChanged={load} onFlash={onFlash} />
      )}

      {tab === 'decide' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <TxnHead sort={decideSort.sort} onSort={decideSort.toggle} />
              </thead>
              <tbody className="divide-y divide-gray-100">
                {decideSort.sorted.map(t => (
                  <TransactionRow key={t.id} t={t} ledgers={[]} contacts={[]} isFinance={false} canDecide decideOnly
                    postingOn={false} settings={null} onChanged={load} onFlash={onFlash} />
                ))}
              </tbody>
            </table>
            {data && !data.transactions?.length && (
              <div className={`${T.meta} text-gray-500 px-3 py-6 text-center`}>
                Nothing to decide here: it has been dealt with, or the cardholder took it back.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'payments' && transactionId && (
        <div className={`${T.body} px-3 py-2 rounded border border-navy/30 bg-navy/5 text-navy flex items-center gap-2 flex-wrap`}>
          <span>Showing just the one transaction.</span>
          <button onClick={() => navigate('card-spend')}
            className="px-2 py-1 text-[10px] font-semibold rounded bg-navy text-white hover:bg-navy/90">
            Show everything
          </button>
        </div>
      )}

      {tab === 'payments' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* ── SEARCH ──
              Across everything the filter matches, not across the rows that happen to be
              on screen: the list is capped and the cap is not visible, so a browser-side
              filter would answer "no results" about transactions we hold. */}
          <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search merchant, person, description, nominal code, card, date or amount"
              className="flex-1 min-w-[16rem] border border-gray-200 rounded px-2 py-1 text-[11px]" />
            {!!search && (
              <button onClick={() => setSearch('')}
                className="px-2 py-1 text-[10px] font-semibold rounded bg-gray-100 text-gray-600 hover:bg-gray-200">
                Clear
              </button>
            )}
            <span className={`${T.meta} text-gray-500`}>
              {data?.matched != null
                ? (data.matched === data.returned
                    ? `${data.matched} row${data.matched === 1 ? '' : 's'}`
                    // The cap, said out loud. A list that stops at 500 without mentioning it
                    // is a list somebody will read as complete.
                    : `showing ${data.returned} of ${data.matched} — narrow it with the search`)
                : ''}
            </span>
          </div>
          <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
            {(financeView
              ? [['todo', `To do${s?.todo ? ` (${s.todo})` : ''}`], ['missing', `Waiting on a receipt${s?.missing_receipt ? ` (${s.missing_receipt})` : ''}`], ['unpaid', 'Showing as owed'], ['done', `In Sage${s?.in_sage ? ` (${s.in_sage})` : ''}`], ['all', 'Everything']]
              : [['all', 'All'], ['missing', 'Missing a receipt']]
            ).map(([id, label]) => (
              <button key={id} onClick={() => setFilter(id)}
                className={`px-2 py-1 text-[10px] font-semibold rounded ${
                  filter === id ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {label}
              </button>
            ))}
            {/* The toggle asked for: the ones waiting on a receipt are out of the to-do by
                default, because the cardholder is being chased for them and there is nothing
                finance can do until they arrive. */}
            {financeView && filter === 'todo' && (
              <label className={`${T.meta} text-gray-500 flex items-center gap-1 cursor-pointer`}>
                <input type="checkbox" checked={includeAwaiting}
                  onChange={e => setIncludeAwaiting(e.target.checked)} />
                include the {s?.missing_receipt || 0} waiting on a receipt
              </label>
            )}
            {/* Everything already coded and receipted, in one go. The per-transaction gates
                all still run, so this is the same decision taken repeatedly rather than a
                way past them. */}
            {financeView && s?.ready_to_post > 0 && (
              <button onClick={postAllReady} disabled={posting}
                className="px-2 py-1 text-[10px] font-bold rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50">
                {posting ? 'Posting…' : `→ Post the ${s.ready_to_post} that are ready`}
              </button>
            )}
            {isFinance && (
              <span className="flex items-center gap-1">
                <button onClick={matchFeed} disabled={matching}
                  className="px-2 py-1 text-[10px] font-semibold rounded border border-navy text-navy bg-white hover:bg-navy/5 disabled:opacity-50"
                  title="Find the entries Capital on Tap's own feed has already put in Sage, link to them, and send our receipts to the ones with none. Creates nothing in Sage, so it is always safe to press.">
                  {matching ? 'Matching…' : '⇄ Find these in Sage'}
                </button>
                <select value={matchDays} onChange={e => setMatchDays(e.target.value)}
                  className="border border-gray-200 rounded px-1 py-1 text-[10px] bg-white"
                  title="Which transactions to look for in Sage">
                  <option value="">everything unmatched</option>
                  <option value="1">the last day</option>
                  <option value="7">the last week</option>
                  <option value="14">the last fortnight</option>
                  <option value="31">the last month</option>
                </select>
              </span>
            )}
            {s?.possible_repeats > 0 && (
              <span className={`${T.meta} text-amber-800 ml-auto`}>
                {s.possible_repeats} flagged as a possible repeat — confirm or exclude them
              </span>
            )}
          </div>
          {/* ── WHO AND WHEN ──
              Its own row, beside the list it narrows, so the effect of picking a name is the
              rows directly underneath changing. */}
          {financeView && !transactionId && (
            <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
              <label className={`${T.meta} text-gray-500 flex items-center gap-1`}>
                Person
                <select value={personFilter} onChange={e => setPersonFilter(e.target.value)}
                  className={`${sel} ${personFilter ? 'border-navy text-navy font-semibold' : ''}`}>
                  <option value="">{filter === 'missing' ? 'Everybody who owes one' : 'Everybody'}</option>
                  {personOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </label>
              <label className={`${T.meta} text-gray-500 flex items-center gap-1`}>
                Cleared from
                <input type="date" value={dateFrom} max={dateTo || undefined} onChange={e => setDateFrom(e.target.value)}
                  className={`${sel} ${dateFrom ? 'border-navy text-navy font-semibold' : ''}`} />
              </label>
              <label className={`${T.meta} text-gray-500 flex items-center gap-1`}>
                to
                <input type="date" value={dateTo} min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)}
                  className={`${sel} ${dateTo ? 'border-navy text-navy font-semibold' : ''}`} />
              </label>
              {hasWhoWhen && (
                <button onClick={clearWhoWhen}
                  className="px-2 py-1 text-[10px] font-semibold rounded bg-gray-100 text-gray-600 hover:bg-gray-200">
                  Clear person and dates
                </button>
              )}
              <span className={`${T.meta} text-gray-400 ml-auto`}>Click a column heading to sort</span>
            </div>
          )}
          {/* ── WHAT THAT BUTTON DOES ──
              It lived in a title attribute, which nobody hovers, so the button read as an
              unexplained action next to a ledger. Said in the open instead: it reads Sage
              and writes nothing to it, which is the property that makes pressing it safe
              and is exactly the thing somebody needs to know before pressing it. */}
          {isFinance && financeView && (
            <p className={`${T.meta} text-gray-500 px-3 py-1.5 border-b border-gray-100`}>
              <strong className="text-gray-700">Find these in Sage</strong> looks for the entries Capital on Tap's own
              feed has already posted, links each transaction to the one that is it, and sends our receipt to any that
              has none. It creates nothing in Sage and posts no money, so it is always safe to press. Where two entries
              could both be the same charge it asks you to pick rather than guessing.
            </p>
          )}
          {/* The to-do deliberately stops at the cutover, and the gap between the count and
              the rows is what "33 to do, eight on screen, no pagination" looked like. */}
          {financeView && filter === 'todo' && s?.todo_history > 0 && (
            <p className={`${T.meta} text-gray-500 px-3 py-1.5 border-b border-gray-100`}>
              A further {s.todo_history} transaction{s.todo_history === 1 ? ' is' : 's are'} dated before the cutover.
              Capital on Tap's own feed posted those at the time, so they are not finance's to post and are kept out of
              this list. They are under <strong className="text-gray-700">Everything</strong>, and
              <strong className="text-gray-700"> Find these in Sage</strong> is what links them to the entries the feed
              already made.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <TxnHead sort={paySort} onSort={togglePaySort} />
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.transactions || []).map(t => (
                  <TransactionRow key={t.id} t={t} ledgers={ledgers} contacts={contacts} isFinance={isFinance}
                    postingOn={!!settings?.posting_enabled} settings={settings}
                    onChanged={load} onFlash={onFlash} />
                ))}
              </tbody>
            </table>
            {!data?.transactions?.length && (
              <div className={`${T.meta} text-gray-500 px-3 py-6 text-center`}>
                {financeView && hasWhoWhen
                  ? 'Nothing matches that person and those dates under this filter.'
                  : financeView && filter === 'todo'
                  ? (s?.total
                      ? <span className="text-emerald-700 font-semibold">
                          Nothing to do. Everything coded and receipted is in Sage
                          {s.missing_receipt ? `, and the ${s.missing_receipt} waiting on a receipt are being chased.` : '.'}
                        </span>
                      : 'Nothing here yet. Import an export from Capital on Tap to start.')
                  : financeView ? 'Nothing matches that filter.'
                  : 'Nothing on your card in the period held.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
